/**
 * The AffDice Affiliate.
 *
 * Every screen is a thin renderer over a view model from `screens.ts`. No
 * component decides what a rule *means*; components decide where it goes on the
 * page. That split is what let this entire visual layer be rewritten without
 * touching a rule or a test, and it is enforced rather than trusted —
 * `check-boundaries.mjs` refuses a `.tsx` that formats money or renders a raw
 * minor-unit field.
 */

import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  UNAVAILABLE,
  freshness,
  countryOptions,
  ApiClientError,
  isApiClientError,
  Callout,
  NavList,
  PortalMark,
  logoSrc,
  Rail,
  RailIdentity,
  ThemeToggle,
  TourButton,
  TourDialog,
  useTour,
  ChangePassword,
  Confirm,
  ConversionNotes,
  CurrencyViewSwitch,
  billingCurrencyNote,
  setDisplayCurrency,
  type CurrencyView,
  EmailInput,
  Field,
  LinkLandingPageField,
  FilterBar,
  Select,
  TextInput,
  Chip,
  CopyButton,
  CopyableCode,
  Empty,
  ErrorNotice,
  Figure,
  EnrolMfa,
  Facts,
  Loading,
  type Load,
  Modal,
  Pager,
  SortTh,
  AttachmentPicker,
  DisputeContextPanel,
  EvidenceList,
  toAttachmentUploads,
  ScreenWithSections,
  SectionSplit,
  Panel,
  rowOpen,
  SessionExpiredError,
  SignOut,
  splitCurrency,
  Tab,
  TabBar,
  TableScroll,
  LOCALES,
  explainFailure,
  DISPLAY_CURRENCY_ORIGINAL,
  setCurrencyScales,
  setDisplayLocale,
  formatCount,
  geoLabel,
  formatDate,
  formatDateTime,
  kitText,
  parseWholeNumber,
  textArray,
  useLoad,
  type OnboardApi,
  periodDays,
  formatDay,
  usePaging,
  useSort,
  countryFlag,
  countryName,
  countryLabel,
  TableActions,
  TimeSeries,
  WorldMap,
  daysBetween,
  rollUp,
  type Grain,
  Sorted,
  KEEP_AS_TYPED,
  POSTBACK_MACRO_KEYS,
  POSTBACK_URL_MAX_LENGTH,
  applyPostbackFills,
  initialPostbackFills,
  isPostbackCidParam,
  parsePostbackUrl,
  postbackFillsComplete,
  postbackMacrosInValue,
  postbackUrlTooLong,
  type PostbackFillChoice,
  type PostbackMacro,
  type PostbackUrlParam,
  deviceLabel,
  HeaderTip,
} from '@affdice/portal-kit';

import {
  AffiliateApi,
  type PostbackRegistration,
  type PostbackTestResult,
  type SmartLinkRow,
} from './client.ts';
import { AffiliateCohortPanel } from './cohort-panel.tsx';
import { cohortFixedReason, cohortRange, type CohortPrivacy } from './cohort-privacy.ts';
import { TrafficSources } from './traffic-sources.tsx';
import { SpendDeclarations } from './spend.tsx';
import {
  dimensions,
  cohortAnalysis,
  cohorts,
  readsAsPerPlayerOff,
  campaignTermsView,
  campaignDirectoryView,
  clickLogView,
  playerQuarantineView,
  clicksImpressionsView,
  detailedActivityView,
  creativeGallery,
  creativeLinkChoices,
  type CreativeLinkChoice,
  dailyActivityView,
  mediaReport,
  runningCommission,
  type CampaignDirectoryRow,
  dashboard,
  commissionInRangeFigure,
  earningsSoFar,
  ftdGapReasons,
  mailSubject,
  heldLines,
  linkReport,
  links,
  payments,
  paymentSummary,
  performance,
  performanceCsv,
  performanceXml,
  periodSampling,
  players,
  samplingChip,
  samplingRateLabel,
  smartLinkView,
  statement,
  subAffiliates,
  subAffiliatesTabVisible,
  subAccountReportRows,
  termsScreen,
  termsLine,
  termsTableSections,
  carryoverDeficitFigures,
  ipGeolocationAttribution,
  recoupmentRows,
  statementBroughtForwardAmount,
  statementCalculation,
  statementLineRows,
  type CalculationRow,
  type GalleryItem,
  type TermsLine,
  type TrafficRulesView,
  type CpaPriceListView,
  clickStatusWords,
  qualificationReasonWords,
} from './screens.ts';
import {
  funnelRow,
  playerColumns,
  payoutMethods,
  SUB_ID_SLOTS,
  checkPayoutDetailsUrl,
  checkPayoutDetailsUrlLabel,
  checkPayoutLabel,
  checkSubId,
  commissionTraceRows,
  type CommissionTraceView,
  disputeDetailAmount,
  disputeDetailLine,
  disputeStatusTone,
  disputeWords,
  explainStatus,
  historyLine,
  invoiceDetailView,
  isDayInput,
  majorUnits,
  reportWindow,
  trackingAddresses,
  invoiceStatusTone,
  invoiceStatusWords,
  mandateStateTone,
  mandateStateWords,
  actorName,
  qualificationStatusTone,
  qualificationStatusWords,
  statementRow,
  invoiceRow,
  playerSortValues,
  playersInOrder,
  playerColumnTotals,
  sortableCell,
  railBrandLogo,
  creativeEmbedHtml,
  impressionPixelHtml,
  textLinkHtml,
} from './present.ts';
import { t, type AffiliateMessageKey } from './messages.ts';
import { joinedProgrammeNames, selectableProgrammes } from './programmes.ts';
import { TABS, railItems, type Tab as RailTab } from './rail.ts';
import { AFFILIATE_TOUR_VERSION, affiliateTourSteps } from './tour.ts';
import { tourMain } from './tour-layouts.tsx';
// Screen-specific rules for the right rail (`PortalAside`) alone — see the
// file's own header for why they live here rather than in the shared kit.
import './aside.css';

// The kit's `Tab` component owns the bare name here.
type Tab = RailTab;


/** The chosen window, as the two dates and the brand every report is given. */
function rangeOf(
  now: Date,
  days: string,
  brandId: string,
  custom?: { readonly from?: string; readonly to?: string },
): { from: string; to: string; brand_id?: string } {
  return {
    ...reportWindow(now, days, custom),
    ...(brandId ? { brand_id: brandId } : {}),
  };
}

function Failure({ error }: { error: unknown }) {
  const shown = explainFailure(error);
  return (
    <Callout tone="error" {...(shown.reference ? { reference: shown.reference } : {})}>
      {shown.message}
    </Callout>
  );
}

interface ScreenProps {
  readonly api: AffiliateApi;
  /**
   * Dates and brand together, and passed to every report unchanged.
   *
   * Held in the shell rather than per screen: a filter each screen keeps for
   * itself is one that says AURORA on the dashboard and "all brands" on
   * commission, with neither screen admitting they disagree. The API refuses
   * the same way — `brand_id` is parsed once, for every report, because a
   * filter honoured by one and dropped by another is worse than none.
   */
  readonly range: { from: string; to: string; brand_id?: string };
  readonly now: Date;
  readonly me: Awaited<ReturnType<AffiliateApi['me']>>;
  /**
   * §2.8a — the language was just saved. The shell re-reads `me`, and the
   * next render of every screen is in the chosen language: without this the
   * portal stayed in the old one until somebody reloaded, which reads as a
   * save that did nothing.
   *
   * Item 5 — the optional `note` is the save's own confirmation text, kept in
   * `App` state so it is still there once the reload above finishes (see
   * `AccountSection`'s doc comment).
   */
  readonly onLocaleSaved: (note?: string) => void;
  /** Rendered by the shell, so all three reporting screens show one control. */
  readonly filters: ReactNode;
  /** Item 2 (third batch) — Account's own voluntary "Change password". */
  readonly onboard: OnboardApi;
  /**
   * Bumped whenever a proposed deal is answered — on the Terms screen or in
   * the right rail. Both list the same proposals, so both must re-read when
   * either answers: a rail still saying "awaiting your answer" after the
   * affiliate accepted on the Terms screen asks a question that no longer
   * exists.
   */
  readonly dealProposalsVersion: number;
  readonly onDealProposalsChanged: () => void;
}

/*
 * The ranges, as ids and message keys rather than as words. Built into options
 * at render — a constant array of labels is a constant array in the language
 * the tab booted in, which is English until `me()` answers.
 */
const RANGES = [
  { value: '7', labelKey: 'range.7' },
  { value: '30', labelKey: 'range.30' },
  { value: '90', labelKey: 'range.90' },
  { value: '365', labelKey: 'range.365' },
  /*
   * The calendar entries, after the rolling ones.
   *
   * A rolling window and a calendar month answer different questions, and only
   * the second can be set against an operator's statement: a period that ran
   * the 1st to the 30th never lines up with "last 30 days", so every figure
   * compared across the two was off by whatever the overlap happened to be.
   * `reportWindow` (present.ts) is where the arithmetic lives and is tested.
   */
  { value: 'today', labelKey: 'range.today' },
  { value: 'yesterday', labelKey: 'range.yesterday' },
  { value: 'mtd', labelKey: 'range.thisMonth' },
  { value: 'last-month', labelKey: 'range.lastMonth' },
  { value: 'ytd', labelKey: 'range.thisYear' },
  { value: 'custom', labelKey: 'range.custom' },
] as const satisfies readonly { value: string; labelKey: AffiliateMessageKey }[];

/**
 * The filter bar, on every screen that reports.
 *
 * It states what is currently in force rather than only offering choices: an
 * affiliate who has left a brand selected and forgotten reads the numbers as
 * their whole account, and every figure on the page is then wrong by exactly
 * the traffic they filtered out.
 */
function Filters({
  days,
  onDays,
  custom,
  onCustom,
  brand,
  onClearBrand,
}: {
  days: string;
  onDays: (v: string) => void;
  /**
   * The typed pair, kept in `App` beside `days` rather than here, so switching
   * to another preset and back does not lose two dates somebody just typed.
   */
  custom: { readonly from: string; readonly to: string };
  onCustom: (next: { readonly from: string; readonly to: string }) => void;
  /**
   * Item 9 — the rail's brand selector sits inside the phone nav sheet, so a
   * page filtered to one brand otherwise reads there as the whole account.
   * Set only once a brand is actually chosen (never "All brands").
   */
  brand?: string;
  onClearBrand?: () => void;
}) {
  /*
   * Half a pair is not an error and does not empty the report — `reportWindow`
   * falls back to the opening thirty days while the second date is still being
   * typed. What it is is worth *saying*, because a report that silently kept
   * showing thirty days under two half-filled date boxes would read as having
   * honoured them.
   */
  const incomplete =
    days === 'custom' && !(isDayInput(custom.from) && isDayInput(custom.to));

  return (
    <FilterBar>
      <Field label={t('filter.period')}>
        <Select value={days} onChange={onDays} options={RANGES.map((r) => ({ value: r.value, label: t(r.labelKey) }))} />
      </Field>
      {days === 'custom' ? (
        <>
          <Field label={t('filter.from')}>
            <input
              type="date"
              className="control"
              value={custom.from}
              max={custom.to || undefined}
              onChange={(e) => onCustom({ ...custom, from: e.currentTarget.value })}
            />
          </Field>
          <Field label={t('filter.to')}>
            <input
              type="date"
              className="control"
              value={custom.to}
              min={custom.from || undefined}
              onChange={(e) => onCustom({ ...custom, to: e.currentTarget.value })}
            />
          </Field>
          {incomplete ? <Chip tone="provisional">{t('filter.needBothDates')}</Chip> : null}
        </>
      ) : null}
      {brand ? (
        <>
          <Chip tone="neutral">{t('filter.brandOnly', { brand })}</Chip>
          {onClearBrand ? (
            <button type="button" className="link-button" onClick={onClearBrand}>
              {t('filter.showAll')}
            </button>
          ) : null}
        </>
      ) : null}
    </FilterBar>
  );
}

/* ------------------------------------------------------------------ *
 * Dashboard
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * One campaign, and what it pays you — §7.12d
 * ------------------------------------------------------------------ */

/**
 * A campaign row, at its thinnest.
 *
 * Four places open this view and they do not carry the same row: a joined
 * campaign comes with its full targeting, while an offered or discoverable
 * one carries where it runs and what it sells and nothing else. Only the id,
 * the name and the brand are certain, so everything else is optional and read
 * with a fallback — a detail view that assumed the richest row would crash on
 * exactly the two lists an affiliate opens to decide whether to join at all.
 */
interface CampaignDetailRow {
  readonly campaign_id: string;
  readonly campaign_name: string;
  readonly brand_name: string;
  readonly jurisdictions?: readonly string[];
  readonly products?: readonly string[];
  readonly os_codes?: readonly string[];
  readonly device_classes?: readonly string[];
  readonly goals?: readonly { readonly id: string; readonly name: string }[];
  readonly landing_pages?: readonly {
    readonly landing_page_id: string;
    readonly label: string;
    readonly url: string;
  }[];
  readonly effective_attribution_window_days?: number;
  readonly sampling_rate_bps?: number;
  readonly postclick_anchor?: string;
  readonly valid_from?: string;
  /** 0133 — the day the operator stops offering it, where one is set. */
  readonly expires_at?: string | null;
  readonly trafficback_url?: string | null;
}

/**
 * When a campaign stops taking traffic.
 *
 * Two dates can end it and the click path checks both: `expires_at` is the end
 * date the operator set on the campaign form, and `valid_to` is this
 * *version's* own validity. Whichever falls first is the one an affiliate
 * needs to plan against.
 *
 * Reading `valid_to` alone is what this did, and `valid_to` is null on almost
 * every campaign there is — so the panel said "No end date set" about
 * campaigns that had one, to the reader with the most at stake in the answer.
 */
function campaignEndsAt(
  campaign: { readonly valid_to?: string | null; readonly expires_at?: string | null },
): string | null {
  const ends = [campaign.expires_at, campaign.valid_to].filter(
    (d): d is string => typeof d === 'string' && d !== '',
  );
  if (ends.length === 0) return null;
  return ends.reduce((a, b) => (new Date(a).getTime() <= new Date(b).getTime() ? a : b));
}

/** One set of terms as label/value rows — the Terms screen's own layout. */
function TermsRowsTable({ rows }: { rows: readonly { label: string; value: string }[] }) {
  return (
    <TableScroll>
      <table>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <th style={{ width: '16rem' }}>{r.label}</th>
              <td className="wrap">{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

/**
 * §7.8 — the commercial-terms block, in the same label/value shape as
 * `line.conditions` above: payout frequency and settlement, the
 * negative-balance policy, the composed formula, the admin fee, cookie
 * lifetime, attribution window and the minimum payout. `screens.ts` has
 * already turned every one of these into words a reader sees (§0.5b's
 * source included, where a field carries one) — this only picks which of
 * F1's already-composed rows exist on this line and lists them, the same
 * shape `conditions.map(...)` already renders.
 */
function commercialTermsRows(line: TermsLine): readonly { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];

  if (line.payout) {
    rows.push({
      label: t('terms.payout' as AffiliateMessageKey),
      value: `${line.payout.frequency} · ${line.payout.settlement} · ${line.payout.source}`,
    });
  }
  if (line.negativeBalance) {
    rows.push({
      label: t('terms.negativeBalance' as AffiliateMessageKey),
      value: `${line.negativeBalance.value} · ${line.negativeBalance.source}`,
    });
  }
  // §20.10b — TURNOVER carries a note instead of a composed line; either way
  // exactly one of the two is set, so there is always something to show here.
  if (line.formula.line || line.formula.note) {
    rows.push({
      label: t('terms.formula' as AffiliateMessageKey),
      value: line.formula.line ?? line.formula.note ?? '',
    });
  }
  // §20.6a — always present, "0.00%" and all: an absent fee is shown, never omitted.
  rows.push({ label: line.adminFee.label, value: line.adminFee.value });
  if (line.cookieLifetime) rows.push(line.cookieLifetime);
  if (line.attributionWindow) rows.push(line.attributionWindow);
  if (line.minimumPayout) {
    rows.push({
      label: t('terms.minimumPayout' as AffiliateMessageKey),
      value: `${line.minimumPayout.value} · ${line.minimumPayout.source}`,
    });
  }

  return rows;
}

/**
 * §24.7b — the CPA price list as label/value rows: one per priced country
 * (and channel, where the row names one), then the base CPA as "Everywhere
 * else", so the list never reads as the only places a player pays.
 */
function cpaPriceListRows(list: CpaPriceListView): readonly { label: string; value: string }[] {
  return [...list.rows.map((r) => ({ label: r.label, value: r.amount })), list.elsewhere];
}

/**
 * QA round 2 F1 — the tier, product and window tables `termsLine` builds, one
 * headed table each. Rendered on an offer before acceptance and on the terms
 * in force, so a tiered or per-product deal is read in full, not discovered
 * on the first statement.
 */
function TermsTableSections({ line }: { line: TermsLine }) {
  return (
    <>
      {termsTableSections(line).map((section) => (
        <div className="stack" key={section.heading}>
          <span className="figure-label">{section.heading}</span>
          <TermsRowsTable rows={section.rows} />
          {section.note ? <p className="link-note">{section.note}</p> : null}
        </div>
      ))}
    </>
  );
}

/**
 * D3 — DB-IP's licence asks for a credit and a link wherever the country it
 * resolves is shown. Small and after the table, on every view that shows a
 * click's country.
 */
function IpGeolocationNote() {
  const credit = ipGeolocationAttribution();
  return (
    <p className="geo-credit">
      <a href={credit.href} target="_blank" rel="noopener noreferrer">{credit.text}</a>
    </p>
  );
}

/**
 * §7.14/§7.8f — every traffic rule this line states, flattened to the same
 * label/value shape: the three headline axes first (always three rows, one
 * per axis, Not Stated where the version says nothing), then everything else
 * grouped by the mode `screens.ts` already sorted it into.
 */
function trafficRulesRows(view: TrafficRulesView): readonly { label: string; value: string }[] {
  return [...view.headline, ...view.allowed, ...view.forbidden, ...view.byAgreement].map((row) => ({
    label: row.label,
    value: row.statusLabel,
  }));
}

/**
 * One campaign, and which terms pay *you* on it.
 *
 * A campaign can carry its own payout now, so "what does this campaign pay"
 * has three answers depending on who asks — its payout, your default deal
 * with the brand, or terms agreed for this campaign alone — and nothing on a
 * list row says which. An affiliate who assumes their default deal applies
 * everywhere promotes a campaign whose payout they never read.
 */
function CampaignDetailModal({
  api,
  campaign,
  onClose,
  onAnswered,
}: {
  api: AffiliateApi;
  campaign: CampaignDetailRow;
  onClose: () => void;
  /** An offer was answered here, so a list elsewhere that shows it is stale. */
  onAnswered?: (() => void) | undefined;
}) {
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState<'ACCEPT' | 'REJECT' | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const loaded = useLoad(
    () => api.campaignTerms(campaign.campaign_id),
    [campaign.campaign_id, version],
  );

  const osCodes = textArray(campaign.os_codes);
  const devices = textArray(campaign.device_classes);
  const goals = campaign.goals ?? [];
  const landingPages = campaign.landing_pages ?? [];
  const windowDays = campaign.effective_attribution_window_days;

  const facts: { label: string; value: ReactNode }[] = [
    { label: t('col.brand'), value: campaign.brand_name },
    { label: t('campaign.runsIn'), value: textArray(campaign.jurisdictions).join(', ') || '—' },
    { label: t('campaign.products'), value: textArray(campaign.products).join(', ') || '—' },
  ];
  if (osCodes.length > 0) facts.push({ label: t('campaign.osTargeting'), value: osCodes.join(', ') });
  if (devices.length > 0) facts.push({ label: t('campaign.deviceTargeting'), value: devices.join(', ') });
  if (goals.length > 0) facts.push({ label: t('campaign.goals'), value: goals.map((g) => g.name).join(', ') });
  if (typeof windowDays === 'number') {
    facts.push({
      label: t('campaign.window'),
      value: t(windowDays === 1 ? 'campaign.window.one' : 'campaign.window.other', { n: windowDays }),
    });
  }
  // §39.13 — stated here as well as on the link form, so a sampled-away
  // conversion is never the first the affiliate hears of it.
  if (typeof campaign.sampling_rate_bps === 'number') {
    facts.push({ label: t('campaign.sampling'), value: samplingRateLabel(campaign.sampling_rate_bps) });
  }
  if (campaign.postclick_anchor) {
    facts.push({
      label: t('campaign.anchor'),
      value: campaign.postclick_anchor === 'GOAL' ? t('campaign.anchor.goal') : t('campaign.anchor.click'),
    });
  }
  if (campaign.valid_from) facts.push({ label: t('campaign.liveSince'), value: formatDate(campaign.valid_from) });
  /*
   * When it stops. An affiliate deciding whether to put spend behind a
   * campaign is deciding for how long — and this list answered every other
   * question about the offer except the one with a deadline attached.
   *
   * A campaign past the date is not in this list at all (it leaves the picker
   * the way a retired one does), so a date shown here is always still ahead.
   */
  const endsAt = campaignEndsAt(campaign);
  if (endsAt) facts.push({ label: t('campaign.runsUntil'), value: formatDate(endsAt) });
  if (campaign.trafficback_url) {
    facts.push({ label: t('campaign.trafficback'), value: <span className="wrap">{campaign.trafficback_url}</span> });
  }
  if (landingPages.length > 0) {
    facts.push({
      label: t('campaignDetail.landingPages'),
      value: (
        <>
          {landingPages.map((lp) => (
            <div key={lp.landing_page_id}>
              {lp.label} <span className="faint wrap">{lp.url}</span>
            </div>
          ))}
        </>
      ),
    });
  }

  const view = loaded.state === 'ready' ? campaignTermsView(loaded.value, campaign.brand_name) : null;
  const offer = view?.offer ?? null;

  const answer = (decision: 'ACCEPT' | 'REJECT') => {
    if (!offer || offer.version === null) return;
    setBusy(decision);
    setFailure(null);
    api
      .respondDealProposal(offer.dealId, offer.version, decision)
      .then(() => {
        // Read again rather than assume: an accepted offer only becomes what
        // applies once its start date arrives, and the server is what knows.
        setVersion((v) => v + 1);
        onAnswered?.();
      })
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(null));
  };

  return (
    <Modal title={campaign.campaign_name} onClose={onClose} wide>
      <div className="stack">
        <Facts items={facts} />

        <h3 className="offer-title">{t('campaignTerms.title')}</h3>
        {failure ? <Failure error={failure} /> : null}
        {loaded.state === 'loading' ? <Loading /> : null}
        {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
        {view ? (
          view.applies === null ? (
            <Empty>{t('campaignTerms.none')}</Empty>
          ) : (
            <>
              <p className="terms-summary">{view.applies.heading}</p>
              {view.applies.rows.length > 0 ? (
                <TermsRowsTable rows={view.applies.rows} />
              ) : (
                <Callout tone="caution">{t('campaignTerms.unreadable')}</Callout>
              )}
              {view.applies.since ? <p className="link-note">{view.applies.since}</p> : null}
            </>
          )
        ) : null}

        {/*
          §7.2a — what this campaign paid before today.

          The operator's screen has carried this and the affiliate's has not,
          which is the wrong way round: the operator sets the rate, and the
          affiliate is the one who has to reconcile a payment against it. A
          player keeps the terms they were attributed under, so one period can
          be paid at two rates and neither is the one shown above.
        */}
        {view && view.history.length > 0 ? (
          <>
            <p className="terms-summary">{t('campaignTerms.history')}</p>
            <p className="link-note">{t('campaignTerms.historyNote')}</p>
            {view.history.map((h) => (
              <div key={h.key}>
                <p className="link-note">
                  {h.ran}
                  {h.live ? <> · {t('campaignTerms.historyLive')}</> : null}
                </p>
                <TermsRowsTable rows={h.rows} />
              </div>
            ))}
          </>
        ) : null}

        {/* §7.11 — its own card, so the terms on offer are never read as the
            terms in force: they pay nothing until accepted. */}
        {offer ? (
          <article className="offer-card">
            <h3 className="offer-title">{t('campaignTerms.offer.title')}</h3>
            <p className="offer-note">{t('campaignTerms.offer.note')}</p>
            {offer.rows.length > 0 ? (
              <TermsRowsTable rows={offer.rows} />
            ) : (
              <Callout tone="caution">{t('campaignTerms.unreadable')}</Callout>
            )}
            {offer.when ? <p className="link-note">{offer.when}</p> : null}
            {offer.version !== null ? (
              <div className="offer-actions">
                <button className="primary" type="button" disabled={busy !== null} onClick={() => answer('ACCEPT')}>
                  {busy === 'ACCEPT' ? t('proposals.accepting') : t('proposals.accept')}
                </button>
                <button className="copy" type="button" disabled={busy !== null} onClick={() => answer('REJECT')}>
                  {t('offers.decline')}
                </button>
              </div>
            ) : null}
          </article>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * §10.11 — campaigns the operator has made visible to this affiliate.
 *
 * Beside the offers panel and deliberately not inside it: an offer is the
 * operator asking, and this is the affiliate asking. The button says "Request
 * access" rather than "Join" because that is what §10.11a makes it — nothing
 * here can carry traffic until somebody accepts (or the operator has said in
 * advance that they need not be asked).
 */
function DiscoverCampaignsPanel({
  api,
  campaigns,
  note,
  onRequested,
  onTermsAnswered,
}: {
  api: AffiliateApi;
  campaigns: readonly {
    campaign_id: string;
    campaign_name: string;
    brand_name: string;
    jurisdictions: readonly string[];
    products: readonly string[];
  }[];
  note?: string;
  onRequested: () => void;
  /** An offer was answered from a campaign's detail view. */
  onTermsAnswered?: (() => void) | undefined;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  // What it would pay, before asking to join it — the question somebody
  // deciding whether to ask actually has.
  const [viewing, setViewing] = useState<(typeof campaigns)[number] | null>(null);

  return (
    <Panel
      title={t('discover.panel')}
      aside={<span className="count count-badge">{campaigns.length}</span>}
    >
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      {/* Item 3 (batch 2) — the join request's own result, read whole. */}
      {said ? <Callout fold={false}>{said}</Callout> : null}
      {campaigns.map((campaign) => (
        <article key={campaign.campaign_id} className="offer-card">
          <h3 className="offer-title">{campaign.campaign_name}</h3>
          <p className="offer-brand">{campaign.brand_name}</p>
          <dl className="offer-details">
            <dt>{t('campaign.runsIn')}</dt>
            <dd>{textArray(campaign.jurisdictions).join(', ') || '—'}</dd>
            <dt>{t('campaign.products')}</dt>
            <dd>{textArray(campaign.products).join(', ') || '—'}</dd>
          </dl>
          <div className="form-actions">
            <button className="copy" type="button" onClick={() => setViewing(campaign)}>
              {t('campaignDetail.open')}
            </button>
            <button
              className="primary"
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setBusy(campaign.campaign_id);
                setFailure(null);
                api
                  .joinCampaign(campaign.campaign_id)
                  .then((r) => {
                    setSaid(r.message);
                    onRequested();
                  })
                  .catch((e: unknown) => setFailure(e))
                  .finally(() => setBusy(null));
              }}
            >
              {busy === campaign.campaign_id ? t('discover.asking') : t('discover.request')}
            </button>
          </div>
        </article>
      ))}
      {note ? <Callout>{note}</Callout> : null}
      {viewing ? (
        <CampaignDetailModal
          api={api}
          campaign={viewing}
          onClose={() => setViewing(null)}
          onAnswered={onTermsAnswered}
        />
      ) : null}
    </Panel>
  );
}

function CampaignOffersPanel({
  api,
  offers,
  onResponded,
  onTermsAnswered,
}: {
  api: AffiliateApi;
  offers: readonly {
    participation_id: string;
    campaign_id: string;
    campaign_name: string;
    brand_name: string;
    jurisdictions: readonly string[];
    products: readonly string[];
  }[];
  onResponded: () => void;
  /** An offer was answered from a campaign's detail view. */
  onTermsAnswered?: (() => void) | undefined;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  // §10.12b — joining is consent to the campaign's payout, so what that
  // payout is has to be readable before Accept, not after.
  const [viewing, setViewing] = useState<(typeof offers)[number] | null>(null);

  return (
      <Panel title={t('offers.panel')} aside={<span className="count count-badge">{offers.length}</span>}>
        {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
        {offers.map((offer) => {
          const jurisdictions = textArray(offer.jurisdictions);
          const products = textArray(offer.products);
          return (
            <article key={offer.participation_id} className="offer-card">
              <h3 className="offer-title">{offer.campaign_name}</h3>
              <p className="offer-brand">{offer.brand_name}</p>
              <dl className="offer-details">
                <dt>{t('campaign.runsIn')}</dt>
                <dd>{jurisdictions.join(', ') || '—'}</dd>
                <dt>{t('campaign.products')}</dt>
                <dd>{products.join(', ') || '—'}</dd>
              </dl>
              <p className="offer-note">{t('offers.note')}</p>
              <div className="offer-actions">
                <button
                  className="primary"
                  disabled={busy === offer.participation_id}
                  onClick={() => {
                    setBusy(offer.participation_id);
                    setFailure(null);
                    void api
                      .respondParticipation(offer.participation_id, 'ACCEPT')
                      .then(onResponded)
                      .catch((e: unknown) => setFailure(e))
                      .finally(() => setBusy(null));
                  }}
                >
                  {busy === offer.participation_id ? t('offers.accepting') : t('offers.accept')}
                </button>
                <button
                  className="copy"
                  disabled={busy === offer.participation_id}
                  onClick={() => {
                    setBusy(offer.participation_id);
                    setFailure(null);
                    void api
                      .respondParticipation(offer.participation_id, 'DECLINE')
                      .then(onResponded)
                      .catch((e: unknown) => setFailure(e))
                      .finally(() => setBusy(null));
                  }}
                >
                  {t('offers.decline')}
                </button>
                <button className="copy" type="button" onClick={() => setViewing(offer)}>
                  {t('campaignDetail.open')}
                </button>
              </div>
            </article>
          );
        })}
        {viewing ? (
          <CampaignDetailModal
            api={api}
            campaign={viewing}
            onClose={() => setViewing(null)}
            onAnswered={onTermsAnswered}
          />
        ) : null}
      </Panel>
  );
}

/**
 * §6.14c — a live invitation, and the two answers to it.
 *
 * This panel used to say "check the email this account signed up with for the
 * link", which is an instruction to leave the portal to answer something the
 * portal is already showing — and one that fails for every affiliate whose
 * invitation mail is old, filtered, or was sent to an address they no longer
 * read. The decision is made where it is read.
 *
 * The brand is sent back with the answer rather than trusted from the session,
 * so accepting means the brand named on this card and not whichever invitation
 * happens to be live when the click lands.
 */
function BrandInvitationsPanel({
  api,
  invitations,
  onResponded,
}: {
  api: AffiliateApi;
  invitations: readonly {
    brand_id: string | null;
    brand: string | null;
    sent_at: string;
    expires_at: string;
  }[];
  onResponded: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  const respond = (key: string, brandId: string | null, decision: 'ACCEPT' | 'DECLINE') => {
    setBusy(key);
    setFailure(null);
    void api
      .respondToInvitation(brandId, decision)
      .then(onResponded)
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(null));
  };

  return (
      <Panel title={t('invitations.panel')} aside={<span className="count count-badge">{invitations.length}</span>}>
        {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
        {invitations.map((inv, i) => {
          const key = `${inv.brand_id ?? 'all'}-${i}`;
          return (
          <article key={key} className="offer-card">
            {/* §2.8b — the operator-wide case reads 'All brands' out of the
                query, which is a literal and not a translation. The brand id
                is what says which case this is, so it decides which of the
                two strings is printed rather than a null that never arrives. */}
            <h3 className="offer-title">
              {inv.brand_id ? inv.brand : t('invitations.allBrands')}
            </h3>
            <dl className="offer-details">
              <dt>{t('invitations.sent')}</dt>
              <dd>{formatDateTime(inv.sent_at)}</dd>
              <dt>{t('invitations.expires')}</dt>
              <dd>{formatDateTime(inv.expires_at)}</dd>
            </dl>
            <p className="offer-note">{t('invitations.note')}</p>
            <div className="offer-actions">
              <button
                className="primary"
                disabled={busy === key}
                onClick={() => respond(key, inv.brand_id, 'ACCEPT')}
              >
                {busy === key ? t('offers.accepting') : t('offers.accept')}
              </button>
              <button
                className="copy"
                disabled={busy === key}
                onClick={() => respond(key, inv.brand_id, 'DECLINE')}
              >
                {t('offers.decline')}
              </button>
            </div>
          </article>
          );
        })}
      </Panel>
  );
}

/*
 * The offers, invitations and proposals that used to sit in a column beside
 * the dashboard's own figures now live in the shell's right rail — see
 * `PortalAside`. What is left here is the dashboard.
 */
function Dashboard({ api, range, now, me, filters }: ScreenProps) {
  type DashboardSection =
    | 'guide' | 'traffic' | 'news' | 'notes' | 'visits' | 'paid' | 'discrepancy' | 'detailed';
  const [section, setSection] = useState<DashboardSection>('traffic');
  /*
   * The dashboard's own narrowing: one campaign, one sub-ID value. Local to
   * this screen, because the shell's filter bar is shared with reports that
   * take neither; the traffic, the chart and the commission tile follow it.
   */
  const [campaignId, setCampaignId] = useState('');
  const [subIdIndex, setSubIdIndex] = useState('1');
  const [subIdDraft, setSubIdDraft] = useState('');
  const [subId, setSubId] = useState('');
  const narrowed = {
    ...range,
    ...(campaignId ? { campaign_id: campaignId } : {}),
    ...(subId ? { sub_id: subId, sub_id_index: subIdIndex } : {}),
  };
  const narrowKey = `${campaignId}|${subIdIndex}|${subId}`;
  const report = useLoad(() => api.funnel(narrowed), [range.from, range.to, range.brand_id, narrowKey]);
  // The day whose Excluded figure was opened; null while the popup is shut.
  const [excludedDay, setExcludedDay] = useState<string | null>(null);
  // The commission earned in the chosen dates — "earned so far" below is
  // bound to open periods and does not move with the range.
  const commissionTile = useLoad(
    () => api.commissionInRange(narrowed),
    [range.from, range.to, range.brand_id, narrowKey],
  );
  // §22.7, §39.1 — the money beside the counts. Loaded separately rather than
  // folded into the funnel: the funnel counts events, this prices them, and a
  // failure to price must not take the traffic figures down with it.
  const earnings = useLoad(
    () => api.currentEarnings(range),
    [range.from, range.to, range.brand_id],
  );
  // Still loaded here: the click-discrepancy panel names the campaign each
  // row belongs to.
  const campaigns = useLoad(() => api.campaigns(), []);
  /*
   * Which headline card has been opened, if any (owner request, 2026-09-29:
   * "tya card vr click kelyavr to number ksa ala he popup open houn tyat disl
   * pahije"). Above the loading guards because it is a hook — see
   * tools/check-hooks.mjs.
   */
  const [openCard, setOpenCard] = useState<'clicks' | 'registrations' | null>(null);
  const [grain, setGrain] = useState<Grain>('day');
  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') return <Failure error={report.error} />;

  const view = dashboard(me, report.value, now);
  // Ready, failed, or still loading are three different things. Collapsing the
  // last two into "render nothing" is how a money panel disappears from the
  // dashboard with nothing saying it failed — and an affiliate reads a missing
  // panel as "I earned nothing", which is the §30.1a mistake on a screen
  // rather than on a statement.
  const earned = earnings.state === 'ready' ? earningsSoFar(earnings.value) : null;
  const earningsFailed = earnings.state === 'error' ? earnings.error : null;
  // Index-paired with `dashboard()`'s headline list, in its order. The two
  // have to be changed together: a figure inserted in one and not the other
  // hands every figure after it its neighbour's chart.
  const series: readonly ((r: (typeof view.funnel)[number]) => number)[] = [
    // §12.13 — a day that carries no impression figure plots as nothing
    // served, which is what the sparkline is for: the shape of the days, not
    // the claim about any one of them (the headline makes that distinction).
    (r) => r.impressions ?? 0,
    (r) => r.counted,
    (r) => r.registrations,
    (r) => r.ftds,
    (r) => r.payableFtds,
  ];
  /*
   * Which headline index opens what. Index-paired with `dashboard()`'s
   * headline list exactly as `series` above is, and for the same reason: the
   * label is translated, so branching on it would work in English and quietly
   * stop working in Spanish.
   *
   * Only the two the owner asked for. A card with nothing to show behind it
   * keeps its plain, unpressable box rather than opening an empty popup.
   */
  const opens: readonly (('clicks' | 'registrations') | null)[] =
    [null, 'clicks', 'registrations', null, null];
  const cards = view.headline.map((f, i) => ({
    figure: f,
    spark: series[i] ? view.funnel.map(series[i]!) : undefined,
    opens: opens[i] ?? null,
  }));

  /*
   * The chart plots the whole chosen range, not only the days that carried a
   * row. The funnel answers with the days that had traffic, which is right for
   * the table under it and wrong here: "Last 30 days" holding two busy days
   * drew a two-point line across the plot, so a quiet month looked exactly
   * like a busy one and the gaps between bursts — what the chart is for —
   * were not drawn at all. A day with no row is a day with no clicks, and
   * plots as the zero it was.
   */
  const chartDays = daysBetween(range.from, range.to);
  const byDay = new Map(view.funnel.map((r) => [r.day, r]));
  /*
   * Day, month or year (owner, 2026-09-29). The range is filled first and
   * rolled up second, so a month with no traffic is a zero in the series
   * rather than a month missing from the axis.
   */
  const chartRows = chartDays.map((d) => ({
    day: d,
    clicks: byDay.get(d)?.counted ?? 0,
    registrations: byDay.get(d)?.registrations ?? 0,
  }));
  const buckets = rollUp(chartRows, grain, ['clicks', 'registrations']);

  return (
    <>
      <div className="page-head">
        <h1>{t('dash.title')}</h1>
        <span className="updated">{view.updated}</span>
      </div>
      {filters}
      <FilterBar>
        <Field label={t('dash.filter.campaign')}>
          <Select
            value={campaignId}
            onChange={setCampaignId}
            options={[
              { value: '', label: t('dash.filter.allCampaigns') },
              // One entry per campaign, by the campaign's id: the report
              // narrows a campaign across every version it has had.
              ...(campaigns.state === 'ready'
                ? [...new Map(campaigns.value.items.map((c) => [c.campaign_id, c])).values()]
                    .filter((c) => !range.brand_id || c.brand_id === range.brand_id)
                    .map((c) => ({ value: c.campaign_id, label: c.campaign_name }))
                : []),
            ]}
          />
        </Field>
        <Field label={t('dash.filter.subId')}>
          <Select
            value={subIdIndex}
            onChange={setSubIdIndex}
            options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
          />
        </Field>
        <Field label={t('dash.filter.subIdValue')}>
          <input
            className="control"
            value={subIdDraft}
            onChange={(e) => setSubIdDraft(e.currentTarget.value)}
            onBlur={() => setSubId(subIdDraft.trim())}
            onKeyDown={(e) => { if (e.key === 'Enter') setSubId(subIdDraft.trim()); }}
          />
        </Field>
      </FilterBar>

      {/* §6.3g — the state, and what it means for the money. The word alone is
          the same for an application under assessment and a live affiliate
          under investigation, and those mean very different things. */}
      {view.status.code !== 'Active' ? (
        <Callout tone="caution">
          <strong>{view.status.status}.</strong> {view.status.meaning}
        </Callout>
      ) : null}

      <div className="figures">
        {cards.map((c) => (
          <Figure
            key={c.figure.label}
            view={c.figure}
            {...(c.spark ? { spark: c.spark } : {})}
            {...(c.opens
              ? {
                  onOpen: () => setOpenCard(c.opens),
                  openLabel: t('dash.card.openLabel'),
                }
              : {})}
          />
        ))}
        {commissionTile.state === 'ready' ? (
          <Figure view={commissionInRangeFigure(commissionTile.value)} />
        ) : null}
      </div>
      {commissionTile.state === 'error' ? <Failure error={commissionTile.error} /> : null}
      {/* §39.4 — the mark is on each figure above; the sentence is said once. */}
      {view.headlineNote ? <p className="figures-note">{view.headlineNote}</p> : null}

      {/*
        * Clicks and registrations over the same days, each on its own scale.
        * Asked for on 2026-09-29: the cards say what the totals are, and this
        * says what the days looked like — where the traffic came in bursts,
        * and which of those bursts turned into anybody signing up.
        *
        * Read off `view.funnel`, the same rows the cards' sparklines and the
        * Detailed activity table below are built from. Never a second query:
        * two answers to the same question at two moments is how a chart comes
        * to disagree with the table under it.
        */}
      {openCard === 'clicks' ? (
        <ClicksBehindTheCard api={api} range={range} onClose={() => setOpenCard(null)} />
      ) : null}
      {openCard === 'registrations' ? (
        <RegistrationsBehindTheCard api={api} range={range} onClose={() => setOpenCard(null)} />
      ) : null}

      <Panel
        title={t('dash.chart.title')}
        aside={<span className="updated">{view.updated}</span>}
      >
        <TimeSeries
          days={buckets.map((b) => String(b['day']))}
          leftLabel={t('col.clicks')}
          rightLabel={t('dash.chart.registrations')}
          emptyNote={t('dash.chart.empty')}
          grain={grain}
          onGrain={setGrain}
          grainLabels={{
            day: t('chart.grain.day'),
            month: t('chart.grain.month'),
            year: t('chart.grain.year'),
          }}
          series={[
            {
              key: 'clicks',
              label: t('col.clicks'),
              values: buckets.map((b) => Number(b['clicks'])),
              axis: 'left',
              dashed: true,
              colour: 'var(--chart-clicks)',
            },
            {
              key: 'registrations',
              label: t('dash.chart.registrations'),
              values: buckets.map((b) => Number(b['registrations'])),
              axis: 'right',
              colour: 'var(--chart-signups)',
            },
          ]}
        />
      </Panel>

      {/* §1.4a — two deposit figures side by side with no explanation reads as
          an error. The gap is qualification and clawback, and it is named. */}
      {view.ftdNote ? (
        <Callout tone="caution" fold={false}>
          {view.ftdNote}
          {/* Owner, 2026-10-07 — the reasons themselves, from the earnings
              below, not only a pointer to a page that may not say. */}
          {ftdGapReasons(earned).length > 0 ? (
            <ul className="ftd-gap-reasons">
              {ftdGapReasons(earned).map((reason) => <li key={reason}>{reason}</li>)}
            </ul>
          ) : null}
        </Callout>
      ) : null}

      {/* §22.7, §39.1 — what the traffic above has earned, per brand, before
          the period is locked and a statement exists to say so. Every figure
          is marked provisional off the report's own envelope. */}
      {earningsFailed ? (
        <Panel title={t('earnings.title')}>
          <Failure error={earningsFailed} />
        </Panel>
      ) : null}
      {earned ? (
        <Panel title={t('earnings.title')}>
          {earned.empty ? (
            <Empty>{earned.empty}</Empty>
          ) : (
            <>
              <div className="figures">
                {earned.totals.map((f) => (
                  // Phase 2 decision (DESIGN.md §8): "Payable so far" is the
                  // affiliate dashboard's hero figure — but exactly one, even
                  // when an affiliate is paid in more than one currency.
                  // `screens.ts` picks which (`hero` on the view model), never
                  // this render site and never by the English label.
                  <Figure
                    key={`payable-${f.value}`}
                    view={f}
                    {...(f.hero ? { emphasis: 'hero' as const } : {})}
                  />
                ))}
                {earned.inPeriod.map((f) => (
                  <Figure key={`in-${f.value}`} view={f} />
                ))}
                {earned.notInPeriod.map((f) => (
                  <Figure key={`out-${f.value}`} view={f} />
                ))}
                {earned.held.map((f) => (
                  <Figure key={`held-${f.value}`} view={f} />
                ))}
              </div>
              {/* WP11 — first deposits waiting on a review, said right under
                  the payable figure they are missing from. */}
              {earned.underReviewNote ? (
                <Callout tone="caution">{earned.underReviewNote}</Callout>
              ) : null}
              {/* §39.4 — the mark is on each figure above; the sentence is
                  said once. Three tiles ending in the same two lines is how a
                  caveat becomes wallpaper. */}
              {earned.provisionalNote ? <p className="figures-note">{earned.provisionalNote}</p> : null}
              {/* Periods are opened by the operator; money earned before they
                  open one is still counted, and this says so once.
                  Item 2 (batch 2) — `.figures-note` is reserved for the
                  provisional legend above; this is a different fact, so it
                  is a plain `.panel-note`. */}
              {earned.notInPeriodNote ? <p className="panel-note">{earned.notInPeriodNote}</p> : null}
              {/* §24.2b — why a registration or deposit is not earning, with
                  the criterion named beside the brand it is on.
                  Item 2 (batch 2) — same: not the provisional legend. */}
              {earned.notEarningNote ? <p className="panel-note">{earned.notEarningNote}</p> : null}
              {/* §24.5 — why money that is earned is not money being paid.
                  Item 2 (batch 2) — same: not the provisional legend. */}
              {earned.heldNote ? <p className="panel-note">{earned.heldNote}</p> : null}
              <Sorted rows={earned.rows} by={{ brand: (r) => sortableCell(r.brand), computed: (r) => sortableCell(r.computed), pipeline: (r) => sortableCell(r.pipeline), held: (r) => sortableCell(r.held), payable: (r) => sortableCell(r.payable) }}>
                {(ordered, sort) => (
              <TableScroll>
                <table>
                  {/* No caption: the Panel above is headed "Earned so far",
                      and a table repeating its panel's title names two things
                      where there is one. */}
                  <thead>
                    <tr>
                      <SortTh sort={sort} col="brand">{t('col.brand')}</SortTh>
                      <SortTh sort={sort} col="computed" align="num">{t('col.computed')}</SortTh>
                      <SortTh sort={sort} col="pipeline" align="num">{t('col.pipeline')}</SortTh>
                      <SortTh sort={sort} col="held" align="num">{t('col.held')}</SortTh>
                      <SortTh sort={sort} col="payable" align="num">{t('col.payable')}</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {ordered.map((r) => (
                      <tr key={`${r.brand}-${r.currency}`}>
                        <td>
                          {r.brand}
                          <div className="figure-note">{r.since} · {r.period}</div>
                          {r.noPeriodNote ? <div className="figure-note">{r.noPeriodNote}</div> : null}
                          {r.heldUntil ? <div className="figure-note">{r.heldUntil}</div> : null}
                          {r.notEarning.map((line) => (
                            <div key={line} className="figure-note">{line}</div>
                          ))}
                        </td>
                        <td className="num">{r.computed}</td>
                        <td className="num">
                          {r.pipeline}
                          {/* §24.2b — the figure and how many players are behind
                              it, so the affiliate can check it against their own
                              qualification list rather than take it on trust. */}
                          {/* Its own line under the figure: inline, the
                              sentence ran into the amount (owner, 2026-10-07). */}
                          {r.pipelineNote ? (
                            <div className="figure-note" style={{ whiteSpace: 'normal', maxWidth: '16rem', marginInlineStart: 'auto' }}>{r.pipelineNote}</div>
                          ) : null}
                        </td>
                        <td className="num">{r.held}</td>
                        <td className="num">{r.payable}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
                )}
              </Sorted>
            </>
          )}
        </Panel>
      ) : null}

      {excludedDay ? (
        <ExcludedClicks
          api={api}
          now={now}
          query={{ ...narrowed, from: excludedDay, to: excludedDay }}
          day={excludedDay}
          onClose={() => setExcludedDay(null)}
        />
      ) : null}
      <SectionSplit
        ariaLabel={t('dash.sections')}
        section={section}
        onSection={setSection}
        groups={[{
          label: t('dash.title'),
          items: [
            /*
             * First in the list and *not* the default section — the console
             * still opens on Traffic, so nobody who knows their way around has
             * to walk past this. Somebody who does not know their way around
             * finds it at the top of the first screen they land on.
             */
            { id: 'guide', label: t('dash.section.guide') },
            { id: 'traffic', label: t('dash.section.traffic') },
            { id: 'news', label: t('dash.section.news') },
            { id: 'notes', label: t('dash.section.notes') },
            { id: 'visits', label: t('dash.section.visits') },
            { id: 'paid', label: t('dash.section.paid') },
            { id: 'discrepancy', label: t('dash.section.discrepancy') },
            { id: 'detailed', label: t('detailedActivity.title') },
          ],
        }]}
      >
          {section === 'traffic' ? (
      <Panel
        title={t('dash.traffic.panel')}
        aside={<span className="count count-badge">{t(view.funnel.length === 1 ? 'dash.traffic.days.one' : 'dash.traffic.days.other', { n: view.funnel.length })}</span>}
      >
        {view.funnel.length === 0 ? (
          <Empty>{t('dash.traffic.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('col.day')}</th>
                  {/* §12.13 — before clicks, in the order the two happen. */}
                  <th className="num">{t('col.impressions')}</th>
                  <th className="num">{t('col.clicks')}</th>
                  <th className="num">{t('col.excluded')}</th>
                  <th className="num">{t('col.registrations')}</th>
                  <th className="num">{t('col.deposits')}</th>
                </tr>
              </thead>
              <tbody>
                {view.funnel.map((row) => (
                  <Fragment key={row.day}>
                  <tr className={row.excludedNote ? 'has-note' : undefined}>
                    <td className="num faint">{row.day}</td>
                    {/* A dash, not a nought: a day the response carried no
                        impression figure for is not a day that served none. */}
                    <td className="num faint">
                      {row.impressions === undefined ? '—' : formatCount(row.impressions)}
                    </td>
                    <td className="num">{formatCount(row.counted)}</td>
                    <td className="num faint">
                      {row.excluded ? (
                        <button
                          type="button"
                          className="link-button"
                          aria-label={t(row.excluded === 1 ? 'excludedClicks.open.one' : 'excludedClicks.open.other', { n: formatCount(row.excluded), day: row.day })}
                          onClick={() => setExcludedDay(row.day)}
                        >
                          {formatCount(row.excluded)}
                        </button>
                      ) : '—'}
                    </td>
                    <td className="num">{formatCount(row.registrations)}</td>
                    <td className="num">{formatCount(row.ftds)}</td>
                  </tr>
                  {/* Moved off the "Why excluded" cell, which a dedup/repeat/
                      attribution sentence stretched to three-plus lines — see
                      styles.css's `tr.has-note` / `tr.row-note` pair. */}
                  {row.excludedNote ? (
                    <tr className="row-note">
                      {/* day, impressions, clicks, excluded, registrations, deposits */}
                      <td colSpan={6}><span className="row-note-text">{row.excludedNote}</span></td>
                    </tr>
                  ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
          ) : null}

          {section === 'guide' ? <WhatsHere api={api} /> : null}

          {section === 'news' ? <NewsPanel api={api} /> : null}
          {section === 'notes' ? <OperatorNotesPanel api={api} /> : null}

          {section === 'visits' ? (
            <RecentVisitsPanel api={api} range={range} />
          ) : null}

          {section === 'paid' ? <PaymentSummaryPanel api={api} /> : null}

          {section === 'detailed'
            ? <DetailedActivityPanel api={api} range={range} now={now} /> : null}
          {section === 'discrepancy' ? (
            <ClickDiscrepancyPanel
              api={api}
              range={range}
              campaigns={campaigns.state === 'ready' ? campaigns.value.items : []}
            />
          ) : null}

          <Callout>{view.attribution}</Callout>
      </SectionSplit>
    </>
  );
}

/**
 * The right rail — what is waiting for an answer.
 *
 * Offers, invitations and proposed terms all share one shape: somebody else
 * has asked this affiliate something, and nothing happens until they reply.
 * They used to sit in a column beside the dashboard's figures, which put them
 * on one screen out of twelve and below the fold on most of them — an
 * invitation that expires in fourteen days was reachable only by scrolling a
 * report nobody opens every day.
 *
 * So it is chrome, like the left rail, and built out of the same parts: same
 * gradient, same border, same sticky full-height column that scrolls on its
 * own. An affiliate reading their Links screen can see there is something to
 * answer without leaving it.
 *
 * It renders nothing at all when there is nothing to answer. A permanent
 * empty column is a worse answer than no column: it teaches the reader to
 * ignore the space, which is the one thing this must not do.
 */
function PortalAside({
  api,
  onBrandsChanged,
  dealProposalsVersion,
  onDealProposalsChanged,
  currentTab,
}: {
  api: AffiliateApi;
  /** Accepting an invitation adds a brand the rail's selector has not seen. */
  onBrandsChanged: () => void;
  /** Shared with the Terms screen: an answer given there empties this rail too. */
  dealProposalsVersion: number;
  onDealProposalsChanged: () => void;
  /**
   * The Terms tab already shows "Terms awaiting your answer" as the page
   * itself, open and in full. Showing the rail's collapsed copy of the same
   * panel beside it is the same information twice, once easy to miss and once
   * that has to stay collapsed to fit — so the rail drops its copy while that
   * tab is open. The offers, invitations and discoverable-campaigns panels
   * are not duplicated anywhere and stay.
   */
  currentTab: Tab;
}) {
  const [campaignVersion, setCampaignVersion] = useState(0);
  const [invitationsVersion, setInvitationsVersion] = useState(0);

  const campaigns = useLoad(() => api.campaigns(), [campaignVersion]);
  const offered = campaigns.state === 'ready' ? campaigns.value.offered : [];
  // §10.11 — campaigns visible to them that they are not on. Separate from
  // `offered` because the two are different acts: one is being asked, the
  // other is asking.
  const discoverable = campaigns.state === 'ready' ? campaigns.value.discoverable : [];
  const discoverNote = campaigns.state === 'ready' ? campaigns.value.discoverable_note : undefined;
  // §6.14c — not keyed on the brand filter. An invitation is an offer to join
  // a brand this affiliate is not on yet, so scoping it to the brand they are
  // currently looking at would hide every invitation there is.
  const invitations = useLoad(() => api.myInvitations(), [invitationsVersion]);
  const pendingInvitations = invitations.state === 'ready' ? invitations.value.items : [];
  const dealProposalsLoaded = useLoad(() => api.dealProposals(), [dealProposalsVersion]);
  const pendingDealProposals = dealProposalsLoaded.state === 'ready' ? dealProposalsLoaded.value.items : [];
  // The Terms tab is already showing these, open and in full — the rail's
  // collapsed copy would only duplicate the page it sits beside.
  const showDealProposals = pendingDealProposals.length > 0 && currentTab !== 'terms';

  if (
    offered.length === 0 &&
    discoverable.length === 0 &&
    pendingInvitations.length === 0 &&
    !showDealProposals
  ) {
    return null;
  }

  return (
    <aside className="rail rail-right" aria-label={t('dash.aside')}>
      {showDealProposals ? (
        <PendingDealProposalsPanel
          api={api}
          proposals={pendingDealProposals}
          onResponded={onDealProposalsChanged}
        />
      ) : null}
      {offered.length > 0 ? (
        <CampaignOffersPanel
          api={api}
          offers={offered}
          onResponded={() => setCampaignVersion((v) => v + 1)}
          onTermsAnswered={onDealProposalsChanged}
        />
      ) : null}
      {discoverable.length > 0 ? (
        <DiscoverCampaignsPanel
          api={api}
          campaigns={discoverable}
          {...(discoverNote ? { note: discoverNote } : {})}
          onRequested={() => setCampaignVersion((v) => v + 1)}
          onTermsAnswered={onDealProposalsChanged}
        />
      ) : null}
      {pendingInvitations.length > 0 ? (
        <BrandInvitationsPanel
          api={api}
          invitations={pendingInvitations}
          onResponded={() => {
            // Accepting adds a brand, which changes the brand list, the
            // campaigns on it and the offers beside it. Re-read all of them
            // rather than only the panel that was clicked.
            setInvitationsVersion((v) => v + 1);
            setCampaignVersion((v) => v + 1);
            onBrandsChanged();
          }}
        />
      ) : null}
    </aside>
  );
}

/* ------------------------------------------------------------------ *
 * Links
 * ------------------------------------------------------------------ */

/**
 * A form rather than a button, because a link is three decisions and not one:
 * which campaign, which landing page, and what the affiliate wants to call the
 * placement. Minting on a single click gives them a link to the campaign's
 * default page with no labels, which they then cannot tell apart from the next
 * four they make.
 *
 * The preview is explicitly a preview. The API builds the link that is stored —
 * two builders disagree eventually, and the disagreement surfaces as an
 * affiliate's tracker reporting a sub-ID ours never saw.
 */
function CreateLinkForm({
  api,
  campaigns,
  onCreated,
}: {
  api: AffiliateApi;
  campaigns: {
    campaign_id: string;
    campaign_name: string;
    brand_name: string;
    brand_id: string;
    campaign_version_id: string;
    trafficback_url: string | null;
    postclick_anchor: string;
    valid_from: string;
    /** When it stops taking traffic. Null is open-ended, and says so. */
    valid_to: string | null;
    jurisdictions: readonly string[];
    products: readonly string[];
    os_codes: readonly string[];
    device_classes: readonly string[];
    goals: readonly { id: string; key: string; name: string }[];
    landing_pages: readonly { landing_page_id: string; label: string; url: string }[];
    effective_attribution_window_days: number;
    /** §39.13 — stated on the campaign, before any conversion is sampled away. */
    sampling_rate_bps: number;
  }[];
  onCreated: () => void;
}) {
  const [versionId, setVersionId] = useState(campaigns[0]?.campaign_version_id ?? '');
  const [trackingDomainId, setTrackingDomainId] = useState('');
  // L13 — which of the campaign's pages this link lands on. Empty is the
  // campaign's own default, which is what every link used to get.
  const [landingPageId, setLandingPageId] = useState('');
  const [subIds, setSubIds] = useState<string[]>(Array<string>(SUB_ID_SLOTS).fill(''));
  const [extraSlots, setExtraSlots] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [created, setCreated] = useState<string | null>(null);
  // §6.16 — only offered when this account has a sub-account to name; empty
  // for a DIRECT affiliate, which is every affiliate by default.
  const [subAccountId, setSubAccountId] = useState('');
  const subAccounts = useLoad(() => api.subAccounts(), []);
  /*
   * §11.9 — the channel this link is filed under, from the affiliate's own
   * declared catalogue. The Sources screen (`traffic-sources.tsx`) is the one
   * place that already loads it (`api.trafficSources().active_channels`), so
   * this reuses that same call rather than fetching a second copy or
   * hardcoding the list here. Empty string means "filed under none" locally;
   * `createLink` only sends `channel` when it is truthy (§11.9's "a link
   * filed under none records none"), so this never sends an empty string.
   */
  const [channel, setChannel] = useState('');
  const channelCatalogue = useLoad(() => api.trafficSources(), []);
  /*
   * §7.12d — what the chosen campaign pays, one press from the form. The
   * featured table is the only other way in, and a campaign nobody featured
   * is still one somebody is about to make a link for.
   */
  const [viewingTerms, setViewingTerms] = useState(false);
  // Item 7 — the Label field is required, so `checkSubId` flags it empty on
  // the very first render; shown only once the reader has actually typed
  // into it; the button stays disabled either way, so nothing is let through.
  const [labelTouched, setLabelTouched] = useState(false);

  const domains = useLoad(
    () => (versionId ? api.trackingDomains(versionId) : Promise.resolve({ items: [] })),
    [versionId],
  );

  /*
   * §7.12d — what the chosen campaign pays, read here rather than behind a
   * press. The figures are the reason an affiliate is making this link; a
   * button that hides them makes the reader ask for what they came for.
   */
  const termsOf = campaigns.find((c) => c.campaign_version_id === versionId)?.campaign_id;
  const terms = useLoad(
    () => (termsOf ? api.campaignTerms(termsOf) : Promise.resolve(null)),
    [termsOf],
  );

  // The tracking domain choice belongs to the campaign's brand (§10.3), so
  // changing the campaign invalidates it rather than carrying it across. The
  // landing page is the campaign's too (L13): a choice made on one campaign
  // names a page the next one does not have.
  useEffect(() => { setTrackingDomainId(''); setLandingPageId(''); }, [versionId]);
  useEffect(() => {
    if (!campaigns.some((c) => c.campaign_version_id === versionId)) {
      setVersionId(campaigns[0]?.campaign_version_id ?? '');
    }
  }, [campaigns, versionId]);

  const selectedCampaign = campaigns.find((c) => c.campaign_version_id === versionId);

  const termsView = terms.state === 'ready' && terms.value && selectedCampaign
    ? campaignTermsView(terms.value, selectedCampaign.brand_name)
    : null;

  const errors = subIds.map((value, i) => checkSubId(value, i === 0));
  const blocked = errors.some((e) => e !== null) || versionId === '';

  const submit = () => {
    setBusy(true);
    setFailure(null);
    api
      .createLink(
        versionId,
        subIds,
        landingPageId || undefined,
        trackingDomainId || undefined,
        // Every link a screen can show is short — the token in the long
        // form is never shown anywhere in the FE, so there is nothing left
        // for an affiliate to choose. `true` always mints one regardless of
        // the operator's own default (§11.6a); a mint failure is the only
        // reason `short_url` would still come back absent.
        true,
        subAccountId || undefined,
        channel || undefined,
      )
      .then((link) => {
        setCreated(link.short_url ?? link.url);
        onCreated();
      })
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  };

  if (campaigns.length === 0) {
    // §10.12a — being approved by an operator is not being on every campaign,
    // and saying so beats an empty box.
    return (
      <Empty>
        {t('link.noCampaigns')}
      </Empty>
    );
  }

  return (
    <div className="form">
      {failure ? <Failure error={failure} /> : null}

      <Field label={t('link.field.campaign')}>
        <Select
          value={versionId}
          onChange={setVersionId}
          options={campaigns.map((c) => ({
            value: c.campaign_version_id,
            // §39.13 — the rate is on the list, not only on the campaign
            // once it is chosen.
            label: `${c.brand_name} · ${c.campaign_name}` +
              (samplingChip(c.sampling_rate_bps) ? ` · ${samplingChip(c.sampling_rate_bps)}` : ''),
          }))}
        />
      </Field>

      {/* L13 — the page this link sends people to, where the campaign has
          more than one. One page is shown; none says where they will land. */}
      {selectedCampaign ? (
        <LinkLandingPageField
          pages={selectedCampaign.landing_pages}
          value={landingPageId}
          onChange={setLandingPageId}
        />
      ) : null}

      {/* Items 7/8 — the same facts the operator sees setting the campaign
          up, read-only, so a link is created knowing where it runs and what
          it pays attention to, not just its name.
          Item 7 — about fifteen rows of these between Campaign and Label
          pushed "Create link" below the fold; collapsed the same way the
          right rail already collapses a deal's terms, behind a one-line
          summary. */}
      {selectedCampaign ? (
        <TermsDisclosure open={false} summary={t('link.terms.summary')}>
          <dl className="offer-details">
            <dt>{t('campaign.runsIn')}</dt>
            <dd>{textArray(selectedCampaign.jurisdictions).join(', ') || '—'}</dd>
            <dt>{t('campaign.products')}</dt>
            <dd>{textArray(selectedCampaign.products).join(', ') || '—'}</dd>
            {textArray(selectedCampaign.os_codes).length > 0 ? (
              <>
                <dt>{t('campaign.osTargeting')}</dt>
                <dd>{textArray(selectedCampaign.os_codes).join(', ')}</dd>
              </>
            ) : null}
            {textArray(selectedCampaign.device_classes).length > 0 ? (
              <>
                <dt>{t('campaign.deviceTargeting')}</dt>
                <dd>{textArray(selectedCampaign.device_classes).join(', ')}</dd>
              </>
            ) : null}
            <dt>{t('campaign.goals')}</dt>
            <dd>{selectedCampaign.goals.map((g) => g.name).join(', ') || '—'}</dd>
            <dt>{t('campaign.window')}</dt>
            <dd>{t(selectedCampaign.effective_attribution_window_days === 1 ? 'campaign.window.one' : 'campaign.window.other', { n: selectedCampaign.effective_attribution_window_days })}</dd>
            {/* §39.13 — stated on the campaign, so a sampled-away conversion is
                never the first the affiliate hears of it. */}
            <dt>{t('campaign.sampling')}</dt>
            <dd>{samplingRateLabel(selectedCampaign.sampling_rate_bps)}</dd>
            <dt>{t('campaign.anchor')}</dt>
            <dd>{selectedCampaign.postclick_anchor === 'GOAL' ? t('campaign.anchor.goal') : t('campaign.anchor.click')}</dd>
            <dt>{t('campaign.liveSince')}</dt>
            <dd>{formatDate(selectedCampaign.valid_from)}</dd>
            {/* The other half of the campaign's life. Without it the panel said
                when it started and how long an attribution counts for, and the
                two got read as one. */}
            <dt>{t('campaign.runsUntil')}</dt>
            <dd>
              {(() => {
                const ends = campaignEndsAt(selectedCampaign);
                return ends ? formatDate(ends) : t('campaign.runsUntil.open');
              })()}
            </dd>
            {selectedCampaign.trafficback_url ? (
              <>
                <dt>{t('campaign.trafficback')}</dt>
                <dd className="wrap">{selectedCampaign.trafficback_url}</dd>
              </>
            ) : null}
          </dl>
          <div className="stack">
            <h3 className="offer-title">{t('campaignDetail.whatItPays')}</h3>
            {terms.state === 'loading' ? <Loading /> : null}
            {terms.state === 'error' ? <Failure error={terms.error} /> : null}
            {termsView ? (
              termsView.applies === null ? (
                <Empty>{t('campaignTerms.none')}</Empty>
              ) : (
                <>
                  <p className="terms-summary">{termsView.applies.heading}</p>
                  {termsView.applies.rows.length > 0 ? (
                    <TermsRowsTable rows={termsView.applies.rows} />
                  ) : (
                    <Callout tone="caution">{t('campaignTerms.unreadable')}</Callout>
                  )}
                  {termsView.applies.since ? <p className="link-note">{termsView.applies.since}</p> : null}
                </>
              )
            ) : null}
            {/* §7.11 — terms on offer pay nothing until accepted, so they are
                never shown beside the ones in force. Answering one is still a
                deliberate act, and the card that asks for it opens here. */}
            {termsView?.offer ? (
              // Item 3 (batch 2) — a deliberate act to answer, not a passing
              // remark; the button that opens it must not be folded away.
              <Callout tone="note" fold={false}>
                {t('campaignTerms.offer.note')}{' '}
                <button className="link-button" type="button" onClick={() => setViewingTerms(true)}>
                  {t('campaignTerms.offer.title')}
                </button>
              </Callout>
            ) : null}
          </div>
        </TermsDisclosure>
      ) : null}
      {viewingTerms && selectedCampaign ? (
        <CampaignDetailModal
          api={api}
          campaign={selectedCampaign}
          onClose={() => setViewingTerms(false)}
        />
      ) : null}

      {domains.state === 'ready' && domains.value.items.length > 1 ? (
        <Field
          label={t('link.field.domain')}
          hint={t('link.field.domain.hint')}
        >
          <Select
            value={trackingDomainId || (domains.value.items.find((d) => d.is_default)?.id ?? '')}
            onChange={setTrackingDomainId}
            options={domains.value.items.map((d) => ({
              value: d.id,
              label: d.is_default ? t('link.field.domain.default', { domain: d.domain }) : d.domain,
            }))}
          />
        </Field>
      ) : null}

      {/* §6.16 — only offered on an agency account; a DIRECT affiliate's
          own list comes back empty and this field never renders. */}
      {subAccounts.state === 'ready' && subAccounts.value.items.length > 0 ? (
        <Field label={t('link.field.subAccount')} hint={t('link.field.subAccount.hint')}>
          <Select
            value={subAccountId}
            onChange={setSubAccountId}
            options={[
              { value: '', label: t('link.field.subAccount.none') },
              ...subAccounts.value.items
                .filter((s) => s.archived_at === null)
                .map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` })),
            ]}
          />
        </Field>
      ) : null}

      {/*
       * §11.9/§11.9a — an ordinary, valid choice, offered beside every real
       * channel rather than after it: this is the affiliate's own statement
       * of where the traffic will come from, never a measurement, and never
       * offered as though the platform prices by an observed source. Only
       * the catalogue's still-active codes are offered — a withdrawn channel
       * keeps its row (so clicks already filed under it are not orphaned)
       * but the API refuses it on a new link, so it is left out here too.
       */}
      <Field label={t('link.field.channel')} hint={t('link.field.channel.hint')}>
        <Select
          value={channel}
          onChange={setChannel}
          options={[
            { value: '', label: t('link.field.channel.none') },
            ...(channelCatalogue.state === 'ready'
              ? channelCatalogue.value.active_channels.map((c) => ({
                  value: c,
                  label: t(`trafficChannel.${c}` as AffiliateMessageKey),
                }))
              : []),
          ]}
        />
      </Field>

      {/*
        * Ten empty boxes labelled S1..S10 explain nothing. Most affiliates use
        * one; the rest exist because §11.2 provides ten slots, not because
        * anyone needs ten on first use. So: one field, and the others on
        * request.
        */}
      <Field
        label={t('link.field.label')}
        hint={t('link.field.label.hint')}
        {...(labelTouched && errors[0] ? { error: errors[0]! } : {})}
      >
        <TextInput
          value={subIds[0] ?? ''}
          invalid={labelTouched && errors[0] !== null}
          placeholder={t('link.field.label.placeholder')}
          onChange={(v) => {
            setLabelTouched(true);
            setSubIds((prev) => prev.map((p, j) => (j === 0 ? v : p)));
          }}
        />
      </Field>

      {extraSlots > 0 ? (
        <div className="subids">
          {subIds.slice(1, 1 + extraSlots).map((value, i) => (
            <Field
              key={i + 1}
              label={t('link.field.extraLabel', { n: i + 2 })}
              {...(errors[i + 1] ? { error: errors[i + 1]! } : {})}
            >
              <TextInput
                value={value}
                invalid={errors[i + 1] !== null}
                onChange={(v) =>
                  setSubIds((prev) => prev.map((p, j) => (j === i + 1 ? v : p)))
                }
              />
            </Field>
          ))}
        </div>
      ) : null}

      {extraSlots < SUB_ID_SLOTS - 1 ? (
        <button className="link-button" onClick={() => setExtraSlots((n) => n + 1)}>
          {t('link.addLabel', { n: SUB_ID_SLOTS - 1 - extraSlots })}
        </button>
      ) : null}

      <div className="form-actions">
        <button className="primary" disabled={blocked || busy} onClick={submit}>
          {busy ? t('link.creating') : t('link.create')}
        </button>
      </div>

      {created ? (
        <Field group label={t('link.new')}>
          <CopyableCode text={created} />
        </Field>
      ) : null}
    </div>
  );
}

/**
 * §11.5 — a revoked link keeps working for clicks already in flight, but
 * takes new ones nowhere. There is no undo, so this asks before it acts.
 */
function RevokeLinkModal({
  api,
  linkId,
  campaign,
  label,
  onClose,
  onDone,
}: {
  api: AffiliateApi;
  linkId: string;
  /** Item 2 — named in the confirmation, not just in the list it was opened from. */
  campaign: string;
  label: string;
  onClose: () => void;
  onDone: () => void;
}) {
  return (
    <Confirm
      title={t('link.revoke.title')}
      tone="error"
      confirmLabel={t('link.revoke')}
      body={<>{t('link.revoke.which', { campaign, label })} {t('link.revoke.warning')}</>}
      onCancel={onClose}
      onConfirm={() => api.revokeLink(linkId).then(onDone)}
    />
  );
}

/**
 * Asking the operator for a campaign.
 *
 * Not creating one, and the screen says why rather than leaving somebody to
 * wonder where the Create button is: a campaign carries a territory, products,
 * goals and the deal that prices it, and those are the operator's to decide.
 *
 * What this adds is the part that was missing — somewhere for the asking to be
 * recorded and answered, rather than an email nobody can find later.
 */
function AskForCampaign({
  api,
  brands,
}: {
  api: AffiliateApi;
  brands: readonly { brand_id: string; brand_name: string }[];
}) {
  const [version, setVersion] = useState(0);
  const [brandId, setBrandId] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const loaded = useLoad(() => api.campaignRequests(), [version]);

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  return (
    <Panel title={t('links.section.ask')}>
      {failure ? <Failure error={failure} /> : null}
      <Callout>{t('ask.lead')}</Callout>

      <div className="form-row">
        <Field label={t('col.brand')}>
          <Select
            value={brandId}
            onChange={setBrandId}
            options={[
              { value: '', label: t('ask.pickBrand') },
              ...brands.map((b) => ({ value: b.brand_id, label: b.brand_name })),
            ]}
          />
        </Field>
        <Field label={t('ask.name')}>
          <TextInput value={name} onChange={setName} placeholder={t('ask.name.placeholder')} />
        </Field>
      </div>
      <Field label={t('ask.reason')} hint={t('ask.reason.hint')}>
        <textarea className="control" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </Field>
      <div className="form-actions">
        <button
          className="primary"
          type="button"
          disabled={busy || brandId === '' || name.trim() === '' || reason.trim() === ''}
          onClick={() => {
            setBusy(true);
            setFailure(null);
            api
              .askForCampaign({ brandId, wantedName: name.trim(), reason: reason.trim() })
              .then(() => { setName(''); setReason(''); setVersion((n) => n + 1); })
              .catch((e: unknown) => setFailure(e))
              .finally(() => setBusy(false));
          }}
        >
          {t('ask.send')}
        </button>
      </div>

      {loaded.value.items.length === 0 ? (
        <Empty>{t('ask.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th>{t('ask.name')}</th>
                <th>{t('col.brand')}</th>
                <th>{t('col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {loaded.value.items.map((r) => (
                <tr key={r.id}>
                  <td className="wrap">{r.wanted_name}</td>
                  <td className="faint">{r.brand}</td>
                  <td>
                    <Chip tone={r.status === 'FULFILLED' ? 'locked' : r.status === 'DECLINED' ? 'neutral' : 'provisional'}>
                      {t(`ask.status.${r.status}` as AffiliateMessageKey)}
                    </Chip>
                    {/* An answer of no with no reason cannot be acted on next
                        time, so the reason travels with the status. */}
                    {r.decision_note ? <div className="faint">{r.decision_note}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

/**
 * The affiliate's own labelling of their own links, and what each label returned.
 *
 * Deliberately not called a campaign. The operator's campaign carries products,
 * goals, landing pages, versions and a deal; this is a name somebody puts on
 * their own links so a report can be split by it, and two things of that name
 * meaning different things on one screen is how somebody joins the wrong one.
 *
 * The spend is the affiliate's own note. Nothing checks it, nothing is priced
 * from it, and the operator never sees it — it is here so the affiliate can
 * read their own return.
 */
function LinkGroups({ api }: { api: AffiliateApi }) {
  const [version, setVersion] = useState(0);
  const [label, setLabel] = useState('');
  const [spend, setSpend] = useState('');
  const [currency, setCurrency] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const loaded = useLoad(() => api.linkGroups(), [version]);
  const currencies = useLoad(() => api.currencies(), []);

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  return (
    <Panel
      title={t('links.section.groups')}
      aside={<span className="count count-badge">{loaded.value.items.length}</span>}
    >
      {failure ? <Failure error={failure} /> : null}
      <p className="link-note">{loaded.value.note}</p>

      <div className="form-row">
        <Field label={t('groups.name')} hint={t('groups.name.hint')}>
          <TextInput value={label} onChange={setLabel} placeholder={t('groups.placeholder')} />
        </Field>
        <Field label={t('groups.spend')}>
          <TextInput value={spend} onChange={setSpend} placeholder="250" />
        </Field>
        <Field label={t('payout.minimum.currency')}>
          <Select
            value={currency}
            onChange={setCurrency}
            options={[
              { value: '', label: t('groups.noSpend') },
              ...(currencies.state === 'ready'
                ? currencies.value.items.map((c) => ({ value: c.code, label: c.code }))
                : []),
            ]}
          />
        </Field>
      </div>
      <div className="form-actions">
        <button
          className="primary"
          type="button"
          disabled={busy || label.trim() === ''}
          onClick={() => {
            setBusy(true);
            setFailure(null);
            api
              .createLinkGroup({
                label: label.trim(),
                ...(spend.trim() !== '' ? { spendAmount: spend.trim(), spendCurrency: currency } : {}),
              })
              .then(() => { setLabel(''); setSpend(''); setCurrency(''); setVersion((n) => n + 1); })
              .catch((e: unknown) => setFailure(e))
              .finally(() => setBusy(false));
          }}
        >
          {t('groups.create')}
        </button>
      </div>

      {loaded.value.items.length === 0 ? (
        <Empty>{t('groups.empty')}</Empty>
      ) : (
        <Sorted rows={loaded.value.items} by={{ id: (g) => sortableCell(g.id), links: (g) => sortableCell(g.links), spend_minor: (g) => sortableCell(g.spend_minor), spend_currency: (g) => sortableCell(g.spend_currency) }}>
          {(ordered, sort) => (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <SortTh sort={sort} col="id">{t('groups.name')}</SortTh>
                <SortTh sort={sort} col="links" align="num">{t('groups.links')}</SortTh>
                <SortTh sort={sort} col="spend_minor" align="num">{t('groups.spend')}</SortTh>
                <SortTh sort={sort} col="spend_currency" align="num">{t('groups.earned')}</SortTh>
              </tr>
            </thead>
            <tbody>
              {ordered.map((g) => (
                <tr key={g.id}>
                  <td>
                    {editing === g.id ? (
                      <span className="cell-inline">
                        <TextInput value={draft} onChange={setDraft} />
                        <button
                          className="link-button btn-sm"
                          type="button"
                          onClick={() => {
                            api
                              .updateLinkGroup(g.id, { label: draft.trim() })
                              .then(() => { setEditing(null); setVersion((n) => n + 1); })
                              .catch((e: unknown) => setFailure(e));
                          }}
                        >
                          {t('payout.save')}
                        </button>
                      </span>
                    ) : (
                      <span className="cell-inline">
                        {g.label}
                        <button
                          className="link-button btn-sm"
                          type="button"
                          onClick={() => { setEditing(g.id); setDraft(g.label); }}
                        >
                          {t('groups.rename')}
                        </button>
                      </span>
                    )}
                  </td>
                  <td className="num faint">{formatCount(g.links)}</td>
                  <td className="num faint">
                    {g.spend_minor && g.spend_currency
                      ? disputeDetailAmount(g.spend_minor, g.spend_currency)
                      : '—'}
                  </td>
                  <td className="num">
                    {/* Earned is in whatever the deals under it pay in, so it
                        is shown with its currency rather than added across. */}
                    {g.spend_currency
                      ? disputeDetailAmount(g.earned_minor, g.spend_currency)
                      : formatCount(Number(g.earned_minor) / 100)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
          )}
        </Sorted>
      )}
    </Panel>
  );
}

/**
 * §12.13 — how each banner actually performed, beside the gallery it was picked from.
 *
 * The one comparison the gallery cannot make: two banners with the same click
 * count are not the same banner if one of them was served ten times as often.
 * That ratio needs impressions per creative, which is what 0219 made storable.
 */
function MediaReport({
  api,
  range,
  now,
}: {
  api: AffiliateApi;
  range: ScreenProps['range'];
  now: ScreenProps['now'];
}) {
  const loaded = useLoad(() => api.mediaReport(range), [range.from, range.to, range.brand_id]);

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const view = mediaReport(loaded.value, now);

  return (
    <Panel title={t('links.section.media')} aside={<span className="updated">{view.updated}</span>}>
      <p className="link-note">{t('media.lead')}</p>
      {view.empty ? (
        <Empty>{view.empty}</Empty>
      ) : (
        <Sorted rows={view.rows} by={{ typeLabel: (r) => sortableCell(r.typeLabel), size: (r) => sortableCell(r.size), impressions: (r) => sortableCell(r.impressions), clicks: (r) => sortableCell(r.clicks), reach: (r) => sortableCell(r.reach), ctr: (r) => sortableCell(r.ctr) }}>
          {(ordered, sort) => (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <SortTh sort={sort} col="typeLabel">{t('col.type')}</SortTh>
                <SortTh sort={sort} col="size">{t('creatives.filter.size')}</SortTh>
                <SortTh sort={sort} col="impressions" align="num">{t('col.impressions')}</SortTh>
                <SortTh sort={sort} col="clicks" align="num">{t('col.clicks')}</SortTh>
                <SortTh sort={sort} col="reach" align="num">{t('col.reach')}</SortTh>
                <SortTh sort={sort} col="ctr" align="num">{t('figure.ctr')}</SortTh>
              </tr>
            </thead>
            <tbody>
              {ordered.map((r) => (
                <tr key={r.creativeId}>
                  <td>{r.typeLabel}</td>
                  <td className="faint">{r.size}</td>
                  <td className="num faint">{r.impressions}</td>
                  <td className="num">{r.clicks}</td>
                  <td className="num faint">{r.reach}</td>
                  <td className="num">{r.ctr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
          )}
        </Sorted>
      )}
    </Panel>
  );
}

/**
 * The published creatives, as pictures rather than as a list of addresses.
 *
 * What was here was three columns of text ending in a bare URI, which is the
 * one thing an affiliate cannot use: they could not see what a banner looked
 * like without opening it, could not narrow twelve assets down to the one size
 * their page has room for, and had to write the anchor themselves.
 *
 * The code each card hands over carries that affiliate's own link for the
 * campaign the creative was published to, and the impression pixel beside it,
 * so a banner put up this way is counted when it is shown as well as when it
 * is clicked.
 */
function CreativesGallery({
  rows,
  loading,
  title,
  linksFor,
}: {
  rows: readonly {
    id: string; campaign_id: string; type: string; language: string | null;
    width?: number | null; height?: number | null; asset_uri: string;
    product?: string | null; geos?: readonly string[] | string | null;
  }[];
  loading: boolean;
  title: string;
  linksFor: (campaignId: string) => readonly CreativeLinkChoice[];
}) {
  const [size, setSize] = useState('');
  const [kind, setKind] = useState('');
  const [product, setProduct] = useState('');
  const [language, setLanguage] = useState('');
  const [geo, setGeo] = useState('');
  // Which of their links a card's code carries, where there is more than one.
  const [chosenLink, setChosenLink] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [showing, setShowing] = useState<string | null>(null);
  const [detail, setDetail] = useState<GalleryItem | null>(null);

  const view = creativeGallery(rows, {
    size, type: kind, product, language, geo, page, perPage: 12,
    assetBase: (import.meta as unknown as { env: Record<string, string | undefined> }).env['VITE_API_BASE'] ?? '',
  });

  if (loading) return <Loading />;

  return (
    <Panel
      title={title}
      aside={<span className="count count-badge">{view.total}</span>}
    >
      {rows.length > 0 ? (
        <FilterBar>
          <Field label={t('creatives.filter.size')}>
            <Select
              value={size}
              onChange={(v) => { setSize(v); setPage(1); }}
              options={[{ value: '', label: t('creatives.filter.all') }, ...view.sizes]}
            />
          </Field>
          <Field label={t('creatives.filter.type')}>
            <Select
              value={kind}
              onChange={(v) => { setKind(v); setPage(1); }}
              options={[{ value: '', label: t('creatives.filter.all') }, ...view.types]}
            />
          </Field>
          <Field label={t('creatives.filter.product')}>
            <Select
              value={product}
              onChange={(v) => { setProduct(v); setPage(1); }}
              options={[{ value: '', label: t('creatives.filter.all') }, ...view.products]}
            />
          </Field>
          <Field label={t('creatives.filter.language')}>
            <Select
              value={language}
              onChange={(v) => { setLanguage(v); setPage(1); }}
              options={[{ value: '', label: t('creatives.filter.all') }, ...view.languages]}
            />
          </Field>
          <Field label={t('creatives.filter.geo')}>
            <Select
              value={geo}
              onChange={(v) => { setGeo(v); setPage(1); }}
              options={[{ value: '', label: t('creatives.filter.all') }, ...view.geos]}
            />
          </Field>
        </FilterBar>
      ) : null}

      {detail ? (
        <Modal title={detail.typeLabel} onClose={() => setDetail(null)}>
          {detail.previewable ? (
            <div className="creative-preview">
              <img src={detail.assetUri} alt={detail.typeLabel} />
            </div>
          ) : null}
          <Facts
            items={[
              { label: t('col.type'), value: detail.typeLabel },
              { label: t('creatives.filter.size'), value: detail.size },
              { label: t('col.language'), value: detail.language },
            ]}
          />
          <Field label={t('col.asset')}>
            <CopyableCode text={detail.assetUri} />
          </Field>
        </Modal>
      ) : null}

      {view.empty ? (
        <Empty>{view.empty}</Empty>
      ) : (
        <>
          <div className="creative-grid">
            {view.items.map((c) => {
              const choices = linksFor(c.campaignId);
              const link = choices.find((x) => x.id === chosenLink[c.id]) ?? choices[0] ?? null;
              const addresses = link === null ? null : trackingAddresses(link.url);
              return (
                <article className="creative-card" key={c.id}>
                  <div className="creative-preview">
                    {c.previewable ? (
                      // Lazily, because a gallery of twelve banners is twelve
                      // images the reader may never scroll to.
                      <img src={c.assetUri} alt={c.typeLabel} loading="lazy" />
                    ) : (
                      <span className="faint">{t('creatives.noPreview')}</span>
                    )}
                  </div>
                  <div className="creative-meta">
                    <Chip tone="neutral">{c.typeLabel}</Chip>
                    <span className="faint">{c.size}</span>
                    <span className="faint">{c.language}</span>
                  </div>
                  <div className="form-actions">
                    <button
                      className="copy"
                      type="button"
                      onClick={() => setShowing(showing === c.id ? null : c.id)}
                    >
                      {t('linkTools.open')}
                    </button>
                    {/* The asset's own details, which the card has no room
                        for: its address, its exact size, and the language it
                        was made in. */}
                    <button className="copy" type="button" onClick={() => setDetail(c)}>
                      {t('creatives.info')}
                    </button>
                  </div>
                  {showing === c.id ? (
                    addresses === null ? (
                      // §10.6 — the asset is published to them, but a snippet
                      // with no link of theirs in it credits nobody. Said
                      // plainly, rather than handed over broken.
                      <Callout tone="caution">{t('creatives.noLink')}</Callout>
                    ) : (
                      <>
                      {choices.length > 1 ? (
                        <Field label={t('creatives.link.choose')}>
                          <Select
                            value={link!.id}
                            onChange={(v) => setChosenLink({ ...chosenLink, [c.id]: v })}
                            options={choices.map((x) => ({ value: x.id, label: x.label }))}
                          />
                        </Field>
                      ) : null}
                      <Field label={t('linkTools.html')}>
                        <CopyableCode
                          text={
                            // §12.13 (0219) — `?cr=` on both halves; every
                            // value escaped, as it goes on the affiliate's
                            // own site (WEB-1, present.ts).
                            creativeEmbedHtml(addresses.click, addresses.impression, c.id, c.assetUri)
                          }
                        />
                      </Field>
                      </>
                    )
                  ) : null}
                </article>
              );
            })}
          </div>

          {view.pages > 1 ? (
            <div className="form-actions">
              <button
                className="copy"
                type="button"
                disabled={view.page <= 1}
                onClick={() => setPage(view.page - 1)}
              >
                {t('creatives.prev')}
              </button>
              <span className="faint">{t('creatives.page', { page: view.page, pages: view.pages })}</span>
              <button
                className="copy"
                type="button"
                disabled={view.page >= view.pages}
                onClick={() => setPage(view.page + 1)}
              >
                {t('creatives.next')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}

/**
 * One link, in the forms somebody actually publishes it in.
 *
 * The list gives the address; this gives the three things an affiliate would
 * otherwise build by hand and get subtly wrong — an anchor with their own
 * wording, a square to print, and the pixel that makes a served banner count
 * as an impression rather than as nothing.
 *
 * Every address is derived from the link's own token (`trackingAddresses`),
 * so nothing here asks the API for something it already holds.
 */
function LinkToolsModal({
  campaign,
  url,
  onClose,
}: {
  campaign: string;
  url: string;
  onClose: () => void;
}) {
  const [anchor, setAnchor] = useState('');
  /*
   * The two identifiers an affiliate's own server-to-server stack wants echoed
   * back to it. We have a full Postbacks tab, which is the richer half of this
   * — register an endpoint per event, test it, remove it — but nothing there
   * let somebody put *their* request and publisher id on the link so it comes
   * back in the postback. These are ordinary link parameters; the edge carries
   * them through the way it carries a sub-ID.
   */
  const [s2sRequestId, setS2sRequestId] = useState('');
  const [s2sPublisherId, setS2sPublisherId] = useState('');
  const base = trackingAddresses(url);
  const addresses = base === null ? null : {
    ...base,
    click: (() => {
      const u = new URL(base.click);
      if (s2sRequestId.trim() !== '') u.searchParams.set('s2s_req_id', s2sRequestId.trim());
      if (s2sPublisherId.trim() !== '') u.searchParams.set('s2s_pub_id', s2sPublisherId.trim());
      return u.toString();
    })(),
  };
  // The affiliate's own wording, or the campaign's name until they type one —
  // never an empty anchor, which renders as an invisible link on their page.
  const text = anchor.trim() === '' ? campaign : anchor.trim();
  // WEB-1 — the campaign name is the operator's text, and the snippet goes on
  // the affiliate's own site: escaped (present.ts).
  const html = addresses ? textLinkHtml(addresses.click, text) : '';

  return (
    <Modal title={t('linkTools.title')} onClose={onClose}>
      {addresses === null ? (
        // Item — a link whose token we cannot name is one whose pixel and QR
        // would be pointed at nothing. Better to say so than to hand over
        // three addresses that quietly do not work.
        <Callout tone="caution">{t('linkTools.unavailable')}</Callout>
      ) : (
        <>
          <Field label={t('linkTools.anchor')} hint={t('linkTools.anchor.hint')}>
            <TextInput value={anchor} onChange={setAnchor} placeholder={campaign} />
          </Field>
          <Field label={t('linkTools.html')}>
            <CopyableCode text={html} />
          </Field>

          <div className="form-row">
            <Field label={t('linkTools.s2sRequest')} hint={t('linkTools.s2s.hint')}>
              <TextInput value={s2sRequestId} onChange={setS2sRequestId} />
            </Field>
            <Field label={t('linkTools.s2sPublisher')}>
              <TextInput value={s2sPublisherId} onChange={setS2sPublisherId} />
            </Field>
          </div>
          {/* The address itself, with whatever was typed above already on it. */}
          <Field label={t('col.link')}>
            <CopyableCode text={addresses.click} />
          </Field>

          <Field label={t('linkTools.qr')} hint={t('linkTools.qr.hint')}>
            <div className="qr-block">
              <img src={addresses.qr} alt={t('linkTools.qr.alt', { campaign })} width={160} height={160} />
              <a className="copy" href={addresses.qr} download={`${campaign}-qr.svg`}>
                {t('linkTools.qr.download')}
              </a>
            </div>
          </Field>

          {/*
            * §12.12a — the pixel counts a serve. It is offered beside the
            * anchor rather than buried, because a banner published without it
            * is a banner whose impressions are never counted, and the
            * affiliate is the only person who can put it on the page.
            */}
          <Field label={t('linkTools.pixel')} hint={t('linkTools.pixel.hint')}>
            <CopyableCode text={impressionPixelHtml(addresses.impression)} />
          </Field>
        </>
      )}
    </Modal>
  );
}

function Links({ api, range, now }: ScreenProps) {
  type LinksSection =
    | 'list' | 'smart' | 'featured' | 'click-report' | 'clicks-impressions' | 'promos'
    | 'creatives' | 'media' | 'groups' | 'ask' | 'sub-accounts';
  const [section, setSection] = useState<LinksSection>('list');
  const [creating, setCreating] = useState(false);
  // Item 2 — the campaign and label travel with the id, so the confirm
  // dialog can name the link rather than asking the reader to trust the row
  // they clicked from.
  const [revoking, setRevoking] = useState<{ link_id: string; campaign: string; label: string } | null>(null);
  // The link whose publishable forms are open, if any.
  const [tooling, setTooling] = useState<{ campaign: string; url: string } | null>(null);

  const [version, setVersion] = useState(0);
  const [viewing, setViewing] = useState<CampaignDetailRow | null>(null);
  // A single box over both fields a link is found by, rather than one search
  // per column — nobody remembers which of "campaign" or "label" holds the
  // word they typed.
  const [linksQuery, setLinksQuery] = useState('');
  // The affiliate's own groups, so each link row can offer them. Declared
  // after `version`, which it re-reads on.
  const [linkGroupOf, setLinkGroupOf] = useState<Record<string, string>>({});
  const groups = useLoad(() => api.linkGroups(), [version]);
  const groupOptions = groups.state === 'ready'
    ? groups.value.items.map((g) => ({ value: g.id, label: g.label }))
    : [];
  /*
   * The operator's own segments. The column only appears when they have
   * defined any — a picker whose only option is "none" is a column of nothing
   * on every row.
   */
  const customerGroups = useLoad(() => api.customerGroups(), []);
  const customerGroupOptions = customerGroups.state === 'ready'
    ? customerGroups.value.items.map((g) => ({ value: g.id, label: g.label }))
    : [];
  const [customerGroupOf, setCustomerGroupOf] = useState<Record<string, string>>({});
  const brandId = range.brand_id ?? '';

  const loaded = useLoad(() => Promise.all([
    api.links(), api.campaigns(), api.promoCodes(), api.brands(),
  ]), [version]);
  const clickReport = useLoad(
    () => api.linkReport(range),
    [range.from, range.to, range.brand_id, version],
  );
  // §6.16 — whether this affiliate is an agency, so the tab only appears
  // for the accounts it means anything for.
  const mySubAccounts = useLoad(() => api.subAccounts(), [version]);
  const isAgency = mySubAccounts.state === 'ready' && mySubAccounts.value.account_kind === 'AGENCY';

  const enrolledBrands = useMemo(() => {
    if (loaded.state !== 'ready') return [];
    return loaded.value[3].items;
  }, [loaded.state, loaded.state === 'ready' ? loaded.value[3].items : null]);

  const brandLabel = brandId
    ? enrolledBrands.find((b) => b.brand_id === brandId)?.brand_name ?? ''
    : '';

  const creativeCampaignIds = useMemo(() => {
    if (loaded.state !== 'ready') return [] as string[];
    const items = loaded.value[1].items;
    if (brandId) return items.filter((c) => c.brand_id === brandId).map((c) => c.campaign_id);
    return items.map((c) => c.campaign_id);
  }, [loaded.state, brandId, loaded.state === 'ready' ? loaded.value[1].items : null]);

  /*
   * The campaign travels with each creative rather than being flattened away.
   * The same asset can be published to more than one campaign, and the code
   * an affiliate is given has to carry the link for the campaign they are
   * actually on — so which one it came from is part of the row, not context
   * that was true at fetch time.
   */
  const creatives = useLoad(
    () => creativeCampaignIds.length === 0
      ? Promise.resolve({ data: [] as {
        id: string; campaign_id: string; type: string; language: string | null;
        width?: number | null; height?: number | null; asset_uri: string; status: string;
        product?: string | null; geos?: readonly string[] | string | null;
      }[] })
      : Promise.all(
          creativeCampaignIds.map((id) =>
            api.creatives(id).then((r) => r.data.map((c) => ({ ...c, campaign_id: id }))),
          ),
        ).then((results) => ({ data: results.flat() })),
    [creativeCampaignIds.join('\0')],
  );

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const [linkList, campaignList, promos] = loaded.value;
  const scopedCampaigns = brandId
    ? campaignList.items.filter((c) => c.brand_id === brandId)
    : campaignList.items;
  const scopedLinkItems = brandId
    ? linkList.items.filter((l) => l.brand_id === brandId)
    : linkList.items;
  const scopedPromos = brandId
    ? promos.data.filter((p) => p.brand_id === brandId)
    : promos.data;
  const view = links(scopedLinkItems);
  const linksSearch = linksQuery.trim().toLowerCase();
  const filteredLinkRows = linksSearch === ''
    ? view.rows
    : view.rows.filter((row) =>
        row.campaign.toLowerCase().includes(linksSearch) ||
        row.labels.some((label) => label.toLowerCase().includes(linksSearch)),
      );

  return (
    <ScreenWithSections
      title={t('links.title')}
      ariaLabel={t('links.sections')}
      section={section}
      onSection={setSection}
      groups={[
        {
          label: t('links.title'),
          items: [
            { id: 'list', label: t('links.section.list') },
            { id: 'smart', label: t('links.section.smart') },
            { id: 'featured', label: t('links.section.featured') },
            { id: 'click-report', label: t('links.section.clickReport') },
            { id: 'clicks-impressions', label: t('clicksImpressions.title') },
            { id: 'promos', label: t('links.section.promos') },
            { id: 'creatives', label: t('links.section.creatives') },
            { id: 'media', label: t('links.section.media') },
            { id: 'groups', label: t('links.section.groups') },
            { id: 'ask', label: t('links.section.ask') },
            // §6.16 — only offered on an agency account.
            ...(isAgency
              ? [{ id: 'sub-accounts' as const, label: t('links.section.subAccounts') }]
              : []),
          ],
        },
      ]}
    >
          {section === 'featured' ? (
            (() => {
              /*
               * Featured is a filter over the list this affiliate can already
               * see, not a different list. The operator promoting a campaign
               * changes where it appears and nothing about who may promote it,
               * so a campaign missing from here was never offered — it is not
               * one being withheld.
               */
              const featured = scopedCampaigns
                .filter((c) => c.featured)
                .sort(
                  (a, b) =>
                    (a.featured_position ?? 0) - (b.featured_position ?? 0) ||
                    a.campaign_name.localeCompare(b.campaign_name),
                );
              return (
                <Panel
                  title={t('featured.panel')}
                  aside={<span className="count count-badge">{featured.length}</span>}
                >
                  {featured.length === 0 ? (
                    <Empty>
                      {t('featured.empty')}
                    </Empty>
                  ) : (
                    <>
                      <TableScroll>
                        <table>
                          <thead>
                            <tr>
                              <th>{t('col.campaign')}</th>
                              <th>{t('col.brand')}</th>
                              <th>{t('campaign.runsIn')}</th>
                              <th>{t('campaign.products')}</th>
                              <th>{t('featured.col.sampling')}</th>
                              <th aria-label={t('campaignDetail.open')} />
                            </tr>
                          </thead>
                          <tbody>
                            {featured.map((c) => (
                              <tr key={c.campaign_id}>
                                <td>{c.campaign_name}</td>
                                <td className="faint">{c.brand_name}</td>
                                <td className="faint">
                                  {textArray(c.jurisdictions).join(', ') || '—'}
                                </td>
                                <td className="faint">
                                  {textArray(c.products).join(', ') || '—'}
                                </td>
                                {/* §39.13 — the rate on the list itself. */}
                                <td>
                                  {/* Item 3 — a sampled-away share is worth a
                                      look, not a still-moving money figure. */}
                                  {samplingChip(c.sampling_rate_bps) ? (
                                    <Chip tone="attention">{samplingChip(c.sampling_rate_bps)}</Chip>
                                  ) : (
                                    <span className="faint">{t('featured.sampling.none')}</span>
                                  )}
                                </td>
                                <td>
                                  <button className="link-button" type="button" onClick={() => setViewing(c)}>
                                    {t('campaignDetail.open')}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableScroll>
                      {/* Item 3 — an explanation of "Featured", not a
                          provisional-figure legend. */}
                      <p className="panel-note">
                        {t('featured.note')}
                      </p>
                    </>
                  )}
                  {viewing ? (
                    <CampaignDetailModal api={api} campaign={viewing} onClose={() => setViewing(null)} />
                  ) : null}
                </Panel>
              );
            })()
          ) : null}

          {section === 'list' ? (
            <>
      <Panel
        title={
          brandId
            ? t('links.panel.brand', { brand: brandLabel })
            : t('links.panel')
        }
        aside={
          <span className="panel-aside">
            <span className="count count-badge">{view.rows.length}</span>
            <button className="primary" type="button" onClick={() => setCreating(true)}>
              {t('link.create')}
            </button>
          </span>
        }
      >
        {view.rows.length === 0 ? (
          <Empty>{t('links.empty')}</Empty>
        ) : (
          <>
            <div className="links-search">
              <TextInput
                value={linksQuery}
                onChange={setLinksQuery}
                placeholder={t('links.search.placeholder')}
                aria-label={t('links.search.placeholder')}
              />
            </div>
            {filteredLinkRows.length === 0 ? (
              <Empty>
                {t('links.search.empty', { query: linksQuery })}{' '}
                <button className="link-button" type="button" onClick={() => setLinksQuery('')}>
                  {t('links.search.clear')}
                </button>
              </Empty>
            ) : (
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <th>{t('col.campaign')}</th>
                      <th>{t('col.label')}</th>
                      <th>{t('col.created')}</th>
                      {/* Which of the affiliate's own groups this link is in.
                          Changed here rather than on a separate screen: the
                          list is where somebody is already looking at links. */}
                      <th>{t('groups.name')}</th>
                      {/* 0224 — which of the operator's segments the people
                          arriving through this link land in. */}
                      {customerGroupOptions.length > 0 ? <th>{t('customerGroup.col')}</th> : null}
                      <th>{t('col.link')}</th>
                      <th className="col-action" aria-label={t('links.revokeAction')} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLinkRows.map((row) => (
                      <Fragment key={row.link_id}>
                        <tr className={row.revoked ? 'row-inactive' : undefined}>
                          <td>
                            <span className="cell-inline">
                              {row.campaign}
                              {row.revoked ? <Chip tone="overdue">{t('links.chip.revoked')}</Chip> : null}
                              {/* Item 2 (batch 2) — not a money figure that can
                                  still move: it is the link's clicks going
                                  uncounted right now, which is exactly the
                                  "no valid clicks" case DESIGN.md §4 names
                                  under `attention`. */}
                              {!row.revoked && row.stoppedNote ? (
                                <Chip tone="attention">{t('links.chip.notOnCampaign')}</Chip>
                              ) : null}
                            </span>
                          </td>
                          <td className="faint">{row.labels.length > 0 ? row.labels.join(', ') : '—'}</td>
                          <td className="faint">{row.created}</td>
                          <td>
                            <Select
                              value={linkGroupOf[row.link_id] ?? ''}
                              onChange={(v) => {
                                setLinkGroupOf({ ...linkGroupOf, [row.link_id]: v });
                                void api.setLinkGroup(row.link_id, v === '' ? null : v);
                              }}
                              options={[
                                { value: '', label: t('groups.none') },
                                ...groupOptions,
                              ]}
                            />
                          </td>
                          {customerGroupOptions.length > 0 ? (
                            <td>
                              <Select
                                value={customerGroupOf[row.link_id] ?? ''}
                                onChange={(v) => {
                                  setCustomerGroupOf({ ...customerGroupOf, [row.link_id]: v });
                                  void api.setLinkCustomerGroup(row.link_id, v === '' ? null : v);
                                }}
                                options={[
                                  { value: '', label: t('customerGroup.none') },
                                  ...customerGroupOptions,
                                ]}
                              />
                            </td>
                          ) : null}
                          <td>
                            {/* §11.1a/§11.6a — the short form only. The long
                                one still exists (revoking needs it, and a
                                link minted before short links were the
                                default may have none), but a second, longer
                                identifier for the same address has no reader
                                who wants it on this screen. */}
                            {row.revoked ? (
                              <code>{row.shortUrl ?? row.url}</code>
                            ) : (
                              <CopyableCode text={row.shortUrl ?? row.url} />
                            )}
                          </td>
                          <td className="col-action">
                            {row.revoked ? null : (
                              <button
                                className="link-button btn-sm"
                                type="button"
                                onClick={() => setTooling({ campaign: row.campaign, url: row.url })}
                              >
                                {t('linkTools.open')}
                              </button>
                            )}
                            {row.revoked ? null : (
                              <button
                                className="link-button btn-sm"
                                type="button"
                                onClick={() => setRevoking({
                                  link_id: row.link_id,
                                  campaign: row.campaign,
                                  label: row.labels.length > 0 ? ` · ${row.labels.join(' · ')}` : '',
                                })}
                              >
                                {t('links.revokeAction')}
                              </button>
                            )}
                          </td>
                        </tr>
                        {row.revokedNote ? (
                          <tr className="row-note">
                            {/* campaign, label, created, group, link, action */}
                            <td colSpan={7}><span className="row-note-text">{row.revokedNote}</span></td>
                          </tr>
                        ) : null}
                        {row.stoppedNote ? (
                          <tr className="row-note">
                            <td colSpan={7}><span className="row-note-text">{row.stoppedNote}</span></td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </>
        )}
      </Panel>
      <Callout>{view.opacity}</Callout>

      {tooling ? (
        <LinkToolsModal
          campaign={tooling.campaign}
          url={tooling.url}
          onClose={() => setTooling(null)}
        />
      ) : null}

      {revoking ? (
        <RevokeLinkModal
          api={api}
          linkId={revoking.link_id}
          campaign={revoking.campaign}
          label={revoking.label}
          onClose={() => setRevoking(null)}
          onDone={() => {
            setRevoking(null);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}

      {creating ? (
        <Modal
          title={
            brandId
              ? t('links.createModal.brand', { brand: brandLabel })
              : t('links.createModal')
          }
          onClose={() => setCreating(false)}
        >
          <CreateLinkForm
            api={api}
            campaigns={scopedCampaigns}
            onCreated={() => {
              setVersion((v) => v + 1);
              setCreating(false);
            }}
          />
        </Modal>
      ) : null}
            </>
          ) : null}

          {section === 'clicks-impressions'
            ? <ClicksImpressionsPanel api={api} range={range} now={now} /> : null}
          {section === 'click-report' ? (
            <>
      {clickReport.state === 'loading' ? <Loading /> : null}
      {clickReport.state === 'error' ? <Failure error={clickReport.error} /> : null}
      {clickReport.state === 'ready' ? (() => {
        const clickView = linkReport(clickReport.value, now);
        return (
          <Panel
            title={t('clickReport.panel')}
            aside={<span className="updated">{clickView.updated}</span>}
          >
            {clickView.rows.length === 0 ? (
              <Empty>{t('clickReport.empty')}</Empty>
            ) : (
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <th>{t('col.campaign')}</th><th>{t('col.created')}</th>
                      <th className="num">{t('col.clicks')}</th><th className="num">{t('col.excluded')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clickView.rows.map((row) => (
                      <Fragment key={row.link_id}>
                      <tr className={row.excludedNote ? 'has-note' : undefined}>
                        <td>{row.campaign}</td>
                        <td className="faint">{row.created}</td>
                        <td className="num">{formatCount(row.counted)}</td>
                        <td className="num faint">{row.excluded || '—'}</td>
                      </tr>
                      {/* Moved off the "Why excluded" cell, which a dedup/repeat/
                          attribution sentence stretched to three-plus lines — see
                          styles.css's `tr.has-note` / `tr.row-note` pair. */}
                      {row.excludedNote ? (
                        <tr className="row-note">
                          {/* campaign, created, clicks, excluded */}
                          <td colSpan={4}><span className="row-note-text">{row.excludedNote}</span></td>
                        </tr>
                      ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </Panel>
        );
      })() : null}
            </>
          ) : null}

          {section === 'promos' ? (
      <Panel
        title={brandId ? t('promos.panel.brand', { brand: brandLabel }) : t('promos.panel')}
        aside={<span className="count count-badge">{scopedPromos.length}</span>}
      >
        {scopedPromos.length === 0 ? (
          <Empty>{t('promos.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr><th>{t('col.code')}</th><th>{t('col.validFrom')}</th><th>{t('col.validTo')}</th></tr>
              </thead>
              <tbody>
                {scopedPromos.map((p) => (
                  <tr key={p.id}>
                    <td><code>{p.code}</code></td>
                    <td>{formatDate(p.valid_from)}</td>
                    <td>{p.valid_to ? formatDate(p.valid_to) : t('promos.openEnded')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
          ) : null}

          {/*
            #46 — one address over several campaigns.

            Beside the link list rather than under it: a smart link is a link,
            and an affiliate deciding how to promote a brand is choosing
            between the two rather than looking for a sub-feature of one.
          */}
          {section === 'smart' ? (
            <SmartLinks api={api} campaigns={scopedCampaigns} />
          ) : null}

          {section === 'media' ? <MediaReport api={api} range={range} now={now} /> : null}

          {section === 'groups' ? <LinkGroups api={api} /> : null}

          {section === 'ask' ? <AskForCampaign api={api} brands={enrolledBrands} /> : null}

          {section === 'creatives' ? (
            <CreativesGallery
              rows={creatives.state === 'ready' ? creatives.value.data : []}
              loading={creatives.state === 'loading'}
              title={brandId ? t('creatives.panel.brand', { brand: brandLabel }) : t('creatives.panel')}
              linksFor={(campaignId) => {
                /*
                 * The affiliate's own live link on that campaign — the one the
                 * pasted code has to carry. A campaign they hold no link on
                 * yet has none, and the card says so rather than handing over
                 * a snippet that credits nobody.
                 *
                 * Matched through the campaign's name because that is what the
                 * links list carries; the creative knows only the id, and the
                 * campaign list is the one place both appear.
                 */
                return creativeLinkChoices(
                  linkList.items,
                  campaignList.items.find((c) => c.campaign_id === campaignId),
                );
              }}
            />
          ) : null}

          {section === 'sub-accounts' ? (
            <SubAccountsSection api={api} range={range} />
          ) : null}
    </ScreenWithSections>
  );
}

/**
 * §6.16 — this agency's own named accounts, and how the last commission
 * range split across them. Only reachable when the account is an agency
 * (the tab above is conditional on it), so this never has to render a
 * "you are not an agency" state of its own.
 */
function SubAccountsSection({ api, range }: { api: AffiliateApi; range: ScreenProps['range'] }) {
  const list = useLoad(() => api.subAccounts(), []);
  const report = useLoad(
    () => api.subAccountReport(range),
    [range.from, range.to, range.brand_id],
  );

  if (list.state === 'loading') return <Loading />;
  if (list.state === 'error') return <Failure error={list.error} />;

  return (
    <>
      <Panel title={t('subAccounts.panel')}>
        {list.value.items.length === 0 ? (
          <Empty>{t('subAccounts.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('subAccounts.col.code')}</th>
                  <th>{t('subAccounts.col.name')}</th>
                  <th>{t('subAccounts.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {list.value.items.map((s) => (
                  <tr key={s.id}>
                    <td>{s.code}</td>
                    <td>{s.name}</td>
                    <td>
                      <Chip tone={s.archived_at ? 'neutral' : 'provisional'}>
                        {s.archived_at ? t('subAccounts.archived') : t('subAccounts.active')}
                      </Chip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>

      <Panel title={t('subAccounts.report.panel')}>
        {report.state === 'loading' ? <Loading /> : null}
        {report.state === 'error' ? <Failure error={report.error} /> : null}
        {report.state === 'ready' ? (() => {
          const rows = subAccountReportRows(report.value.data.groups);
          return rows.length === 0 ? (
            <Empty>{t('subAccounts.report.empty')}</Empty>
          ) : (
            <>
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <th>{t('subAccounts.report.col.subAccount')}</th>
                      <th>{t('subAccounts.report.col.lines')}</th>
                      <th className="num">{t('subAccounts.report.col.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.label ?? t('subAccounts.report.unfiled')}</td>
                        <td>{row.lineCount}</td>
                        <td className="num money">{row.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
              <Callout>{t('subAccounts.report.note')}</Callout>
            </>
          );
        })() : null}
      </Panel>
    </>
  );
}

/**
 * #46 — one address over several campaigns.
 *
 * The screen's whole job is to make two silent things visible.
 *
 * **A campaign in the pool the affiliate is not on.** It is not an error and
 * it is not removed — the click skips it, and the day they are added it starts
 * being used with nothing to regenerate. But a list showing it identically to
 * a working member is one where somebody wonders for a month why a campaign
 * never gets traffic.
 *
 * **Clicks no member would take.** They still reached the brand's page, so
 * nothing looks broken anywhere: the click count is up and the conversions are
 * not. The number is on the row, because widening the pool or asking to be
 * added to a campaign is the affiliate's own move and they cannot make it from
 * a figure they never see.
 */
function SmartLinks({
  api,
  campaigns,
}: {
  api: AffiliateApi;
  campaigns: readonly { campaign_id: string; campaign_name: string; brand_name: string }[];
}) {
  const [version, setVersion] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SmartLinkRow | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const loaded = useLoad(() => api.smartLinks(), [version]);

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  return (
    <>
      <Panel
        title={t('smart.panel')}
        aside={<span className="count count-badge">{loaded.value.items.length}</span>}
      >
        {problem ? <Callout tone="error">{problem}</Callout> : null}
        <Callout>{t('smart.intro')}</Callout>

        <div className="form-actions">
          <button className="primary" onClick={() => setCreating(true)}>
            {t('smart.new')}
          </button>
        </div>

        {loaded.value.items.length === 0 ? (
          <Empty>{t('smart.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('smart.col.name')}</th>
                  <th>{t('smart.col.pool')}</th>
                  <th>{t('smart.col.chooses')}</th>
                  <th className="num">{t('smart.col.clicks')}</th>
                  <th className="num">{t('smart.col.unmatched')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loaded.value.items.map((row: SmartLinkRow) => {
                  const view = smartLinkView(row);
                  return (
                    <tr key={view.id}>
                      <td>
                        {view.name}
                        {/* Item 3 — "Off" is the tone table's own example of
                            what `overdue` is never for; a paused smart link
                            is not a failure. */}
                        {view.off ? <Chip tone="neutral">{t('smart.disabled')}</Chip> : null}
                        {view.url ? (
                          <div className="faint small mono wrap">{view.url}</div>
                        ) : null}
                        {view.warning ? (
                          <div className="faint small">{view.warning}</div>
                        ) : null}
                      </td>
                      <td>
                        <ul className="plain">
                          {view.pool.map((p, i) => (
                            <li key={i}>
                              {p.name}
                              {p.reachable === false ? (
                                <span className="faint small"> — {t('smart.notOn')}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="faint">{view.chooses}</td>
                      <td className="num">{view.clicks}</td>
                      <td className="num">{view.unmatched}</td>
                      <td>
                        {view.url ? <CopyButton text={view.url} /> : null}
                        {view.off ? null : (
                          <>
                            {/*
                              Editing is the feature, not a convenience: the
                              address does not change when the pool does, so
                              changing what is behind it is the entire reason
                              to have one of these rather than four links.
                            */}
                            <button className="copy" onClick={() => setEditing(row)}>
                              {t('smart.edit')}
                            </button>
                            <button
                              className="copy"
                              onClick={() => {
                                api
                                  .revokeSmartLink(view.id)
                                  .then(() => setVersion((v) => v + 1))
                                  .catch((e: unknown) => setProblem(explainFailure(e).message));
                              }}
                            >
                              {t('smart.disable')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>

      {creating || editing ? (
        <SmartLinkForm
          api={api}
          campaigns={campaigns}
          existing={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Building one, or changing one.
 *
 * The same form for both, because they are the same decision — and because
 * changing a pool is the point rather than an afterthought: the address
 * survives it, so what is behind the link is meant to move.
 *
 * The order of the list is the order the click tries them in, which is why it
 * is explicit rather than hidden: an affiliate moves a campaign up because
 * they want that traffic there first, and a picker that concealed the ordering
 * would make the one thing they are choosing invisible.
 */
function SmartLinkForm({
  api,
  campaigns,
  existing,
  onClose,
  onSaved,
}: {
  api: AffiliateApi;
  campaigns: readonly { campaign_id: string; campaign_name: string; brand_name: string }[];
  existing: SmartLinkRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [chosen, setChosen] = useState<string[]>(
    // Categories are kept but not editable here: this picker offers the
    // campaigns this affiliate can see, and a category is the operator's own
    // grouping. Dropping them silently on save would empty half a pool.
    (existing?.members ?? [])
      .filter((m) => m.kind === 'campaign' && m.campaign_id !== null)
      .map((m) => m.campaign_id!),
  );
  const [spread, setSpread] = useState(existing?.selection === 'SPREAD');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const toggle = (id: string) =>
    setChosen((list) => (list.includes(id) ? list.filter((c) => c !== id) : [...list, id]));

  return (
    <Modal title={existing ? t('smart.edit') : t('smart.new')} onClose={onClose}>
      <div className="form">
        {problem ? <Callout tone="error">{problem}</Callout> : null}

        <Field label={t('smart.name.label')}>
          <TextInput value={name} onChange={setName} />
        </Field>

        <Field group label={t('smart.pool.label')}>
          <div className="stack">
            {/* Item 12 — `.check` had no rule (like `.mono`/`.small` before
                it), so the box sat wherever the browser's own baseline
                alignment put it; `.checkbox-stack-row` is the kit's row for
                a checkbox with its label. */}
            {campaigns.map((c) => (
              <label key={c.campaign_id} className="checkbox-stack-row">
                <input
                  type="checkbox"
                  checked={chosen.includes(c.campaign_id)}
                  onChange={() => toggle(c.campaign_id)}
                />
                <span>{c.campaign_name}</span>
                <span className="faint small"> — {c.brand_name}</span>
              </label>
            ))}
          </div>
        </Field>

        <Field label={t('smart.selection.label')}>
          <Select
            value={spread ? 'SPREAD' : 'PRIORITY'}
            onChange={(v) => setSpread(v === 'SPREAD')}
            options={[
              { value: 'PRIORITY', label: t('smart.chooses.priority') },
              { value: 'SPREAD', label: t('smart.chooses.spread') },
            ]}
          />
        </Field>

        <div className="form-actions">
          {/* Item 12 — a class-less button rendered at the browser's own
              21px; `.copy` is the kit's plain-button style. */}
          <button className="copy" onClick={onClose}>{t('smart.cancel')}</button>
          <button
            className="primary"
            disabled={busy || name.trim() === '' || chosen.length === 0}
            onClick={() => {
              setBusy(true);
              setProblem(null);
              // The checkbox order is the affiliate's order: the click tries
              // them in exactly this sequence. Categories already in the pool
              // are carried through unchanged — this picker does not offer
              // them, and dropping them on save would empty half a pool.
              const members = [
                ...chosen.map((id) => ({ campaign_id: id })),
                ...(existing?.members ?? [])
                  .filter((m) => m.kind === 'category' && m.category_id !== null)
                  .map((m) => ({ category_id: m.category_id! })),
              ];
              const saved = existing
                ? api.updateSmartLink(existing.id, {
                    name: name.trim(),
                    selection: spread ? 'SPREAD' : 'PRIORITY',
                    members,
                  })
                : api.createSmartLink({
                    name: name.trim(),
                    selection: spread ? 'SPREAD' : 'PRIORITY',
                    members,
                  });
              saved
                .then(onSaved)
                .catch((e: unknown) => setProblem(explainFailure(e).message))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? t('language.saving') : existing ? t('smart.save') : t('smart.create')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ *
 * Terms — §7.8, §24.2b, §26.2a
 * ------------------------------------------------------------------ */

/**
 * The terms behind a proposal: behind a disclosure where they are a detail
 * (the right rail, where the summary line and Accept sit side by side), and
 * simply shown where they are the point of the page (Terms). A `<details>`
 * left open still says "Show full terms" over terms already on screen, so the
 * open case is a plain block rather than an opened toggle.
 */
function TermsDisclosure({
  open,
  summary,
  children,
}: {
  open: boolean;
  summary: string;
  children: ReactNode;
}) {
  return open ? (
    <div className="terms-details">{children}</div>
  ) : (
    <details className="terms-details">
      <summary>{summary}</summary>
      {children}
    </details>
  );
}

function PendingDealProposalsPanel({
  api,
  proposals,
  onResponded,
  termsOpen,
}: {
  api: AffiliateApi;
  /**
   * DESIGN.md's rail decision (terms collapse behind `<details>`) is right at
   * 320px, but wrong on the Terms screen itself: that page exists to show the
   * terms, so collapsing the one thing it is for defeats the page. Unset (the
   * rail's call) keeps it collapsed; the Terms screen passes `true`.
   */
  termsOpen?: boolean;
  proposals: readonly {
    deal_id: string;
    brand: string;
    deal_name?: string | null;
    version: number;
    proposed_at: string;
    valid_from: string;
    summary: string;
    revenue_basis: string;
    revshare_rate_bps: number | null;
    cpa_amount_minor: string | null;
    cpa_currency: string | null;
    /**
     * §24.7b — the proposed version's CPA price list. The API has sent it
     * (`pendingDealProposals`, terms.ts) since the price list shipped; this
     * panel rebuilt its `termsLine` input field by field and never read it,
     * so a per-country CPA was invisible until after acceptance. Optional
     * so an older response reads as "no rows".
     */
    cpa_price_rows?: readonly { jurisdiction: string; channel: string; amount_minor: string }[];
    /**
     * QA round 2 F1 — the proposed version's tier tables, product rates and
     * component windows (`pendingDealProposals`, terms.ts, sends them all).
     * Optional so an older response reads as "none stated".
     */
    cpa_tier_bands?: readonly { from: number; to: number | null; amount_minor: string; currency: string }[];
    revshare_tier_bands?: readonly { from: number; to: number | null; rate_bps: number }[];
    revshare_product_tiers?: readonly { product: string; tiers: readonly { from: number; to: number | null; rate_bps: number }[] }[];
    revshare_tier_key_metric?: string | null;
    product_tier_qftd_note?: string | null;
    product_rates?: readonly { product: string; revshare_rate_bps: number | null; cpa_amount_minor: string | null; cpa_currency: string | null }[];
    cpa_window?: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean };
    revshare_window?: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean };
    cpa_criteria: { criterion: string; threshold_minor: string | null; threshold_count: number | null; label: string | null; standard?: boolean; applies?: boolean }[];
    scopes: { dimension: string; value: string }[];
    /**
     * `label` is the platform's own word, null while nobody has set one
     * (0173). `kind`/`rate_bps`/`amount_minor`/`currency` are §20.4a's
     * widened shape (task A4).
     */
    deduction_stack: {
      order: number;
      what: string;
      label: string | null;
      kind: 'AMOUNT' | 'RATE' | null;
      rate_bps: number | null;
      amount_minor: string | null;
      currency: string | null;
    }[];
    bonus_payout_enabled: boolean;
    bonus_payout_is_percentage: boolean;
    bonus_payout_basis: string | null;
    bonus_payout_rate_bps: number | null;
    bonus_payout_amount_minor: string | null;
    bonus_payout_currency: string | null;
    bonus_payout_window_days: number | null;
    /** §7.12 — set when the offer is for one campaign alone; null for the default deal. */
    campaign_name: string | null;
    cpa_trigger: string;
    coverage: readonly { component: string; mode: string; jurisdiction: string }[];
    /**
     * §7.11 — an affiliate must be able to read what they are being asked to
     * accept, not only what they already accepted. Same fields, same
     * meaning as the Terms screen's own terms rows below.
     */
    commission_type: string;
    flat_fee_minor: string | null;
    flat_fee_currency: string | null;
    cpc_amount_minor: string | null;
    cpc_currency: string | null;
    cpc_period_cap: number | null;
    /**
     * §26.11/§26.11a and §26.12/§8.10 — the API (`pendingDealProposals`,
     * terms.ts) has sent these since Wave 8/7; this panel rebuilds its own
     * `termsLine` input from `proposal` field by field rather than reading
     * the response through, and had not been widened to read them (review
     * finding, task A9: an affiliate could not see a proposed CPM or
     * spend-share deal's own rate and cap before accepting it, only its
     * one-line summary).
     */
    cpm_amount_minor: string | null;
    cpm_currency: string | null;
    cpm_period_cap: number | null;
    spend_share_rate_bps: number | null;
    spend_period_cap_minor: string | null;
    spend_cap_currency: string | null;
    payout: { frequency: string; settlement_terms_days: number; source: string };
    negative_balance: { policy: string; carry_bps: number; source: string };
    cookie_lifetime_days: number;
    attribution_window_days: number;
    minimum_payout: { amount_minor: string; currency: string; source: string };
    traffic_rules: readonly { restriction: string; value: string; mode: string }[];
  }[];
  onResponded: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  return (
    <Panel title={t('proposals.panel')} aside={<span className="count count-badge">{proposals.length}</span>}>
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      {proposals.map((proposal) => {
        const line = termsLine({
          brand: proposal.brand,
          deal_name: proposal.deal_name ?? null,
          summary: proposal.summary,
          since: proposal.valid_from,
          accepted_by_side: null,
          accepted_at: null,
          revenue_basis: proposal.revenue_basis,
          revshare_rate_bps: proposal.revshare_rate_bps,
          cpa_amount_minor: proposal.cpa_amount_minor,
          cpa_currency: proposal.cpa_currency,
          cpa_price_rows: proposal.cpa_price_rows ?? [],
          // `exactOptionalPropertyTypes` — passed only when the response carries them.
          ...(proposal.cpa_tier_bands ? { cpa_tier_bands: proposal.cpa_tier_bands } : {}),
          ...(proposal.revshare_tier_bands ? { revshare_tier_bands: proposal.revshare_tier_bands } : {}),
          ...(proposal.revshare_product_tiers ? { revshare_product_tiers: proposal.revshare_product_tiers } : {}),
          ...(proposal.revshare_tier_key_metric !== undefined ? { revshare_tier_key_metric: proposal.revshare_tier_key_metric } : {}),
          ...(proposal.product_tier_qftd_note !== undefined ? { product_tier_qftd_note: proposal.product_tier_qftd_note } : {}),
          ...(proposal.product_rates ? { product_rates: proposal.product_rates } : {}),
          ...(proposal.cpa_window ? { cpa_window: proposal.cpa_window } : {}),
          ...(proposal.revshare_window ? { revshare_window: proposal.revshare_window } : {}),
          cpa_minimum_deposit_minor: null,
          cpa_qualification_deadline_days: null,
          cpa_hold_period_days: null,
          revshare_amount_per_unit_minor: null,
          revshare_amount_per_unit_currency: null,
          cpa_criteria: proposal.cpa_criteria,
          scopes: proposal.scopes,
          tier_mode: null,
          deduction_stack: proposal.deduction_stack,
          bonus_payout_enabled: proposal.bonus_payout_enabled,
          bonus_payout_is_percentage: proposal.bonus_payout_is_percentage,
          bonus_payout_basis: proposal.bonus_payout_basis,
          bonus_payout_rate_bps: proposal.bonus_payout_rate_bps,
          bonus_payout_amount_minor: proposal.bonus_payout_amount_minor,
          bonus_payout_currency: proposal.bonus_payout_currency,
          bonus_payout_window_days: proposal.bonus_payout_window_days,
          campaign_name: proposal.campaign_name,
          cpa_trigger: proposal.cpa_trigger,
          coverage: proposal.coverage,
          commission_type: proposal.commission_type,
          flat_fee_minor: proposal.flat_fee_minor,
          flat_fee_currency: proposal.flat_fee_currency,
          cpc_amount_minor: proposal.cpc_amount_minor,
          cpc_currency: proposal.cpc_currency,
          cpc_period_cap: proposal.cpc_period_cap,
          cpm_amount_minor: proposal.cpm_amount_minor,
          cpm_currency: proposal.cpm_currency,
          cpm_period_cap: proposal.cpm_period_cap,
          spend_share_rate_bps: proposal.spend_share_rate_bps,
          spend_period_cap_minor: proposal.spend_period_cap_minor,
          spend_cap_currency: proposal.spend_cap_currency,
          payout: proposal.payout,
          negative_balance: proposal.negative_balance,
          cookie_lifetime_days: proposal.cookie_lifetime_days,
          attribution_window_days: proposal.attribution_window_days,
          minimum_payout: proposal.minimum_payout,
          traffic_rules: proposal.traffic_rules,
        });
        const commercialRows = commercialTermsRows(line);
        const trafficRows = trafficRulesRows(line.trafficRules);
        const key = `${proposal.deal_id}:v${proposal.version}`;
        return (
          <article key={key} className="offer-card">
            <h3 className="offer-title">{line.title}</h3>
            {/* §7.12 — an offer for one campaign pays only there; the default
                deal pays everywhere else. Two cards headed with the same brand
                differ by exactly this. */}
            <p className="offer-brand"><Chip tone="neutral">{line.scope}</Chip></p>
            <p className="terms-summary">{line.summary}</p>
            <p className="offer-note">
              {t('proposals.when', { proposed: formatDate(proposal.proposed_at), from: formatDate(proposal.valid_from) })}
            </p>

            {/* The full criteria and deduction stack are what makes this
                decision a considered one, not a reflex — but at the rail's
                320px they ran the card to 400px+ on its own. A native
                <details> keeps Accept/Reject beside the summary line, where
                the decision is actually made, and puts the reasoning one
                press away rather than off the bottom of the rail. No JS: the
                disclosure state, and that there is one, come from the
                element itself. */}
            {line.conditions.length > 0 || line.deductions.length > 0 || commercialRows.length > 0 || trafficRows.length > 0 ? (
              <TermsDisclosure open={termsOpen === true} summary={t('proposals.showTerms')}>
                {line.conditions.length > 0 ? (
                  <TableScroll>
                    <table>
                      <tbody>
                        {line.conditions.map((c) => (
                          <tr key={c.label}>
                            {/* No fixed label column. The same table on the Terms
                                screen holds one at 16rem because it has a page to
                                spread across; this card lives in the right rail,
                                where 16rem is the whole width and the value it is
                                supposed to be labelling ends up off the edge. */}
                            <th className="wrap">{c.label}</th>
                            <td className="wrap">{c.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                ) : null}

                {line.deductions.length > 0 ? (
                  <div className="stack">
                    <span className="figure-label">{t('terms.deductions')}</span>
                    <ol className="deductions">
                      {line.deductions.map((d) => <li key={d}>{d.replace(/^\d+\.\s*/, '')}</li>)}
                    </ol>
                  </div>
                ) : null}

                {/* §7.8/§7.11 — the same commercial-terms disclosure the Terms
                    screen shows once accepted, read here before acceptance:
                    payout, negative balance, the formula, the admin fee, the
                    two tracking windows and the minimum payout. */}
                {commercialRows.length > 0 ? (
                  <TableScroll>
                    <table>
                      <tbody>
                        {commercialRows.map((r) => (
                          <tr key={r.label}>
                            <th className="wrap">{r.label}</th>
                            <td className="wrap">{r.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                ) : null}

                {/* §24.7b — what a qualifying player pays by country, and the
                    base CPA everywhere a row does not name. Read before
                    acceptance, not discovered on the first statement. */}
                {line.cpaPriceList ? (
                  <div className="stack">
                    <span className="figure-label">{t('terms.cpaPriceList')}</span>
                    <TableScroll>
                      <table>
                        <tbody>
                          {cpaPriceListRows(line.cpaPriceList).map((r) => (
                            <tr key={r.label}>
                              <th className="wrap">{r.label}</th>
                              <td className="wrap">{r.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableScroll>
                  </div>
                ) : null}

                {/* QA round 2 F1 — tier bands, per-product tables, product
                    rates and component windows, read before acceptance. */}
                <TermsTableSections line={line} />

                {/* §7.14/§7.8f — the traffic rules this offer states. */}
                {trafficRows.length > 0 ? (
                  <div className="stack">
                    <span className="figure-label">{t('terms.trafficRules' as AffiliateMessageKey)}</span>
                    <TableScroll>
                      <table>
                        <tbody>
                          {trafficRows.map((r) => (
                            <tr key={r.label}>
                              <th className="wrap">{r.label}</th>
                              <td className="wrap">{r.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableScroll>
                  </div>
                ) : null}
              </TermsDisclosure>
            ) : null}

            <div className="offer-actions">
              <button
                className="primary"
                disabled={busy === key}
                onClick={() => {
                  setBusy(key);
                  setFailure(null);
                  void api
                    .respondDealProposal(proposal.deal_id, proposal.version, 'ACCEPT')
                    .then(onResponded)
                    .catch((e: unknown) => setFailure(e))
                    .finally(() => setBusy(null));
                }}
              >
                {busy === key ? t('proposals.accepting') : t('proposals.accept')}
              </button>
              <button
                className="copy"
                disabled={busy === key}
                onClick={() => {
                  setBusy(key);
                  setFailure(null);
                  void api
                    .respondDealProposal(proposal.deal_id, proposal.version, 'REJECT')
                    .then(onResponded)
                    .catch((e: unknown) => setFailure(e))
                    .finally(() => setBusy(null));
                }}
              >
                {t('proposals.reject')}
              </button>
            </div>
          </article>
        );
      })}
    </Panel>
  );
}

function DealHistoryPanel({ api, dealId }: { api: AffiliateApi; dealId: string }) {
  const [history, setHistory] = useState<
    Awaited<ReturnType<AffiliateApi['dealHistory']>>['data'] | null
  >(null);
  const [busy, setBusy] = useState(false);

  if (history === null) {
    return (
      <button
        className="copy"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void api.dealHistory(dealId).then((r) => setHistory(r.data)).finally(() => setBusy(false));
        }}
      >
        {busy ? t('dealHistory.loading') : t('dealHistory.view')}
      </button>
    );
  }

  if (history.length <= 1) return <p className="link-note">{t('dealHistory.onlyVersion')}</p>;

  return (
    <Sorted rows={history} by={{ version: (v) => sortableCell(v.version), commission_type: (v) => sortableCell(v.commission_type), valid_from: (v) => sortableCell(v.valid_from), accepted_at: (v) => sortableCell(v.accepted_at) }}>
      {(ordered, sort) => (
    <TableScroll>
      <table>
        <thead>
          <tr><SortTh sort={sort} col="version">v</SortTh><SortTh sort={sort} col="commission_type">{t('dealHistory.col.terms')}</SortTh><SortTh sort={sort} col="valid_from">{t('dealHistory.col.inForce')}</SortTh><SortTh sort={sort} col="accepted_at">{t('dealHistory.col.accepted')}</SortTh></tr>
        </thead>
        <tbody>
          {ordered.map((v) => (
            <tr key={v.version}>
              <td>{v.version}</td>
              <td>
                {/*
                    The commission type is an identifier (§2.8b) and stays one;
                    the sentence around it is not, so `terms.revshare.value` is
                    the same phrasing the Terms screen uses for the same figure.
                */}
                {v.commission_type === 'CPA'
                  ? `CPA ${v.cpa_amount_minor ?? '?'} ${v.cpa_currency ?? ''}`
                  : `${v.commission_type} ` +
                    t('terms.revshare.value', {
                      rate: ((v.revshare_rate_bps ?? 0) / 100).toFixed(2),
                      basis: v.revenue_basis,
                    })}
              </td>
              <td>
                {formatDate(v.valid_from)}
                {v.valid_to ? ` – ${formatDate(v.valid_to)}` : ' –'}
              </td>
              <td>{v.accepted_at ? formatDate(v.accepted_at) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
      )}
    </Sorted>
  );
}

function Terms({ api, range, dealProposalsVersion, onDealProposalsChanged }: ScreenProps) {
  const [version, setVersion] = useState(0);
  const loaded = useLoad(
    () =>
      Promise.all([
        api.terms(range.brand_id),
        api.recoupment(range.brand_id),
        api.dealProposals(),
      ]).then(([t, r, p]) => ({
        terms: t,
        recoupment: r,
        proposals: p,
      })),
    [version, dealProposalsVersion, range.brand_id],
  );
  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const view = termsScreen(loaded.value.terms.items, loaded.value.terms.agency, loaded.value.terms.campaigns);
  const balances = recoupmentRows(loaded.value.recoupment.items);
  const proposals = loaded.value.proposals.items;

  return (
    <>
      <div className="page-head"><h1>{t('terms.title')}</h1></div>

      {/* The question this screen exists to answer, answered before the data. */}
      <Callout>{view.whereRulesLive}</Callout>
      <Callout>{view.whereNumbersComeFrom}</Callout>
      {/* §6.16/§6.16a — one account fact, said once, not per deal below. */}
      {view.agencyNote ? <Callout>{view.agencyNote}</Callout> : null}

      {proposals.length > 0 ? (
        <PendingDealProposalsPanel
          api={api}
          proposals={proposals}
          // This page is where an affiliate reads the terms before accepting
          // them; collapsed-by-default is the rail's trade-off for 320px, not
          // this one's.
          termsOpen
          onResponded={() => {
            setVersion((v) => v + 1);
            // The rail lists the same proposals; an answer here is an answer there.
            onDealProposalsChanged();
          }}
        />
      ) : null}

      {view.groups.length === 0 ? (
        <Empty>
          {t('terms.empty')}
        </Empty>
      ) : (
        // §7.12d — brand by brand: which terms pay on each campaign the
        // affiliate takes part in, then the terms themselves, each once.
        view.groups.map((group) => (
          <Fragment key={group.brand}>
            {group.campaigns.length > 0 ? (
              <Panel
                title={t('terms.campaigns.title' as AffiliateMessageKey, { brand: group.brand })}
                count={group.campaigns.length}
              >
                <TableScroll>
                  <table>
                    <thead>
                      <tr>
                        <th scope="col" style={{ width: '16rem' }}>{t('col.campaign')}</th>
                        <th scope="col">{t('terms.campaigns.paidOn' as AffiliateMessageKey)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.campaigns.map((c) => (
                        <tr key={c.campaign}>
                          <td className="wrap">
                            {c.campaign}
                            {c.leftNote ? <><br /><span className="link-note">{c.leftNote}</span></> : null}
                          </td>
                          <td className="wrap">
                            {c.priced ? c.paidOn : <Chip tone="attention">{c.paidOn}</Chip>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              </Panel>
            ) : null}
            {group.lines.map((line) => (
          <Panel
            // The scope is in the key because a brand can now hold a default
            // deal and a campaign's own terms that started the same day.
            key={`${line.brand}:${line.scope}:${line.since}`}
            title={line.title}
            aside={<span className="count count-badge">{line.accepted}</span>}
          >
            <p className="offer-brand"><Chip tone="neutral">{line.scope}</Chip></p>
            <p className="terms-summary">{line.summary}</p>
            {line.sourceNote ? <p className="link-note">{line.sourceNote}</p> : null}
            {line.conditions.length > 0 ? (
              <TableScroll>
                <table>
                  <tbody>
                    {line.conditions.map((c) => (
                      <tr key={c.label}>
                        {/* §24.2b — "qualifying player" without the criteria is
                            a term nobody can check their reporting against. */}
                        <th style={{ width: '16rem' }}>{c.label}</th>
                        <td className="wrap">{c.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            ) : null}

            {line.deductions.length > 0 ? (
              <div className="stack">
                <span className="figure-label">{t('terms.deductions')}</span>
                {/* §20.4a — the order is part of the deal. GGR − fee% − bonus
                    and GGR − bonus − fee% are different numbers. */}
                <ol className="deductions">
                  {line.deductions.map((d) => <li key={d}>{d.replace(/^\d+\.\s*/, '')}</li>)}
                </ol>
              </div>
            ) : null}

            {/* §7.8 — payout, negative balance, the composed formula, the
                admin fee, the two tracking windows and the minimum payout;
                already words a reader sees, resolved by `screens.ts`. */}
            <TermsRowsTable rows={commercialTermsRows(line)} />

            {/* §24.7b — the CPA price list this version pays by, with the
                base CPA as everywhere else. */}
            {line.cpaPriceList ? (
              <div className="stack">
                <span className="figure-label">{t('terms.cpaPriceList')}</span>
                <TermsRowsTable rows={cpaPriceListRows(line.cpaPriceList)} />
              </div>
            ) : null}

            {/* QA round 2 F1 — the tables that actually pay a tiered or
                per-product deal, and each component's own window. */}
            <TermsTableSections line={line} />

            {/* §7.14/§7.8f — every traffic rule this version states, the
                three headline axes always shown even where nothing is
                stated (§7.14c). */}
            <div className="stack">
              <span className="figure-label">{t('terms.trafficRules' as AffiliateMessageKey)}</span>
              <TermsRowsTable rows={trafficRulesRows(line.trafficRules)} />
            </div>

            <p className="link-note">{t('terms.since', { date: line.since })}</p>
            {/* A campaign's payout has no history with this affiliate to
                open; its changes reach them as notices. */}
            {line.source === 'PERSONAL' && line.dealId ? <DealHistoryPanel api={api} dealId={line.dealId} /> : null}
          </Panel>
            ))}
          </Fragment>
        ))
      )}

      {/* §26.2a — CPA-recoupable outstanding per player, or withheld months
          look like the operator simply did not pay. */}
      <Panel title={t('recoupment.panel')}>
        {balances.length === 0 ? (
          <Empty>{t('recoupment.empty')}</Empty>
        ) : (
          <Sorted rows={balances} by={{ player: (b) => sortableCell(b.player), brand: (b) => sortableCell(b.brand), cpaPaid: (b) => sortableCell(b.cpaPaid), recouped: (b) => sortableCell(b.recouped), outstanding: (b) => sortableCell(b.outstanding) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="player">{t('col.player')}</SortTh>
                  <SortTh sort={sort} col="brand">{t('col.brand')}</SortTh>
                  <SortTh sort={sort} col="cpaPaid">{t('col.cpaPaid')}</SortTh>
                  <SortTh sort={sort} col="recouped">{t('col.recouped')}</SortTh>
                  <SortTh sort={sort} col="outstanding">{t('col.outstanding')}</SortTh>
                </tr>
              </thead>
              <tbody>
                {ordered.map((b) => (
                  <tr key={`${b.brand}:${b.player}`}>
                    <td>{b.player}</td>
                    <td>{b.brand}</td>
                    <td>{b.cpaPaid}</td>
                    <td>{b.recouped}</td>
                    <td>{b.outstanding}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
        )}
      </Panel>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Click discrepancy — §32.9
 *
 * Typing must not refetch on every keystroke (that looked like a refresh
 * with no useful change). Compare is explicit; the note explains the gap.
 * ------------------------------------------------------------------ */

/**
 * Support — the question an affiliate had nowhere to ask.
 *
 * Disputes is next door and is not this. A dispute is a claim about money with
 * a category, an amount and an outcome; "my link 404s" and "do you have this
 * banner in 300x600" are neither, and until now they went to whatever email
 * address the affiliate could find, if they could find one.
 *
 * The thread is the ticket's audit trail, which is why nothing here can edit
 * what was said earlier — by either side.
 */
function Support({ api }: { api: AffiliateApi }) {
  const [showResolved, setShowResolved] = useState(false);
  const [version, setVersion] = useState(0);
  const [opening, setOpening] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);

  const tickets = useLoad(() => api.supportTickets(showResolved), [showResolved, version]);

  if (tickets.state === 'loading') return <Loading />;
  if (tickets.state === 'error') return <Failure error={tickets.error} />;

  return (
    <>
      <div className="page-head">
        <h1>{t('support.title')}</h1>
      </div>

      {viewing ? (
        <SupportTicketModal
          api={api}
          ticketId={viewing}
          onClose={() => setViewing(null)}
          onReplied={() => setVersion((n) => n + 1)}
        />
      ) : null}

      {opening ? (
        <NewTicketModal
          api={api}
          onClose={() => setOpening(false)}
          onRaised={() => {
            setOpening(false);
            setShowResolved(false);
            setVersion((n) => n + 1);
          }}
        />
      ) : null}

      <Panel
        title={showResolved ? t('support.panel.resolved') : t('support.panel.open')}
        aside={
          <span className="panel-aside">
            <span className="count count-badge">{tickets.value.data.length}</span>
            <button className="copy" type="button" onClick={() => setShowResolved(!showResolved)}>
              {showResolved ? t('support.showOpen') : t('support.showResolved')}
            </button>
            <button className="primary" type="button" onClick={() => setOpening(true)}>
              {t('support.new')}
            </button>
          </span>
        }
      >
        {/* Said once, at the top: the two queues exist for different things,
            and somebody filing a money claim here waits behind questions. */}
        <Callout>{t('support.notDisputes')}</Callout>

        {tickets.value.data.length === 0 ? (
          <Empty>{showResolved ? t('support.empty.resolved') : t('support.empty.open')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('col.when')}</th>
                  <th>{t('support.col.subject')}</th>
                  <th>{t('support.col.priority')}</th>
                  <th>{t('col.status')}</th>
                  <th className="col-action" aria-label={t('support.open')} />
                </tr>
              </thead>
              <tbody>
                {tickets.value.data.map((x) => (
                  <tr key={x.id}>
                    <td className="num faint">{formatDate(x.created_at)}</td>
                    <td className="wrap">{x.subject}</td>
                    <td className="faint">{t(`support.priority.${x.priority}` as AffiliateMessageKey)}</td>
                    <td>
                      <Chip tone={x.status === 'ANSWERED' ? 'provisional' : 'neutral'}>
                        {t(`support.status.${x.status}` as AffiliateMessageKey)}
                      </Chip>
                    </td>
                    <td className="col-action">
                      <button className="link-button btn-sm" type="button" onClick={() => setViewing(x.id)}>
                        {t('support.open')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
    </>
  );
}

/** Opening one. Subject, description, and how urgent the affiliate thinks it is. */
function NewTicketModal({
  api,
  onClose,
  onRaised,
}: {
  api: AffiliateApi;
  onClose: () => void;
  onRaised: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const ready = subject.trim() !== '' && body.trim() !== '';

  return (
    <Modal title={t('support.new')} onClose={onClose}>
      {failure ? <Failure error={failure} /> : null}
      <Field label={t('support.col.subject')} hint={t('support.subject.hint')}>
        <TextInput value={subject} onChange={setSubject} placeholder={t('support.subject.placeholder')} />
      </Field>
      <Field label={t('support.body')} hint={t('support.body.hint')}>
        <textarea className="control" value={body} onChange={(e) => setBody(e.target.value)} rows={6} />
      </Field>
      <Field label={t('support.col.priority')} hint={t('support.priority.hint')}>
        <Select
          value={priority}
          onChange={setPriority}
          options={['TRIVIAL', 'LOW', 'NORMAL', 'HIGH', 'CRITICAL'].map((p) => ({
            value: p,
            label: t(`support.priority.${p}` as AffiliateMessageKey),
          }))}
        />
      </Field>
      <div className="form-actions">
        <button
          className="primary"
          type="button"
          disabled={!ready || busy}
          onClick={() => {
            setBusy(true);
            setFailure(null);
            api
              .raiseTicket({ subject: subject.trim(), body: body.trim(), priority })
              .then(onRaised)
              .catch((e: unknown) => setFailure(e))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? kitText('action.saving') : t('support.send')}
        </button>
        <button className="copy" type="button" onClick={onClose}>{t('smart.cancel')}</button>
      </div>
    </Modal>
  );
}

/** One ticket and its thread, with a reply box while it is still open. */
function SupportTicketModal({
  api,
  ticketId,
  onClose,
  onReplied,
}: {
  api: AffiliateApi;
  ticketId: string;
  onClose: () => void;
  onReplied: () => void;
}) {
  const [version, setVersion] = useState(0);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const loaded = useLoad(() => api.supportTicket(ticketId), [ticketId, version]);

  if (loaded.state === 'loading') return <Modal title={t('support.open')} onClose={onClose}><Loading /></Modal>;
  if (loaded.state === 'error') {
    return <Modal title={t('support.open')} onClose={onClose}><Failure error={loaded.error} /></Modal>;
  }

  const ticket = loaded.value.data;
  const closed = ticket.status === 'RESOLVED' || ticket.status === 'CLOSED';

  return (
    <Modal title={ticket.subject} onClose={onClose} wide>
      {failure ? <Failure error={failure} /> : null}
      <Facts
        items={[
          { label: t('col.status'), value: t(`support.status.${ticket.status}` as AffiliateMessageKey) },
          { label: t('support.col.priority'), value: t(`support.priority.${ticket.priority}` as AffiliateMessageKey) },
          { label: t('col.when'), value: formatDateTime(ticket.created_at) },
        ]}
      />

      <h3 className="panel-subhead">{t('support.thread')}</h3>
      <TableScroll>
        <table>
          <tbody>
            {ticket.thread.map((m, i) => (
              <tr key={`${m.at}-${i}`}>
                <td className="num faint">{formatDateTime(m.at)}</td>
                <td className="faint">
                  {m.by_role === 'AFFILIATE' ? t('disputes.by.you') : t('disputes.by.operator')}
                </td>
                <td className="wrap">{m.reason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>

      {ticket.resolution ? (
        <Callout tone="note" fold={false}>{ticket.resolution}</Callout>
      ) : null}

      {closed ? (
        // Nothing to type into. A settled thread stays settled; a new question
        // is a new ticket, which is what the API says too.
        <p className="panel-note">{t('support.closed')}</p>
      ) : (
        <>
          <Field label={t('support.reply')}>
            <textarea className="control" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} />
          </Field>
          <div className="form-actions">
            <button
              className="primary"
              type="button"
              disabled={message.trim() === '' || busy}
              onClick={() => {
                setBusy(true);
                setFailure(null);
                api
                  .replyToTicket(ticketId, message.trim())
                  .then(() => {
                    setMessage('');
                    setVersion((n) => n + 1);
                    onReplied();
                  })
                  .catch((e: unknown) => setFailure(e))
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? kitText('action.saving') : t('support.send')}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

/**
 * What has been earned in the open period, in the rail, on every page.
 *
 * It fails silent and renders nothing while loading, on an error, or when
 * there is nothing yet: chrome that shows an error on every screen is chrome
 * that has to be read past on every screen, and a permanent `0.00` for an
 * affiliate between periods is worse than no line at all.
 */
function RunningCommission({ api, range }: { api: AffiliateApi; range: ScreenProps['range'] }) {
  const loaded = useLoad(() => api.currentEarnings(range), [range.from, range.to, range.brand_id]);
  if (loaded.state !== 'ready') return null;

  const figures = runningCommission(loaded.value.data);
  if (figures.length === 0) return null;

  return (
    <div className="rail-running">
      <span className="rail-running-label">{t('rail.thisPeriod')}</span>
      {figures.map((f) => (
        <span className="rail-running-value" key={f}>{f}</span>
      ))}
    </div>
  );
}

/**
 * §2.3a/§6.7/§3.7 — the profile, edited by the person it describes.
 *
 * Two halves, and the screen says which is which rather than leaving somebody
 * to find out by pressing Save. Contact details apply at once; a registered
 * name or country is a request the operator decides, because those are what
 * they know the affiliate as and what jurisdiction and tax read.
 *
 * The email is a third thing again: §3.7 wants a second factor before the
 * address we reach somebody on can change, and an account without one is told
 * so here rather than refused after typing.
 */
function EditProfile({
  api,
  me,
  onClose,
  onSaved,
}: {
  api: AffiliateApi;
  me: Awaited<ReturnType<AffiliateApi['me']>>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const c = me.contact;
  const [phone, setPhone] = useState(c.phone ?? '');
  const [company, setCompany] = useState(c.company_name ?? '');
  const [telegram, setTelegram] = useState(c.telegram ?? '');
  const [teams, setTeams] = useState(c.teams ?? '');
  const [websites, setWebsites] = useState(c.website_urls ?? '');
  const [marketing, setMarketing] = useState(c.marketing_note ?? '');
  const [newsletter, setNewsletter] = useState(c.newsletter_opt_in);
  const [legalName, setLegalName] = useState(me.display_name);
  const [country, setCountry] = useState(me.country);
  const [email, setEmail] = useState(me.email);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const identityChanged = legalName !== me.display_name || country !== me.country;
  const emailChanged = email !== me.email;

  return (
    <Panel title={t('account.edit')}>
      {failure ? <Failure error={failure} /> : null}

      <h3 className="panel-subhead">{t('account.contact')}</h3>
      <p className="link-note">{t('account.contact.lead')}</p>
      <div className="form-row">
        <Field label={t('account.phone')}><TextInput value={phone} onChange={setPhone} /></Field>
        <Field label={t('account.company')}><TextInput value={company} onChange={setCompany} /></Field>
      </div>
      <div className="form-row">
        <Field label={t('account.telegram')}><TextInput value={telegram} onChange={setTelegram} /></Field>
        <Field label={t('account.teams')}><TextInput value={teams} onChange={setTeams} /></Field>
      </div>
      <Field label={t('account.websites')} hint={t('account.websites.hint')}>
        <TextInput value={websites} onChange={setWebsites} />
      </Field>
      <Field label={t('account.marketing')} hint={t('account.marketing.hint')}>
        <textarea
          className="control"
          value={marketing}
          onChange={(e) => setMarketing(e.target.value)}
          rows={3}
        />
      </Field>
      <label className="export-option">
        <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.currentTarget.checked)} />
        {t('account.newsletter')}
      </label>

      <h3 className="panel-subhead">{t('account.registered')}</h3>
      {/* Said before the fields, not after a save: somebody typing their new
          married name should know it is going to somebody for a decision. */}
      <Callout>{t('account.registered.lead')}</Callout>
      <div className="form-row">
        <Field label={t('col.name')}><TextInput value={legalName} onChange={setLegalName} /></Field>
        <Field label={t('col.country')}><TextInput value={country} onChange={setCountry} /></Field>
      </div>

      <h3 className="panel-subhead">{kitText('field.email')}</h3>
      {me.mfa_enrolled ? null : (
        // §3.7 — told here rather than refused after typing.
        <Callout tone="caution">{t('account.email.needsMfa')}</Callout>
      )}
      <Field label={kitText('field.email')} hint={t('account.email.hint')}>
        <TextInput value={email} onChange={setEmail} invalid={emailChanged && !me.mfa_enrolled} />
      </Field>

      <div className="form-actions">
        <button
          className="primary"
          type="button"
          disabled={busy || (emailChanged && !me.mfa_enrolled)}
          onClick={() => {
            setBusy(true);
            setFailure(null);
            api
              .editProfile({
                phone, companyName: company, telegram, teams,
                websiteUrls: websites, marketingNote: marketing,
                newsletterOptIn: newsletter,
                ...(identityChanged ? { legalName, country } : {}),
                ...(emailChanged ? { email } : {}),
              })
              .then(onSaved)
              .catch((e: unknown) => setFailure(e))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? kitText('action.saving') : t('payout.save')}
        </button>
        <button className="copy" type="button" onClick={onClose}>{t('smart.cancel')}</button>
      </div>
    </Panel>
  );
}

/**
 * What is in this console, and what is switched off for this account.
 *
 * A new affiliate arrives at a rail of seventeen words and no way to tell
 * which of them holds the thing they are looking for. Worse, some capabilities
 * are off until an operator turns them on — and a feature that is invisible
 * until you already know it exists is a feature nobody asks for.
 *
 * The list is built from `TABS`, the same array the rail renders, so a tab
 * added later appears here without anybody remembering to add it. A tab with
 * no sentence written for it yet is listed by its own name rather than
 * skipped: a gap in the guide should look like a gap, not like an absence.
 */
function WhatsHere({ api }: { api: AffiliateApi }) {
  // The one capability an operator switches per account today. Failing to load
  // it is not worth an error on an orientation page — it simply is not claimed
  // either way.
  const apiAccess = useLoad(() => api.apiClients(), []);
  const apiOff = apiAccess.state === 'ready' && !apiAccess.value.enabled;

  return (
    <>
      <Panel title={t('dash.guide.panel')}>
        <p className="link-note">{t('dash.guide.lead')}</p>
        <TableScroll>
          <table>
            <tbody>
              {TABS.filter((tab) => tab.id !== 'dashboard').map((tab) => {
                const key = `guide.${tab.id}` as AffiliateMessageKey;
                const line = t(key);
                return (
                  <tr key={tab.id}>
                    <th style={{ width: '12rem' }}>{t(tab.labelKey)}</th>
                    {/* `t()` answers with the key when nothing is written for
                        it; showing the tab's own name is the honest fallback. */}
                    <td className="wrap">{line === key ? t(tab.labelKey) : line}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      </Panel>

      {/*
        * Said out loud, on the first screen, rather than left to be discovered
        * by somebody who happened to open Account. An operator can switch this
        * on; until they do, the page that offers it explains why it is empty.
        */}
      <Panel title={t('dash.guide.off.panel')}>
        {apiOff ? (
          <Callout tone="caution">{t('dash.guide.off.api')}</Callout>
        ) : (
          <Empty>{t('dash.guide.off.none')}</Empty>
        )}
      </Panel>
    </>
  );
}

/**
 * What the operator has announced.
 *
 * There is no read state stored — one notice is one row for every affiliate,
 * which is the whole point — so "new" here means published since the sign-in
 * before this one. That is the question somebody actually has when they open
 * the page, and `sessions` already answers it.
 */
/**
 * L10 — what the operator wrote for this affiliate to read. Staff could mark
 * a note "affiliate can see" and nothing ever showed it to the affiliate.
 */
function OperatorNotesPanel({ api }: { api: AffiliateApi }) {
  const loaded = useLoad(() => api.myNotes(), []);
  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;
  const items = loaded.value.data;
  return (
    <Panel title={t('dash.notes.panel')}>
      {items.length === 0 ? (
        <Empty>{t('dash.notes.empty')}</Empty>
      ) : (
        items.map((n) => (
          <article className="news-item" key={n.id}>
            <p className="faint">
              {formatDate(n.created_at)}{n.author_name ? ` · ${n.author_name}` : ''}
            </p>
            <p className="wrap">{n.body}</p>
          </article>
        ))
      )}
    </Panel>
  );
}

function NewsPanel({ api }: { api: AffiliateApi }) {
  const loaded = useLoad(() => api.news(), []);
  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const items = loaded.value.data;
  return (
    <Panel
      title={t('dash.news.panel')}
      aside={
        items.some((n) => n.is_new)
          ? <Chip tone="attention">{t('dash.news.new', { n: items.filter((x) => x.is_new).length })}</Chip>
          : null
      }
    >
      {items.length === 0 ? (
        <Empty>{t('dash.news.empty')}</Empty>
      ) : (
        items.map((n) => (
          <article className="news-item" key={n.id}>
            <h3 className="panel-subhead">
              <span className="cell-inline">
                {n.title}
                {n.is_new ? <Chip tone="attention">{t('dash.news.badge')}</Chip> : null}
                {n.brand ? <Chip tone="neutral">{n.brand}</Chip> : null}
              </span>
            </h3>
            <p className="faint">{formatDate(n.published_at)}</p>
            <p className="wrap">{n.body}</p>
          </article>
        ))
      )}
    </Panel>
  );
}

/**
 * Credentials for reading your own reports from somewhere else.
 *
 * Off until the operator turns it on for this account, and the screen says so
 * rather than offering a button that refuses: nothing throttles an API caller
 * yet (§41.4 is deferred), so exposing the interface is a decision somebody
 * makes per affiliate rather than a default.
 *
 * The secret appears exactly once, here, at creation. Nothing stored could
 * show it again, and the screen says that before it is dismissed.
 */
function ApiClients({ api }: { api: AffiliateApi }) {
  const [version, setVersion] = useState(0);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [issued, setIssued] = useState<{ client_id: string; client_secret: string } | null>(null);
  const loaded = useLoad(() => api.apiClients(), [version]);

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  return (
    <>
      <Panel title={t('account.section.api')}>
        {failure ? <Failure error={failure} /> : null}
        <p className="link-note">{loaded.value.note}</p>

        {!loaded.value.enabled ? (
          <Callout tone="caution">{t('api.off')}</Callout>
        ) : (
          <>
          {issued ? (
            // Once. There is nothing stored that could show it again.
            <Callout tone="caution" fold={false}>
              <strong>{t('api.copyNow')}</strong>
              <Facts
                items={[
                  { label: t('api.clientId'), value: issued.client_id },
                  { label: t('api.secret'), value: issued.client_secret },
                ]}
              />
            </Callout>
          ) : null}

          <Field label={t('api.label')} hint={t('api.label.hint')}>
            <TextInput value={label} onChange={setLabel} placeholder={t('api.label.placeholder')} />
          </Field>
          <div className="form-actions">
            <button
              className="primary"
              type="button"
              disabled={busy || label.trim() === ''}
              onClick={() => {
                setBusy(true);
                setFailure(null);
                api
                  .createApiClient(label.trim())
                  .then((r) => {
                    setIssued({ client_id: r.client_id, client_secret: r.client_secret });
                    setLabel('');
                    setVersion((n) => n + 1);
                  })
                  .catch((e: unknown) => setFailure(e))
                  .finally(() => setBusy(false));
              }}
            >
              {t('api.create')}
            </button>
          </div>
          </>
        )}

        {loaded.value.items.length === 0 ? (
          <Empty>{t('api.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
            <thead>
              <tr>
                <th>{t('api.label')}</th>
                <th>{t('api.clientId')}</th>
                <th>{t('api.lastUsed')}</th>
                <th className="col-action" aria-label={t('api.revoke')} />
              </tr>
            </thead>
            <tbody>
              {loaded.value.items.map((c) => (
                <tr key={c.id}>
                  <td>{c.label}</td>
                  <td className="faint"><code>{c.client_id}</code></td>
                  <td className="faint">{c.last_used_at ? formatDateTime(c.last_used_at) : '—'}</td>
                  <td className="col-action">
                    <button
                      className="link-button btn-sm"
                      type="button"
                      onClick={() =>
                        api.revokeApiClient(c.id).then(() => setVersion((n) => n + 1))
                      }
                    >
                      {t('api.revoke')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
      {loaded.value.enabled ? <ApiUsageGuide baseUrl={api.apiBaseUrl} /> : null}
    </>
  );
}

function ApiUsageGuide({ baseUrl }: { baseUrl: string }) {
  const tokenRequest =
    `curl --request POST '${baseUrl}/v1/oauth/token' \\\n` +
    `  --header 'Content-Type: application/json' \\\n` +
    `  --data '{"client_id":"YOUR_CLIENT_ID","client_secret":"YOUR_CLIENT_SECRET"}'`;
  const reportRequest =
    `curl '${baseUrl}/v1/reports/performance?from=2026-09-01&to=2026-09-30&dimension=campaign' \\\n` +
    `  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`;

  const endpoint = (path: string, purpose: AffiliateMessageKey) => (
    <tr key={path}>
      <td className="wrap"><code>GET {path}</code></td>
      <td className="wrap">{t(purpose)}</td>
    </tr>
  );

  return (
    <Panel title={t('api.guide.title')}>
      <p>{t('api.guide.intro')}</p>
      <Facts items={[{ label: t('api.guide.base'), value: baseUrl }]} />

      <h3 className="panel-subhead">{t('api.guide.step1')}</h3>
      <p className="link-note">{t('api.guide.step1.body')}</p>
      <CopyableCode text={tokenRequest} multiline />

      <h3 className="panel-subhead">{t('api.guide.step2')}</h3>
      <p className="link-note">{t('api.guide.step2.body')}</p>
      <CopyableCode text={reportRequest} multiline />

      <h3 className="panel-subhead">{t('api.guide.endpoints')}</h3>
      <p className="link-note">{t('api.guide.endpoints.body')}</p>
      <TableScroll>
        <table>
          <thead>
            <tr><th>{t('api.guide.endpoint')}</th><th>{t('api.guide.purpose')}</th></tr>
          </thead>
          <tbody>
            <tr><th colSpan={2}>{t('api.guide.account')}</th></tr>
            {endpoint('/v1/me', 'api.guide.me')}
            {endpoint('/v1/me/terms', 'api.guide.terms')}
            {endpoint('/v1/campaigns', 'api.guide.campaigns')}
            {endpoint('/v1/links', 'api.guide.links')}

            <tr><th colSpan={2}>{t('api.guide.reporting')}</th></tr>
            {endpoint('/v1/reports/performance?from={date}&to={date}&dimension=campaign', 'api.guide.performance')}
            {endpoint('/v1/reports/funnel?from={date}&to={date}', 'api.guide.funnel')}
            {endpoint('/v1/reports/commission?from={date}&to={date}', 'api.guide.commission')}
            {endpoint('/v1/reports/players?from={date}&to={date}', 'api.guide.players')}
            {endpoint('/v1/reports/cohorts?from={date}&to={date}', 'api.guide.cohorts')}
            {endpoint('/v1/reports/qualification?from={date}&to={date}', 'api.guide.qualification')}
            {endpoint('/v1/reports/clicks?from={date}&to={date}', 'api.guide.clicks')}
            {endpoint('/v1/reports/discrepancy?from={date}&to={date}', 'api.guide.discrepancy')}

            <tr><th colSpan={2}>{t('api.guide.money')}</th></tr>
            {endpoint('/v1/statements', 'api.guide.statements')}
            {endpoint('/v1/statements/{period}', 'api.guide.statement')}
            {endpoint('/v1/invoices', 'api.guide.invoices')}
            {endpoint('/v1/payments', 'api.guide.payments')}
            {endpoint('/v1/commission-lines/{id}/trace', 'api.guide.trace')}
          </tbody>
        </table>
      </TableScroll>
      <Callout>{t('api.guide.parameters')}</Callout>
      <Callout tone="caution">{t('api.guide.security')}</Callout>
    </Panel>
  );
}

/**
 * Every email the operator has sent, newest first.
 *
 * Nothing new was stored for this: the platform has recorded each one since
 * the schema's fiftieth migration and had never shown any of them to the
 * person they were sent to. An affiliate who has lost a message, or who is not
 * sure one was ever sent, could not check.
 *
 * "New" is the same reckoning the news panel makes — sent since the sign-in
 * before this one — because a notification carries no read state and adding
 * one would be a row per affiliate per message.
 */
function MailArchive({ api }: { api: AffiliateApi }) {
  const loaded = useLoad(() => api.mailArchive(), []);
  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  return (
    <Panel title={t('account.section.mail')}>
      <p className="link-note">{loaded.value.note}</p>
      {loaded.value.data.length === 0 ? (
        <Empty>{t('mail.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th>{t('col.when')}</th>
                <th>{t('mail.subject')}</th>
                <th>{t('col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {loaded.value.data.map((m) => (
                <tr key={m.id}>
                  <td className="num faint">{formatDateTime(m.sent_at)}</td>
                  <td className="wrap">
                    <span className="cell-inline">
                      {mailSubject(m)}
                      {m.is_new ? <Chip tone="attention">{t('dash.news.badge')}</Chip> : null}
                    </span>
                  </td>
                  <td>
                    {/* Bounced and failed are the rows worth finding: an
                        affiliate who never got something should be able to see
                        that we know. */}
                    <Chip tone={m.status === 'BOUNCED' || m.status === 'FAILED' ? 'overdue' : 'neutral'}>
                      {t(`mail.status.${m.status}` as AffiliateMessageKey)}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

/**
 * §3.7a — the sign-ins on this account.
 *
 * `sessions` has recorded the time and the address of every one since the
 * schema's fourth migration, and nothing had ever shown them to the person
 * they belong to. An account takeover is what §3.7's second factor exists to
 * prevent; this is how its owner would notice one that already happened.
 *
 * It fails quietly. A security panel whose password and authenticator controls
 * are replaced by an error because a supporting list would not load is a panel
 * that stopped somebody securing their account — the list is the least
 * important thing on it.
 */
function LoginHistory({ api }: { api: AffiliateApi }) {
  const loaded = useLoad(() => api.loginHistory(), []);
  if (loaded.state !== 'ready' || loaded.value.data.length === 0) return null;

  return (
    <>
      <h3 className="panel-subhead">{t('account.logins')}</h3>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th>{t('col.when')}</th>
              <th>{t('account.logins.from')}</th>
            </tr>
          </thead>
          <tbody>
            {loaded.value.data.map((l, i) => (
              <tr key={`${l.at}-${i}`}>
                <td className="num faint">{formatDateTime(l.at)}</td>
                {/* An address the request arrived without is ordinary — a
                    proxy, a private network — and reads as a dash. */}
                <td className="faint">{l.ip ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      <p className="panel-note">{loaded.value.note}</p>
    </>
  );
}

/**
 * §31.1a — what is owed, what was last sent, and whether we know where to send it.
 *
 * Invoices have their own screen; this is the three-line answer to "when do I
 * get paid", on the page somebody is already looking at. The third line is the
 * one that matters most: an affiliate with a balance and no saved payment
 * method has nowhere for it to go, and nothing on the dashboard said so.
 */
function PaymentSummaryPanel({ api }: { api: AffiliateApi }) {
  const loaded = useLoad(() => Promise.all([api.payments(), api.paymentPreference()]), []);

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const [invoices, preference] = loaded.value;
  const view = paymentSummary(invoices.data, preference.method);

  return (
    <Panel title={t('dash.paid.panel')}>
      {/* The one thing here the affiliate can act on today, so it leads. */}
      {view.needsMethod ? <Callout tone="caution">{view.needsMethod}</Callout> : null}

      <div className="figures">
        {view.awaiting.length === 0 ? (
          <Empty>{t('dash.paid.none')}</Empty>
        ) : (
          // One per currency: an affiliate paid in two has two balances, and
          // one added-up figure would be true in neither.
          view.awaiting.map((a) => (
            <Figure
              key={a.currency}
              view={{ label: `${t('dash.paid.awaiting')} — ${a.currency}`, value: a.amount, provisional: false }}
            />
          ))
        )}
        {view.lastPayment ? (
          <Figure view={{ label: t('dash.paid.last'), value: view.lastPayment.amount, provisional: false }} />
        ) : null}
      </div>

      {/* §31.1a — "paid" is the operator's claim. The sentence travels with
          the figure it qualifies, never on its own. */}
      {view.lastPayment ? <p className="panel-note">{view.lastPayment.when}</p> : null}
      {view.caveat ? <p className="figures-note">{view.caveat}</p> : null}
    </Panel>
  );
}

/**
 * §39.6 — the clicks themselves, newest first, on the page somebody opens first.
 *
 * The log already existed under Links, which is where somebody goes when they
 * already suspect a problem. This is the same rows on the overview, because the
 * question it answers — "is anything arriving right now?" — is the one an
 * affiliate opens the console to ask, and a daily count cannot answer it until
 * tomorrow.
 *
 * The referring URL is the column that makes it worth reading: it is how an
 * affiliate tells their own placements apart when the traffic is not labelled
 * with a sub-ID.
 */
function RecentVisitsPanel({ api, range }: { api: AffiliateApi; range: ScreenProps['range'] }) {
  const log = useLoad(
    // A short page deliberately: this is a glance at what is arriving, not the
    // full log, which keeps its own screen under Links with every filter on it.
    () => api.clickLog({ ...range, limit: '25' }),
    [range.from, range.to, range.brand_id],
  );

  if (log.state === 'loading') return <Loading />;
  if (log.state === 'error') return <Failure error={log.error} />;

  // The envelope carries the page, and the page carries the rows.
  const rows = log.value.data.data;
  return (
    <Panel
      title={t('dash.visits.panel')}
      aside={<span className="count count-badge">{rows.length}</span>}
    >
      {rows.length === 0 ? (
        <Empty>{t('dash.visits.empty')}</Empty>
      ) : (
        <Sorted rows={rows} by={{ occurred_at: (c) => sortableCell(c.occurred_at), campaign: (c) => sortableCell(c.campaign), referrer: (c) => sortableCell(c.referrer), ip_country: (c) => sortableCell(c.ip_country), device: (c) => sortableCell(c.device) }}>
          {(ordered, sort) => (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <SortTh sort={sort} col="occurred_at">{t('col.when')}</SortTh>
                <SortTh sort={sort} col="campaign">{t('col.campaign')}</SortTh>
                <SortTh sort={sort} col="referrer">{t('dim.referrer')}</SortTh>
                <SortTh sort={sort} col="ip_country">{t('col.country')}</SortTh>
                <SortTh sort={sort} col="device">{t('dim.device')}</SortTh>
              </tr>
            </thead>
            <tbody>
              {ordered.map((c, i) => (
                <tr key={`${c.occurred_at}-${i}`}>
                  <td className="num faint">{formatDateTime(c.occurred_at)}</td>
                  <td>{c.campaign}</td>
                  {/*
                    * A click the browser sent no referrer for is ordinary —
                    * a typed address, an app, a stripped `rel=noreferrer` —
                    * so it reads as a dash rather than as something missing.
                    */}
                  <td className="wrap faint">{c.referrer ?? '—'}</td>
                  {/* Flag and name, the same as every other country on the
                      platform — this column printed the bare code. */}
                  <td>{countryLabel(c.ip_country)}</td>
                  <td className="faint">{c.device ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
          )}
        </Sorted>
      )}
      {rows.length > 0 ? <IpGeolocationNote /> : null}
    </Panel>
  );
}

function ClickDiscrepancyPanel({
  api,
  range,
  campaigns,
}: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  /** Item 9 — this affiliate's own joined campaigns, for the filter options. */
  campaigns: readonly {
    campaign_version_id: string;
    campaign_name: string;
    brand_id: string;
    brand_name: string;
    products: readonly string[];
  }[];
}) {
  const [draft, setDraft] = useState('');
  const [reported, setReported] = useState<number | undefined>(undefined);
  const [campaignId, setCampaignId] = useState('');
  const [product, setProduct] = useState('');

  const products = useMemo(
    () => [...new Set(campaigns.flatMap((c) => textArray(c.products)))].sort(),
    [campaigns],
  );

  const discrepancy = useLoad(
    () =>
      api.discrepancy({
        ...range,
        ...(reported != null ? { reported_clicks: reported } : {}),
        ...(campaignId ? { campaign_id: campaignId } : {}),
        ...(product ? { product } : {}),
      }),
    [range.from, range.to, range.brand_id, reported, campaignId, product],
  );

  /*
   * What the reader typed has to be read whole or refused out loud.
   * `Number.parseInt` did neither: "1,240" came back as 1 and "12abc" as 12,
   * so an affiliate comparing their tracker's 1,240 clicks was shown a gap
   * against 1 — and that gap is the figure they would have quoted raising a
   * dispute. `parseWholeNumber` knows the reader's own digit grouping (a `.`
   * groups in Spanish) and answers null rather than a partial read.
   */
  const [draftProblem, setDraftProblem] = useState(false);
  const applyCompare = () => {
    const trimmed = draft.trim();
    setDraftProblem(false);
    if (!trimmed) {
      // Clearing the box is how the comparison is taken off again.
      setReported(undefined);
      return;
    }
    const n = parseWholeNumber(trimmed);
    if (n === null) {
      setDraftProblem(true);
      setReported(undefined);
      return;
    }
    setReported(n);
  };

  // Item 10 — raised with the row's own filter context, so the brand does
  // not have to re-derive which campaign/product/brand this was about from
  // a free-text description alone.
  const [raising, setRaising] = useState(false);
  const [raiseText, setRaiseText] = useState('');
  const [raiseBusy, setRaiseBusy] = useState(false);
  const [raiseFailure, setRaiseFailure] = useState<unknown>(null);
  const [raisedId, setRaisedId] = useState<string | null>(null);
  const [raiseBrand, setRaiseBrand] = useState('');
  const brands = useLoad(() => api.brands(), []);
  const selectedCampaign = campaigns.find((c) => c.campaign_version_id === campaignId);
  // Every dispute names its brand. The brand filter or the chosen campaign
  // usually already says which; the affiliate is asked only when neither does.
  const impliedBrand = range.brand_id || selectedCampaign?.brand_id || '';
  const raiseBrandId = impliedBrand || raiseBrand;
  // A campaign chosen before the brand filter moved to another brand is no
  // longer one of the options, and must not stay chosen out of sight.
  useEffect(() => {
    if (range.brand_id && selectedCampaign && selectedCampaign.brand_id !== range.brand_id) setCampaignId('');
  }, [range.brand_id, selectedCampaign]);

  return (
    <Panel title={t('discrepancy.panel')}>
      {campaigns.length > 0 ? (
        <FilterBar>
          <Field label={t('col.campaign')}>
            <Select
              value={campaignId}
              onChange={setCampaignId}
              options={[
                { value: '', label: t('discrepancy.allCampaigns') },
                // Within the brand filter: a campaign of another brand would
                // make the report read zero and a dispute from it name two brands.
                ...campaigns
                  .filter((c) => !range.brand_id || c.brand_id === range.brand_id)
                  .map((c) => ({
                    value: c.campaign_version_id,
                    label: `${c.brand_name} · ${c.campaign_name}`,
                  })),
              ]}
            />
          </Field>
          {products.length > 0 ? (
            <Field label={t('discrepancy.product')}>
              <Select
                value={product}
                onChange={setProduct}
                options={[
                  { value: '', label: t('discrepancy.allProducts') },
                  ...products.map((p) => ({ value: p, label: p })),
                ]}
              />
            </Field>
          ) : null}
        </FilterBar>
      ) : null}
      <Field
        label={t('discrepancy.yourTotal')}
        hint={t('discrepancy.yourTotal.hint')}
        {...(draftProblem ? { error: t('discrepancy.yourTotal.notANumber') } : {})}
      >
        <div className="form-actions">
          <TextInput
            value={draft}
            onChange={(v) => { setDraft(v); setDraftProblem(false); }}
            placeholder={t('discrepancy.yourTotal.placeholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyCompare();
              }
            }}
          />
          <button className="primary" type="button" onClick={applyCompare}>
            {t('discrepancy.compare')}
          </button>
        </div>
      </Field>

      {discrepancy.state === 'loading' ? <Loading rows={2} /> : null}
      {discrepancy.state === 'error' ? <Failure error={discrepancy.error} /> : null}

      {discrepancy.state === 'ready' ? (
        <>
          <div className="figures">
            {/* Item 13 — the hand-built `<article className="figure">` cards
                had no currency handling to miss here (these are counts, not
                money), but they also had no hero and no provisional rule if
                either were ever needed; the kit component is the one place
                that decides that, so every figure card in the portal is the
                same component. None of these counts are provisional — a
                discrepancy report is a comparison run on demand, not a
                figure that settles later. */}
            <Figure view={{ label: t('discrepancy.oursValid'), value: formatCount(discrepancy.value.ours_valid), provisional: false }} />
            {discrepancy.value.affiliate_tracker_total != null ? (
              <>
                <Figure view={{ label: t('discrepancy.yourTracker'), value: formatCount(discrepancy.value.affiliate_tracker_total), provisional: false }} />
                <Figure
                  view={{
                    label: t('discrepancy.gap'),
                    value: `${(discrepancy.value.gap_vs_valid ?? 0) > 0 ? '+' : ''}${formatCount(discrepancy.value.gap_vs_valid ?? 0)}`,
                    provisional: false,
                  }}
                />
                <Figure view={{ label: t('discrepancy.explained'), value: formatCount(discrepancy.value.explained_rejects ?? 0), provisional: false }} />
                <Figure view={{ label: t('discrepancy.unexplained'), value: formatCount(discrepancy.value.unexplained_gap ?? 0), provisional: false }} />
              </>
            ) : null}
            <Figure view={{ label: t('discrepancy.bot'), value: formatCount(discrepancy.value.bot), provisional: false }} />
            <Figure view={{ label: t('discrepancy.duplicate'), value: formatCount(discrepancy.value.duplicate), provisional: false }} />
            <Figure view={{ label: t('discrepancy.geoBlocked'), value: formatCount(discrepancy.value.geo_blocked), provisional: false }} />
            <Figure view={{ label: t('discrepancy.osBlocked'), value: formatCount(discrepancy.value.os_blocked), provisional: false }} />
            <Figure view={{ label: t('discrepancy.deviceBlocked'), value: formatCount(discrepancy.value.device_blocked), provisional: false }} />
            <Figure view={{ label: t('discrepancy.targetingBlocked'), value: formatCount(discrepancy.value.targeting_blocked), provisional: false }} />
          </div>
          {!discrepancy.value.by_sub_id || discrepancy.value.by_sub_id.length === 0 ? (
            <Empty>{t('discrepancy.noSubIds')}</Empty>
          ) : (
            <Sorted rows={discrepancy.value.by_sub_id} by={{ sub_id: (row) => sortableCell(row.sub_id), ours_valid: (row) => sortableCell(row.ours_valid), bot: (row) => sortableCell(row.bot), duplicate: (row) => sortableCell(row.duplicate), geo_blocked: (row) => sortableCell(row.geo_blocked), os_blocked: (row) => sortableCell(row.os_blocked), device_blocked: (row) => sortableCell(row.device_blocked), targeting_blocked: (row) => sortableCell(row.targeting_blocked) }}>
              {(ordered, sort) => (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <SortTh sort={sort} col="sub_id">{t('col.subId')}</SortTh>
                    <SortTh sort={sort} col="ours_valid" align="num">{t('col.valid')}</SortTh>
                    <SortTh sort={sort} col="bot" align="num">{t('col.bot')}</SortTh>
                    <SortTh sort={sort} col="duplicate" align="num">{t('col.duplicate')}</SortTh>
                    <SortTh sort={sort} col="geo_blocked" align="num">{t('col.geo')}</SortTh>
                    <SortTh sort={sort} col="os_blocked" align="num">OS</SortTh>
                    <SortTh sort={sort} col="device_blocked" align="num">{t('col.device')}</SortTh>
                    <SortTh sort={sort} col="targeting_blocked" align="num">{t('col.targeting')}</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((row) => (
                    <tr key={row.sub_id}>
                      <td>{row.sub_id}</td>
                      <td className="num">{formatCount(row.ours_valid)}</td>
                      <td className="num">{formatCount(row.bot)}</td>
                      <td className="num">{formatCount(row.duplicate)}</td>
                      <td className="num">{formatCount(row.geo_blocked)}</td>
                      <td className="num">{formatCount(row.os_blocked)}</td>
                      <td className="num">{formatCount(row.device_blocked)}</td>
                      <td className="num">{formatCount(row.targeting_blocked)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
              )}
            </Sorted>
          )}
          <Callout
            tone={
              discrepancy.value.affiliate_tracker_total != null &&
              (discrepancy.value.unexplained_gap ?? 0) > 0
                ? 'caution'
                : 'note'
            }
          >
            {discrepancy.value.note}
          </Callout>

          {raisedId ? (
            // Item 3 (batch 2) — a result, not a plain note: the affiliate
            // needs the whole id to copy it, and it must still be there after
            // the write's own toast has faded. Unfolded rather than moved to
            // the toast, because the id has to stay on screen to be copied.
            <Callout fold={false}>
              {t('discrepancy.raised')} <code>{raisedId}</code> <CopyButton text={raisedId} />{' '}
              {t('discrepancy.raised.keep')}
            </Callout>
          ) : raising ? (
            <div className="form">
              {raiseFailure ? <Failure error={raiseFailure} /> : null}
              {!impliedBrand && brands.state === 'error' ? <Failure error={brands.error} /> : null}
              {!impliedBrand && brands.state !== 'error' ? (
                <Field label={t('disputes.field.brand')}>
                  <Select
                    value={raiseBrand}
                    onChange={setRaiseBrand}
                    options={[
                      { value: '', label: t('disputes.chooseBrand') },
                      ...(brands.state === 'ready' ? brands.value.items : []).map((b) => ({
                        value: b.brand_id,
                        label: b.brand_name,
                      })),
                    ]}
                  />
                </Field>
              ) : null}
              <Field
                label={t('dispute.field.what')}
                hint={
                  selectedCampaign
                    ? t('discrepancy.raisedAgainst', {
                        campaign:
                          `${selectedCampaign.brand_name} · ${selectedCampaign.campaign_name}` +
                          (product ? ` · ${product}` : ''),
                      })
                    : t('discrepancy.noCampaign')
                }
              >
                <textarea
                  className="control"
                  rows={3}
                  value={raiseText}
                  onChange={(e) => setRaiseText(e.target.value)}
                  placeholder={
                    discrepancy.value.affiliate_tracker_total != null
                      ? t('discrepancy.placeholder', { total: discrepancy.value.affiliate_tracker_total, gap: discrepancy.value.unexplained_gap ?? 0 })
                      : undefined
                  }
                />
              </Field>
              <div className="form-actions">
                <button className="copy" disabled={raiseBusy} onClick={() => setRaising(false)}>
                  {kitText('action.cancel')}
                </button>
                <button
                  className="primary"
                  disabled={raiseBusy || !raiseText.trim() || !raiseBrandId}
                  onClick={() => {
                    setRaiseBusy(true);
                    setRaiseFailure(null);
                    api
                      .raiseDispute({
                        category: 'ATTRIBUTION',
                        whatHappened: raiseText.trim(),
                        contextBrandId: raiseBrandId,
                        ...(campaignId ? { campaignVersionId: campaignId } : {}),
                        ...(product ? { product } : {}),
                        // The dates and the tracker total this panel was
                        // showing. The server rereads its own figures for
                        // them and stores those with the dispute.
                        context: {
                          source: 'CLICK_DISCREPANCY',
                          from: range.from,
                          to: range.to,
                          ...(reported !== undefined ? { reportedClicks: reported } : {}),
                        },
                      })
                      .then((r) => {
                        setRaisedId(r.dispute_id);
                        setRaising(false);
                        setRaiseText('');
                      })
                      .catch((e: unknown) => setRaiseFailure(e))
                      .finally(() => setRaiseBusy(false));
                  }}
                >
                  {raiseBusy ? t('discrepancy.raising') : t('discrepancy.raiseIt')}
                </button>
              </div>
            </div>
          ) : (
            <div className="form-actions">
              <button className="copy" type="button" onClick={() => setRaising(true)}>
                {t('discrepancy.raiseAbout')}
              </button>
            </div>
          )}
        </>
      ) : null}
    </Panel>
  );
}

/* ------------------------------------------------------------------ *
 * Sources — §39.6, §12.5c
 * ------------------------------------------------------------------ */

/*
 * §7.12d / §10.11a — every campaign open to this affiliate, in three groups.
 *
 * The console had no campaigns screen at all. Campaigns reached an affiliate
 * through the link builder's picker and through a detail dialogue opened from
 * elsewhere, so "which campaigns am I on, which have I been offered, which
 * could I ask for, and what does each pay" was four questions with no screen
 * between them.
 *
 * The three groups are kept apart rather than merged with a status column,
 * because only the first can be turned into a link: a list that mixed them
 * would invite an affiliate to promote a campaign that pays them nothing yet.
 */
function Campaigns({ api }: ScreenProps) {
  const loaded = useLoad(() => api.campaigns(), []);
  const [openCampaign, setOpenCampaign] = useState<CampaignDetailRow | null>(null);

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const view = campaignDirectoryView(loaded.value, () => '');

  const table = (
    rows: readonly CampaignDirectoryRow[],
    onOpen: ((row: CampaignDirectoryRow) => void) | null,
  ) => (
    <Sorted rows={rows} by={{ name: (r) => sortableCell(r.name), brand: (r) => sortableCell(r.brand), paidOn: (r) => sortableCell(r.paidOn), plan: (r) => sortableCell(r.plan), rate: (r) => sortableCell(r.rate), products: (r) => sortableCell(r.products), jurisdictions: (r) => sortableCell(r.jurisdictions) }}>
      {(ordered, sort) => (
    <TableScroll>
      <table>
        <thead>
          <tr>
            <SortTh sort={sort} col="name">{t('nav.campaigns')}</SortTh>
            <SortTh sort={sort} col="brand">{t('col.brand')}</SortTh>
            <SortTh sort={sort} col="paidOn">{t('campaignsDirectory.colPaidOn')}</SortTh>
            <SortTh sort={sort} col="plan">{t('campaignsDirectory.colPlan')}</SortTh>
            <SortTh sort={sort} col="rate" align="num">{t('campaignsDirectory.colRate')}</SortTh>
            <SortTh sort={sort} col="products">{t('campaignsDirectory.colProducts')}</SortTh>
            <SortTh sort={sort} col="jurisdictions">{t('campaignsDirectory.colGeos')}</SortTh>
          </tr>
        </thead>
        <tbody>
          {ordered.map((r) => (
            <tr
              key={r.key}
              {...(onOpen
                ? { className: 'row-clickable', onClick: () => onOpen(r) }
                : {})}
            >
              <td>{r.name}</td>
              <td>{r.brand}</td>
              {/* A campaign that pays nothing yet says so, rather than
                  showing an empty cell that reads as a missing figure. */}
              <td className="faint">{r.paidOn ?? t('campaignsDirectory.notYet')}</td>
              <td>{r.plan ?? '—'}</td>
              <td className="num money">{r.rate ?? '—'}</td>
              <td className="faint">{r.products}</td>
              <td className="faint">{r.jurisdictions}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
      )}
    </Sorted>
  );

  return (
    <>
      <Panel title={t('nav.campaigns')}>
        <p className="link-note">{t('campaignsDirectory.lead')}</p>
        {view.empty ? <Empty>{view.empty}</Empty> : null}

        {view.on.length > 0 ? (
          <>
            <p className="terms-summary">{t('campaignsDirectory.on')}</p>
            {table(view.on, (r) => {
              const full = loaded.value.items.find((c) => c.campaign_id === r.campaignId);
              if (full) setOpenCampaign(full as CampaignDetailRow);
            })}
          </>
        ) : null}

        {view.invited.length > 0 ? (
          <>
            <p className="terms-summary">{t('campaignsDirectory.invited')}</p>
            {table(view.invited, null)}
          </>
        ) : null}

        {view.canAskFor.length > 0 ? (
          <>
            <p className="terms-summary">{t('campaignsDirectory.canAskFor')}</p>
            {view.askNote ? <p className="link-note">{view.askNote}</p> : null}
            {table(view.canAskFor, null)}
          </>
        ) : null}
      </Panel>

      {/* The full terms, and what the payout has been — the dialogue that
          already existed, now reachable from a list instead of only from
          wherever it happened to be linked. */}
      {openCampaign ? (
        <CampaignDetailModal
          api={api}
          campaign={openCampaign}
          onClose={() => setOpenCampaign(null)}
        />
      ) : null}
    </>
  );
}

/*
 * §39.6 — the clicks themselves, under the counts of them.
 *
 * The rest of this screen answers "how much traffic, broken down how". It
 * cannot answer the question an affiliate actually arrives with, which is "I
 * clicked my own link and nothing happened": nought clicks and one click look
 * identical in a total, and the reason a click was refused — a token that
 * would not resolve, a geo rule, a campaign that had ended, a bot verdict —
 * is recorded on the click and was shown nowhere.
 */
/*
 * §17.8 / §22.2a — an affiliate's players, day by day.
 *
 * The players screen says what each one has been worth altogether, which
 * answers whether they are valuable and not when they stopped. Those are the
 * same number for a cohort that went quiet in its third week and one that did
 * not, and only one of them is worth doing something about.
 */
function DailyActivity({ api, range, now }: { api: AffiliateApi; range: ScreenProps['range']; now: Date }) {
  const paging = usePaging();
  const report = useLoad(
    () => api.customerDaily({ ...range, limit: paging.limitParam }),
    [range.from, range.to, range.brand_id, paging.limit],
  );

  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') {
    // The same switch as Customer activity and registrations (§17.8a): with
    // player-level reporting off the API refuses this per-player report, and
    // the reader is told why rather than shown the refusal's own wording.
    if (readsAsPerPlayerOff(report.error)) {
      return (
        <Panel title={t('dailyActivity.title')}>
          <Callout>{t('players.cohortOnly')}</Callout>
        </Panel>
      );
    }
    return <Failure error={report.error} />;
  }

  const view = dailyActivityView(report.value, now);
  return (
    <Panel title={t('dailyActivity.title')} aside={<span className="updated">{view.updated}</span>}>
      <p className="link-note">{t('dailyActivity.lead')}</p>
      {view.empty ? (
        <Empty>{view.empty}</Empty>
      ) : (
        <>
          <Sorted rows={view.rows} by={{ day: (r) => sortableCell(r.day), player: (r) => sortableCell(r.player), turnover: (r) => sortableCell(r.turnover), wins: (r) => sortableCell(r.wins), ggr: (r) => sortableCell(r.ggr), deposits: (r) => sortableCell(r.deposits), withdrawals: (r) => sortableCell(r.withdrawals) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="day" align="date">{t('op.cohortMonth')}</SortTh>
                  <SortTh sort={sort} col="player">{t('col.player')}</SortTh>
                  <SortTh sort={sort} col="turnover" align="num">{t('col.turnover')}</SortTh>
                  <SortTh sort={sort} col="wins" align="num">{t('col.wins')}</SortTh>
                  <SortTh sort={sort} col="ggr" align="num">{t('col.ggr')}</SortTh>
                  <SortTh sort={sort} col="deposits" align="num">{t('col.deposits')}</SortTh>
                  <SortTh sort={sort} col="withdrawals" align="num">{t('col.withdrawals')}</SortTh>
                </tr>
              </thead>
              <tbody>
                {ordered.map((r) => (
                  <tr key={r.key}>
                    <td className="date">{r.day}</td>
                    <td>{r.player}</td>
                    <td className="num money">{r.turnover}</td>
                    <td className="num money">{r.wins}</td>
                    <td className="num money">{r.ggr}</td>
                    <td className="num money">{r.deposits}</td>
                    <td className="num money">{r.withdrawals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
          <Pager
            shown={view.rows.length}
            hasMore={view.hasMore}
            atCeiling={paging.atCeiling}
            onMore={paging.more}
          />
        </>
      )}
    </Panel>
  );
}

/**
 * §12.5c — the clicks behind one day's Excluded figure on the dashboard, with
 * why each was left out. Same filters as the row it was opened from, so the
 * list is exactly the clicks that number counts.
 */
function ExcludedClicks({ api, now, query, day, onClose }: {
  api: AffiliateApi;
  now: Date;
  query: Parameters<AffiliateApi['clickLog']>[0];
  day: string;
  onClose: () => void;
}) {
  const LIMIT = 100;
  const report = useLoad(
    () => api.clickLog({ ...query, excluded: '1', limit: String(LIMIT) }),
    [day, JSON.stringify(query)],
  );
  return (
    <Modal title={t('excludedClicks.title', { day })} onClose={onClose} wide>
      {report.state === 'loading' ? <Loading /> : report.state === 'error' ? <Failure error={report.error} /> : (() => {
        const view = clickLogView(report.value, now);
        if (view.empty) return <Empty>{t('excludedClicks.empty')}</Empty>;
        return (
          <>
            <p className="link-note">
              {t('excludedClicks.lead')}
              {/* Said once, not on every row: a repeat carries no reason of
                  its own on the click, and it is the one most often read as
                  a lost click. */}
              {view.rows.some((r) => r.status === 'DUPLICATE') ? ` ${t('excludedClicks.why.DUPLICATE')}` : null}
            </p>
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <th className="date">{t('excludedClicks.colTimeUtc')}</th>
                    <th>{t('excludedClicks.colWhy')}</th>
                    <th>{t('excludedClicks.colCampaign')}</th>
                    <th>{t('excludedClicks.colFrom')}</th>
                  </tr>
                </thead>
                <tbody>
                  {view.rows.map((r) => (
                    <tr key={r.key}>
                      <td className="date">{r.timeUtc}</td>
                      <td className="wrap" style={{ minWidth: '15rem' }}>
                        <Chip tone="provisional">{t(`clickStatus.${r.status}` as AffiliateMessageKey)}</Chip>
                        {r.why ? <div>{r.why}</div> : null}
                      </td>
                      <td>
                        {r.campaign}
                        <div className="faint">{r.labels}</div>
                      </td>
                      <td>
                        {r.where}
                        <div className="faint">{r.what}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            {view.hasMore ? <p className="faint">{t('excludedClicks.more', { n: LIMIT })}</p> : null}
          </>
        );
      })()}
    </Modal>
  );
}

function ClickLog({ api, range, now }: { api: AffiliateApi; range: ScreenProps['range']; now: Date }) {
  const PAGE = 25;
  const [country, setCountry] = useState('');
  const [page, setPage] = useState(0);
  const report = useLoad(
    () => api.clickLog({
      ...range,
      limit: String(PAGE),
      offset: String(page * PAGE),
      ...(country.trim() !== '' ? { country: country.trim() } : {}),
    }),
    [range.from, range.to, range.brand_id, country, page],
  );

  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') return <Failure error={report.error} />;

  const view = clickLogView(report.value, now);
  return (
    <Panel title={t('clickLog.title')} aside={<span className="updated">{view.updated}</span>}>
      <p className="link-note">{t('clickLog.lead')}</p>
      {/* Two letters, because that is what the column holds. Typing one resets
          to the first page — staying on page 4 of a narrower result is how a
          filter reads as having matched nothing. */}
      <FilterBar>
        <Field label={t('col.country')} hint={t('clickLog.country.hint')}>
          <TextInput
            value={country}
            onChange={(v) => { setCountry(v); setPage(0); }}
            placeholder="US"
          />
        </Field>
      </FilterBar>
      {view.empty ? (
        <Empty>{view.empty}</Empty>
      ) : (
        <>
          <Sorted rows={view.rows} by={{ when: (r) => sortableCell(r.when), campaign: (r) => sortableCell(r.campaign), placement: (r) => sortableCell(r.placement), where: (r) => sortableCell(r.where), what: (r) => sortableCell(r.what), outcome: (r) => sortableCell(r.outcome) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="when" align="date">{t('clickLog.colWhen')}</SortTh>
                  <SortTh sort={sort} col="campaign">{t('dim.campaign')}</SortTh>
                  <SortTh sort={sort} col="placement">{t('clickLog.colPlacement')}</SortTh>
                  <SortTh sort={sort} col="where">{t('clickLog.colWhere')}</SortTh>
                  <SortTh sort={sort} col="what">{t('clickLog.colWhat')}</SortTh>
                  <SortTh sort={sort} col="outcome">{t('clickLog.colOutcome')}</SortTh>
                </tr>
              </thead>
              <tbody>
                {ordered.map((r) => (
                  <tr key={r.key}>
                    <td className="date">{r.when}</td>
                    <td>{r.campaign}</td>
                    <td className="terms-cell">{r.placement}</td>
                    <td>{r.where}</td>
                    <td className="faint">{r.what}</td>
                    <td>
                      {/* Three outcomes, not two. A refused click and a valid
                          one that never became a player are different
                          problems, and only the reader can act on either. */}
                      {r.outcome === 'ATTRIBUTED' ? (
                        t('clickLog.outcome.ATTRIBUTED')
                      ) : (
                        <Chip tone="provisional">
                          {t(`clickLog.outcome.${r.outcome}` as AffiliateMessageKey)}
                        </Chip>
                      )}
                      {r.why ? <div className="faint">{r.why}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
          {/*
            * Pages by offset, which this log can do and the other lists here
            * cannot: it reads a closed window, so no row arrives at the head
            * to shift the rest under somebody turning pages.
            */}
          {view.hasMore || page > 0 ? (
            <div className="form-actions">
              <button
                className="copy"
                type="button"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                {t('creatives.prev')}
              </button>
              <span className="faint">{t('clickLog.page', { page: page + 1 })}</span>
              <button
                className="copy"
                type="button"
                disabled={!view.hasMore}
                onClick={() => setPage(page + 1)}
              >
                {t('creatives.next')}
              </button>
            </div>
          ) : null}
          <IpGeolocationNote />
        </>
      )}
    </Panel>
  );
}

/**
 * Hand a built report to the browser as a file.
 *
 * The object URL is revoked immediately after the click: it is a handle onto a
 * blob the page is otherwise holding for as long as the tab lives, and a
 * reader who downloads twenty reports over a morning should not be carrying
 * twenty of them.
 */
function downloadReport(body: string, type: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([body], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * The five reports the Sources screen holds, one at a time.
 *
 * They were stacked down one page: the breakdown the dimension list beside
 * them chooses, then a by-campaign overview that ignores it, then the tracker
 * comparison, the peer figures and the raw click log. Four of the five do not
 * answer to that list, so choosing "Country" appeared to leave three reports
 * unchanged, and whichever one somebody came for was three scrolls down.
 *
 * As tabs, the dimension list is only on screen for the one report it applies
 * to, and each of the others is fetched when it is opened rather than on every
 * visit to the screen.
 */
type SourcesView = 'dimension' | 'campaign' | 'discrepancy' | 'benchmark' | 'clicks';

function Sources({ api, range, now, filters }: ScreenProps) {
  const [dimension, setDimension] = useState<string>('link_label');
  const [shown, setShown] = useState<SourcesView>('dimension');
  /*
   * #S7 — the placement filters, held here rather than on the shared bar at the
   * top of the console. That bar is rendered once and handed to every screen,
   * and a landing-page filter over the Invoices page would be furniture nobody
   * can use. This is the screen the filters answer for.
   *
   * Empty strings are dropped rather than sent: "All campaigns" is the absence
   * of a filter, not a filter for a campaign whose id is the empty string.
   */
  const [placement, setPlacement] = useState<{
    campaign_group_id?: string; campaign_id?: string; country?: string; product?: string;
  }>({});
  const filtered = useMemo(() => ({
    ...range,
    ...Object.fromEntries(Object.entries(placement).filter(([, v]) => v !== '' && v !== undefined)),
  }), [range, placement]);
  const placementKey = JSON.stringify(placement);
  // One list, read by the campaign filter below and by the discrepancy panel.
  // It was loaded twice under two names, which was two identical requests on
  // every visit to this screen.
  const campaignList = useLoad(() => api.campaigns(), []);
  const groupList = useLoad(() => api.campaignGroups(), []);
  /*
   * Both off by default, which is the file every existing download already
   * got: raw digits, no totals line. A grouped number is text to a
   * spreadsheet, so making it the default would silently break every column
   * somebody sums.
   */
  const [exportFormatted, setExportFormatted] = useState(false);
  const [exportTotals, setExportTotals] = useState(false);
  const [exportIds, setExportIds] = useState(false);
  const exportOptions = { formatted: exportFormatted, totals: exportTotals, referenceIds: exportIds };

  /*
   * §39.6 — "choose columns". Extra dimensions ticked beside the primary one,
   * held as a list so the column order on screen is the order they were chosen
   * in. The primary is filtered out of the grouping if it is also ticked,
   * because asking for one dimension twice is refused by the API.
   */
  const [extras, setExtras] = useState<readonly string[]>([]);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const grouping = [dimension, ...extras.filter((e) => e !== dimension)].join(',');
  const report = useLoad(
    () => api.performance(grouping, filtered),
    [grouping, range.from, range.to, range.brand_id, placementKey],
  );
  /*
   * Fetched when their tab is opened, not on every visit. `null` is "this tab
   * is shut", which is why each one is guarded on its value as well as its
   * state — a `ready` holding `null` is not an empty report.
   */
  const overview = useLoad(
    () => (shown === 'campaign' ? api.performanceOverview(range, 'campaign') : Promise.resolve(null)),
    [shown, range.from, range.to, range.brand_id],
  );
  const benchmark = useLoad(
    () => (shown === 'benchmark' ? api.benchmark(range) : Promise.resolve(null)),
    [shown, range.from, range.to, range.brand_id],
  );

  const dimensionLabel = dimensions().find((d) => d.value === dimension)?.label ?? dimension;

  return (
    <>
      <div className="page-head">
        <h1>{t('sources.title')}</h1>
        {report.state === 'ready' ? (
          <span className="updated">{performance(report.value, now, dimension).updated}</span>
        ) : null}
      </div>

      {filters}

      <TabBar aria-label={t('sources.views')}>
        <Tab selected={shown === 'dimension'} onClick={() => setShown('dimension')}>
          {t('sources.view.dimension')}
        </Tab>
        <Tab selected={shown === 'campaign'} onClick={() => setShown('campaign')}>
          {t('sources.view.campaign')}
        </Tab>
        <Tab selected={shown === 'discrepancy'} onClick={() => setShown('discrepancy')}>
          {t('discrepancy.panel')}
        </Tab>
        <Tab selected={shown === 'benchmark'} onClick={() => setShown('benchmark')}>
          {t('benchmark.panel')}
        </Tab>
        <Tab selected={shown === 'clicks'} onClick={() => setShown('clicks')}>
          {t('clickLog.title')}
        </Tab>
      </TabBar>

      {shown === 'dimension' ? (
      <SectionSplit
        ariaLabel={t('sources.sections')}
        section={dimension}
        onSection={setDimension}
        groups={[{
          label: t('sources.title'),
          items: dimensions().map((d) => ({ id: d.value, label: d.label })),
        }]}
      >
      {/*
        The placement filters, on the report they answer for. Campaign comes
        from the affiliate's own list; country and product are catalogues, so
        both are offered rather than typed.
      */}
      <FilterBar>
        <Field label={t('filter.campaignGroup')}>
          <Select
            value={placement.campaign_group_id ?? ''}
            onChange={(v) => setPlacement({ ...placement, campaign_group_id: v })}
            options={[
              { value: '', label: t('filter.allCampaignGroups') },
              ...(groupList.state === 'ready'
                ? groupList.value.items.map((g) => ({ value: g.id, label: g.label }))
                : []),
            ]}
          />
        </Field>
        <Field label={t('dim.campaign')}>
          <Select
            value={placement.campaign_id ?? ''}
            onChange={(v) => setPlacement({ ...placement, campaign_id: v })}
            options={[
              { value: '', label: t('filter.allCampaigns') },
              ...(campaignList.state === 'ready'
                ? campaignList.value.items.map((c) => ({ value: c.campaign_id, label: c.campaign_name }))
                : []),
            ]}
          />
        </Field>
        <Field label={t('dim.geo')}>
          <Select
            value={placement.country ?? ''}
            onChange={(v) => setPlacement({ ...placement, country: v })}
            options={[{ value: '', label: t('filter.allCountries') }, ...countryOptions()]}
          />
        </Field>
      </FilterBar>

      {/*
        §39.6 — ticking one adds a grouping column, so the table goes from a
        row per country to a row per country per campaign. The primary
        dimension is left out: it is already the first column (and the one
        chosen in the list beside this), and offering it would let somebody
        ask for it twice.

        Folded, and off the filter bar. Twenty dimensions in a bordered box
        is half a screen of checkboxes above a report nobody came here to
        tick: as one tall flex item beside three filters it took a line of
        its own, left the country filter stranded in the white space next to
        it, and pushed the table itself under the fold. The count on the
        summary is what says the columns are on while it is shut.
      */}
      <details
        className="column-picker"
        open={columnsOpen}
        onToggle={(e) => setColumnsOpen(e.currentTarget.open)}
      >
        <summary>
          {t('sources.addColumns')}
          {extras.length > 0 ? <span className="count-badge">{extras.length}</span> : null}
        </summary>
        {/* The `<fieldset>`/`<legend>` this was is what told a screen reader
            the boxes are one group; a `<legend>` inside the grid would now be
            a cell of it, so the group says its own name instead. */}
        <div className="checkbox-list" role="group" aria-label={t('sources.addColumns')}>
          {dimensions()
            .filter((d) => d.value !== dimension)
            .map((d) => (
              <label key={d.value}>
                <input
                  type="checkbox"
                  checked={extras.includes(d.value)}
                  onChange={(e) =>
                    setExtras(e.currentTarget.checked
                      ? [...extras, d.value]
                      : extras.filter((x) => x !== d.value))}
                />
                {d.label}
              </label>
            ))}
        </div>
      </details>

      {report.state === 'loading' ? <Loading /> : null}
      {report.state === 'error' ? <Failure error={report.error} /> : null}

      {report.state === 'ready'
        ? (() => {
            const view = performance(report.value, now, dimension);
            return (
              <>
                <Panel
                  title={dimensionLabel}
                  aside={
                    <span className="panel-aside">
                      <span className="count count-badge">{view.rows.length}</span>
                    </span>
                  }
                >
                  {view.rows.length === 0 ? (
                    <Empty>{t('sources.empty')}</Empty>
                  ) : (
                    <>
                      {/* Where the traffic came from, before the rows that
                          count it — the reference consoles open the country
                          report with a map, and it is the one dimension a
                          picture reads faster than a list. */}
                      {dimension === 'geo' ? (
                        <WorldMap
                          values={view.rows.map((r) => [r.geo, r.counted] as const)}
                          label={(code, n) => `${geoLabel(code)}: ${t(n === 1 ? 'map.clicks.one' : 'map.clicks.other', { n: formatCount(n) })}`}
                          title={t('map.title')}
                        />
                      ) : null}
                      {/*
                        The download, on a strip of its own above the table
                        rather than crammed into the panel's title bar. Three
                        checkboxes and two buttons beside an `h2` wrapped onto
                        a second line and left the title floating against a
                        ragged block of controls; on a line of their own they
                        read as one sentence — what goes in the file, then the
                        file.

                        The checkboxes describe the *file*, not the format, so
                        they apply to whichever of the two buttons is pressed.
                      */}
                      <div className="report-export">
                        <span className="report-export-options">
                          <span className="report-export-label">
                            {t('sources.exportOptions')}
                          </span>
                          <label className="export-option">
                            <input
                              type="checkbox"
                              checked={exportFormatted}
                              onChange={(e) => setExportFormatted(e.currentTarget.checked)}
                            />
                            {t('export.formatted')}
                          </label>
                          <label className="export-option">
                            <input
                              type="checkbox"
                              checked={exportTotals}
                              onChange={(e) => setExportTotals(e.currentTarget.checked)}
                            />
                            {t('export.totals')}
                          </label>
                          {/* Only meaningful where the grouped thing has an id
                              of its own — a campaign, a landing page, a
                              creative. On a sub-ID or a country the column
                              comes back empty, which is the honest answer. */}
                          <label className="export-option">
                            <input
                              type="checkbox"
                              checked={exportIds}
                              onChange={(e) => setExportIds(e.currentTarget.checked)}
                            />
                            {t('export.referenceIds')}
                          </label>
                        </span>
                        <span className="report-export-actions">
                          <button
                            className="copy btn-sm"
                            type="button"
                            onClick={() =>
                              downloadReport(
                                performanceCsv(dimensionLabel, view.rows, exportOptions),
                                'text/csv',
                                `sources-${dimension}.csv`,
                              )
                            }
                          >
                            {t('sources.exportCsv')}
                          </button>
                          <button
                            className="copy btn-sm"
                            type="button"
                            onClick={() =>
                              downloadReport(
                                performanceXml(dimensionLabel, view.rows, exportOptions),
                                'application/xml',
                                `sources-${dimension}.xml`,
                              )
                            }
                          >
                            {t('sources.exportXml')}
                          </button>
                        </span>
                      </div>
                      <TableScroll>
                        <table>
                          <thead>
                            <tr>
                              {/* §39.6 — a column per grouped dimension where
                                  several were asked for, and the single column
                                  this table always had otherwise. */}
                              {view.columns
                                ? view.columns.map((c) => <th key={c}>{c}</th>)
                                : <th>{t('col.value')}</th>}
                              <th className="num">{t('col.clicks')}</th>
                              {/* Beside the clicks it divides. The brand console
                                  has shown this since its breakdown was built. */}
                              <th>{t('col.share')}</th>
                              <th className="num">{t('col.excluded')}</th>
                              {/* §12.7 — beside the clicks they divide, because
                                  the pair is what tells a thousand people who
                                  came once from one who came a thousand times. */}
                              <th className="num">{t('col.reach')}</th>
                              <th className="num">{t('col.frequency')}</th>
                              <th className="num">{t('col.automatedShare')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {view.rows.map((row) => (
                              <Fragment key={row.value}>
                              <tr className={row.excludedNote || row.warning ? 'has-note' : undefined}>
                                {row.parts
                                  ? row.parts.map((p, i) => <td key={`${row.value}-${i}`}>{p}</td>)
                                  : <td>{row.value}</td>}
                                <td className="num">{formatCount(row.counted)}</td>
                                {/* The number beside the bar rather than only in
                                    it: a bar answers "which is biggest" at a
                                    glance and "by how much" not at all. */}
                                <td>
                                  <div className="share-cell">
                                    <div className="bar-track">
                                      <div className="bar-fill" style={{ width: `${row.sharePct}%` }} />
                                    </div>
                                    <span className="share-pct">{row.sharePct}%</span>
                                  </div>
                                </td>
                                <td className="num faint">{row.excluded || '—'}</td>
                                <td className="num faint">{row.reach}</td>
                                <td className="num faint">{row.frequency}</td>
                                <td className="num">
                                  {/* Item 3 — a high automated share needs a
                                      look, but it is not a money state or a
                                      negative outcome the way `overdue` means. */}
                                  {row.warning ? (
                                    <Chip tone="attention">{row.botShare}</Chip>
                                  ) : (
                                    <span className="faint">{row.botShare}</span>
                                  )}
                                </td>
                              </tr>
                              {/* Item 10 — "Why excluded" off its own cell and
                                  onto a row-note, same as Links → Click report:
                                  see styles.css's `tr.has-note` / `tr.row-note`
                                  pair.

                                  The automated-share warning now travels the
                                  same way. As its own column it was a sentence
                                  competing with four numbers for the table's
                                  width — some 550px of it — which left the
                                  figures squeezed and the heading above them
                                  pushed off to the right. A row-note and not a
                                  tooltip, because the reader who needs this
                                  most is the one scanning a list of placements
                                  on a phone, and hover reaches neither them nor
                                  a screen reader nor a printed page. */}
                              {row.excludedNote || row.warning ? (
                                <tr className="row-note">
                                  {/* The grouping columns, then clicks, share,
                                      excluded, reach, frequency, automated
                                      share — so the note still spans the row
                                      once more than one dimension is grouped. */}
                                  <td colSpan={(row.parts?.length ?? 1) + 6}>
                                    <span className="row-note-text">
                                      {row.warning}
                                      {row.warning && row.excludedNote ? ' ' : null}
                                      {/* The exclusion breakdown is a bare
                                          list ("402 automated, 5 duplicate"),
                                          not a sentence: run straight on after
                                          the warning's full stop it read as
                                          one garbled sentence. Faint, so the
                                          eye takes it as the detail under the
                                          thing that needed saying. */}
                                      {row.excludedNote
                                        ? <span className="faint">{row.excludedNote}</span>
                                        : null}
                                    </span>
                                  </td>
                                </tr>
                              ) : null}
                              </Fragment>
                            ))}
                          </tbody>
                        </table>
                      </TableScroll>
                    </>
                  )}
                </Panel>

                <Callout>{view.unlabelled}</Callout>
              </>
            );
          })()
        : null}
      </SectionSplit>
      ) : null}

      {shown === 'campaign' ? (
        overview.state === 'error' ? <Failure error={overview.error} /> :
        overview.state === 'ready' && overview.value ? (
        <Panel title={t('sources.byCampaign')}>
          {(() => {
            const view = performance(overview.value, now, 'campaign');
            return view.rows.length === 0 ? (
              <Empty>{t('sources.byCampaign.empty')}</Empty>
            ) : (
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <th>{t('col.campaign')}</th>
                      <th className="num">{t('col.clicks')}</th>
                      <th className="num">{t('col.excluded')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.rows.map((row) => (
                      <Fragment key={row.value}>
                      <tr className={row.excludedNote ? 'has-note' : undefined}>
                        <td>{row.value}</td>
                        <td className="num">{formatCount(row.counted)}</td>
                        <td className="num faint">{row.excluded || '—'}</td>
                      </tr>
                      {/* Item 10 — same row-note pattern as the table above. */}
                      {row.excludedNote ? (
                        <tr className="row-note">
                          {/* campaign, clicks, excluded */}
                          <td colSpan={3}><span className="row-note-text">{row.excludedNote}</span></td>
                        </tr>
                      ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            );
          })()}
        </Panel>
        ) : <Loading />
      ) : null}

      {shown === 'discrepancy' ? (
        <ClickDiscrepancyPanel
          api={api}
          range={range}
          campaigns={campaignList.state === 'ready' ? campaignList.value.items : []}
        />
      ) : null}

      {shown === 'benchmark' ? (
        benchmark.state === 'error' ? <Failure error={benchmark.error} /> :
        benchmark.state === 'ready' && benchmark.value ? (
        <Panel title={t('benchmark.panel')}>
          {benchmark.value.withheld ? (
            <Callout>{benchmark.value.withheld}</Callout>
          ) : benchmark.value.data ? (
            <div className="figures">
              <article className="figure">
                <span className="figure-label">{t('benchmark.yourClicks')}</span>
                <span className="figure-value">
                  {formatCount(benchmark.value.data.your_clicks)}
                </span>
              </article>
              <article className="figure">
                <span className="figure-label">{t('benchmark.peerMedian')}</span>
                <span className="figure-value">
                  {formatCount(benchmark.value.data.peer_median_clicks)}
                </span>
              </article>
              <article className="figure">
                <span className="figure-label">{t('benchmark.peers')}</span>
                <span className="figure-value">{benchmark.value.data.peer_count}</span>
              </article>
            </div>
          ) : (
            <Empty>{t('benchmark.empty')}</Empty>
          )}
        </Panel>
        ) : <Loading />
      ) : null}

      {shown === 'clicks' ? <ClickLog api={api} range={range} now={now} /> : null}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Commission
 * ------------------------------------------------------------------ */

function Statement({ api, range, now, filters }: ScreenProps) {
  type StatementSection = 'held' | 'periods' | 'trace';
  const [section, setSection] = useState<StatementSection>('periods');
  // §23.8 — a row's own Trace action sets this and jumps to the trace
  // section, so the affiliate never has to know or type a raw line id.
  const [traceLineId, setTraceLineId] = useState<string | null>(null);
  const report = useLoad(() => api.commission(range), [range.from, range.to, range.brand_id]);
  const held = useLoad(() => api.heldLines(), []);
  // QA round 2 F4 — the revenue deficit still carried is a balance, not a
  // figure of the chosen range, so it is read from the statements list
  // (brand filter only) and shown whatever dates are picked.
  const standing = useLoad(() => api.statements(range.brand_id), [range.brand_id]);
  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') return <Failure error={report.error} />;

  const view = statement(report.value, now);
  const heldView = held.state === 'ready' ? heldLines(held.value.items) : [];
  const deficits = standing.state === 'ready' ? carryoverDeficitFigures(standing.value.carryover_deficits) : [];

  return (
    <>
      <div className="page-head">
        <h1>{t('commission.title')}</h1>
        <span className="updated">{view.updated}</span>
        <div className="page-head-actions">
          {/* §39 — the report leaves the screen. Reconciling a month by copying
              figures by hand is how a transcription error becomes a dispute
              about the platform's arithmetic. */}
          <button
            className="link-button"
            onClick={() => {
              void api.download('/v1/reports/commission', {
                from: range.from,
                to: range.to,
                format: 'csv',
              });
            }}
          >
            {t('commission.downloadCsv')}
          </button>
        </div>
      </div>
      {filters}

      {deficits.length > 0 ? (
        <div className="figures">
          {deficits.map((f) => <Figure key={`deficit-${f.label}-${f.value}`} view={f} />)}
        </div>
      ) : null}

      <SectionSplit
        ariaLabel={t('commission.sections')}
        section={section}
        onSection={setSection}
        groups={[{
          label: t('commission.title'),
          items: [
            ...(heldView.length > 0 ? [{ id: 'held', label: t('commission.section.held') }] : []),
            { id: 'periods', label: t('commission.section.periods') },
            { id: 'trace', label: t('commission.section.trace') },
          ],
        }]}
      >
          {section === 'held' && held.state === 'ready' && heldView.length > 0 ? (
        <Panel title={t('commission.section.held')}>
          <p className="link-note">{t('commission.quarantine.lead')}</p>
          <Sorted rows={heldView} by={{ period: (line) => sortableCell(line.period), brand: (line) => sortableCell(line.brand), kind: (line) => sortableCell(line.kind), amount: (line) => sortableCell(line.amount), reason: (line) => sortableCell(line.reason), releaseOn: (line) => sortableCell(line.releaseOn) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="period">{t('col.period')}</SortTh>
                  <SortTh sort={sort} col="brand">{t('col.brand')}</SortTh>
                  <SortTh sort={sort} col="kind">{t('col.type')}</SortTh>
                  <SortTh sort={sort} col="amount" align="num">{t('col.amount')}</SortTh>
                  <SortTh sort={sort} col="reason">{t('col.whyHeld')}</SortTh>
                  <SortTh sort={sort} col="releaseOn" align="num">{t('col.release')}</SortTh>
                </tr>
              </thead>
              <tbody>
                {ordered.map((line) => (
                  <tr key={line.id}>
                    <td className="faint">{line.period}</td>
                    <td>{line.brand}</td>
                    <td>{line.kind}</td>
                    <td className="num money">{line.amount}</td>
                    <td className="wrap">{line.reason}</td>
                    <td className="num">{line.releaseOn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
          <Callout>{held.value.note}</Callout>
        </Panel>
          ) : null}

          {section === 'periods' ? (
      <Panel title={t('commission.section.periods')}>
        {/* §21.7f — no currency switch here, and nothing converted: these are
            the lines a statement and an invoice are built from. */}
        {view.lines.length === 0 ? (
          <Empty>{t('commission.empty')}</Empty>
        ) : (
          <Sorted rows={view.lines} by={{ period: (line) => sortableCell(line.period), brand: (line) => sortableCell(line.brand), kind: (line) => sortableCell(line.kind), amount: (line) => sortableCell(line.amount), because: (line) => sortableCell(line.because) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="period">{t('col.period')}</SortTh>
                  <SortTh sort={sort} col="brand">{t('col.brand')}</SortTh>
                  <SortTh sort={sort} col="kind">{t('col.type')}</SortTh>
                  <SortTh sort={sort} col="amount" align="num">{t('col.amount')}</SortTh>
                  <SortTh sort={sort} col="amount">{t('col.state')}</SortTh>
                  <SortTh sort={sort} col="because">{t('col.because')}</SortTh>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ordered.map((line) => (
                  <tr key={line.lineId}>
                    <td className="num faint">{line.period}</td>
                    <td>{line.brand}</td>
                    <td>{line.kind}</td>
                    <td className={`num money${line.amount.value.startsWith('−') ? ' negative' : ''}`}>
                      {line.amount.value}
                    </td>
                    <td>
                      {/* §39.4 — judged on this line's own period, not the
                          range's. "Locked, not merely closed." */}
                      {line.amount.provisional
                        ? <Chip tone="provisional">{t('commission.provisional')}</Chip>
                        : <Chip tone="locked">{t('commission.locked')}</Chip>}
                    </td>
                    {/* §33.4a — "our system says so" is not an acceptable answer
                        to an affiliate asking why a figure is what it is. */}
                    <td className="num faint">{line.because.join(' ') || '—'}</td>
                    <td className="num">
                      {/* §23.8 — the trace this line's own id opens; nowhere
                          else in this portal shows that id as a raw value. */}
                      <button
                        className="link-button"
                        onClick={() => {
                          setTraceLineId(line.lineId);
                          setSection('trace');
                        }}
                      >
                        {t('commission.trace')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
        )}
      </Panel>
          ) : null}

          {section === 'trace' ? (
            <CommissionTraceLookup api={api} presetLineId={traceLineId} />
          ) : null}

          <Callout>{view.provenance}</Callout>
      </SectionSplit>
    </>
  );
}

function CommissionTraceLookup({
  api,
  presetLineId,
}: {
  api: AffiliateApi;
  /** §23.8 — set by a row's own Trace action on "By period and brand". */
  presetLineId?: string | null;
}) {
  const [lineId, setLineId] = useState('');
  const [result, setResult] = useState<CommissionTraceView | null>(null);
  const [busy, setBusy] = useState(false);
  // Item 5 — "run" had no `.catch`; an id that does not resolve (mistyped, or
  // from a period the affiliate cannot see) disappeared with nothing shown.
  const [failure, setFailure] = useState<unknown>(null);

  const run = useCallback((id: string) => {
    setBusy(true);
    setResult(null);
    setFailure(null);
    void api
      .commissionTrace(id)
      .then((r) => setResult(commissionTraceRows(r)))
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  }, [api]);

  useEffect(() => {
    if (!presetLineId) return;
    setLineId(presetLineId);
    run(presetLineId);
    // Only on a genuinely new id — a re-render with the same preset must not
    // re-fire the trace, or every unrelated state change on this screen would.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetLineId]);

  return (
    <Panel title={t('trace.panel')}>
      {/* §23.8 — inputs, rates, rule IDs. */}
      <div className="form">
        <Field label={t('trace.field.lineId')}>
          <TextInput value={lineId} onChange={setLineId} />
        </Field>
        <div className="form-actions">
          <button
            className="primary"
            disabled={busy || !lineId.trim()}
            onClick={() => run(lineId.trim())}
          >
            {busy ? t('trace.loading') : t('commission.trace')}
          </button>
        </div>
        {failure ? <ErrorNotice error={failure} onRetry={() => run(lineId.trim())} /> : null}
      </div>
      {/* §23.8 — the trace the affiliate asked for, one fact per row: whose
          player, where, which deal version and price row, the basis and the
          rate. Never the rule ids (`commissionTraceRows` leaves them out). */}
      {result ? (
        <div className="stack">
          <TermsRowsTable rows={result.rows} />
          {result.breakdown.length > 0 ? (
            <div className="stack">
              <span className="figure-label">{t('trace.breakdown')}</span>
              <TermsRowsTable rows={result.breakdown} />
            </div>
          ) : null}
          {result.steps.length > 0 ? (
            <div className="stack">
              <span className="figure-label">{t('trace.steps')}</span>
              <ol className="deductions">
                {result.steps.map((step, i) => <li key={`${i}:${step}`}>{step}</li>)}
              </ol>
            </div>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

/* ------------------------------------------------------------------ *
 * Payments
 * ------------------------------------------------------------------ */

/**
 * §30.1a — the list of statements, including the periods that earned nothing.
 *
 * The zero rows are the reason this screen exists. An affiliate who had a bad
 * month and sees an empty list cannot tell it from a platform that stopped
 * computing, and §30.1a says so directly: "Silence in a bad month is
 * indistinguishable from a broken platform."
 */
/**
 * §42.7 — everything that affected their money, and who did it.
 *
 * The audit log has recorded all of this since Ch. 42 was built and only the
 * operator could read it, so an affiliate asking why their rate changed in
 * March had to ask the person who changed it. §33.4a is about exactly that
 * position: "our system says so" is not an acceptable answer, and neither is
 * "ask the person who did it".
 */
/**
 * Ch. 33 — raising a disagreement, and what happened to it.
 *
 * §33.4a sets the standard the screen has to meet: "our system says so" is not
 * an acceptable answer. Which means the resolution is shown in the operator's
 * own words, and for a settlement dispute the screen says plainly what the
 * platform can and cannot establish — §33.1b forbids presenting ourselves as
 * having verified a payment we never saw.
 */
const DISPUTE_CATEGORY_OPTIONS = [
  { value: 'COMMISSION_FIGURE', labelKey: 'disputeCat.COMMISSION_FIGURE' },
  { value: 'ATTRIBUTION', labelKey: 'disputeCat.ATTRIBUTION' },
  { value: 'DEDUCTION', labelKey: 'disputeCat.DEDUCTION' },
  { value: 'CLAWBACK', labelKey: 'disputeCat.CLAWBACK' },
  { value: 'HOLD', labelKey: 'disputeCat.HOLD' },
  { value: 'SETTLEMENT', labelKey: 'disputeCat.SETTLEMENT' },
  { value: 'STATUS_CHANGE', labelKey: 'disputeCat.STATUS_CHANGE' },
] as const satisfies readonly { value: string; labelKey: AffiliateMessageKey }[];

/**
 * §3.11 / E40 — authority platform support has asked for, in this affiliate's
 * name.
 *
 * Support can read this account and may not act on it (§3.9d). Where they must
 * act, §3.9d routes the request through the operator — except here, because a
 * dispute is *against* the operator and asking them to approve it would hand
 * the complaint to the party it is against. So the affiliate is the only one
 * who can say yes, and this panel is where they do.
 *
 * It sits inside Disputes rather than in Account on purpose: the thing being
 * authorised is a dispute, and a consent screen filed away from the subject it
 * concerns is one nobody reads in context.
 */
function MandatesPanel({ api }: ScreenProps) {
  const [version, setVersion] = useState(0);
  const loaded = useLoad(() => api.agentMandates(), [version]);
  const [busy, setBusy] = useState('');
  const [failure, setFailure] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Item 2 — "Allow, once" used to grant immediately on click; it now opens
  // a Confirm naming what is being allowed, for the one row it was clicked on.
  const [confirmingGrant, setConfirmingGrant] = useState<{ id: string; reason: string } | null>(null);

  const act = (id: string, fn: () => Promise<{ message: string }>) => {
    setBusy(id);
    setFailure(null);
    void fn()
      .then((r) => { setNotice(r.message); setVersion((v) => v + 1); })
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(''));
  };

  // Nothing ever asked for, and nothing to explain. A consent panel on an
  // account nobody has asked anything of is noise on a screen that already
  // has a job.
  if (loaded.state === 'ready' && loaded.value.items.length === 0) return null;

  return (
    <Panel title={t('mandates.title')}>
      {/* Item 3 (batch 2) — the action's own result, read whole. */}
      {notice ? <Callout fold={false}>{notice}</Callout> : null}
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      {loaded.state === 'loading' ? <Loading rows={1} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'ready' ? (
        <>
          <Sorted rows={loaded.value.items} by={{ act: (m) => sortableCell(m.act), reason: (m) => sortableCell(m.reason), requested_by_email: (m) => sortableCell(actorName(m.requested_by_email, m.requested_by_platform)), state: (m) => sortableCell(m.state) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="act">{t('mandates.col.what')}</SortTh>
                  <SortTh sort={sort} col="reason">{t('mandates.col.why')}</SortTh>
                  <SortTh sort={sort} col="requested_by_email">{t('mandates.col.who')}</SortTh>
                  <SortTh sort={sort} col="state">{t('mandates.col.state')}</SortTh>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ordered.map((m) => (
                  <tr key={m.id}>
                    <td>{m.act === 'RAISE_DISPUTE' ? t('mandates.act.dispute') : m.act}</td>
                    <td className="faint">{m.reason}</td>
                    <td className="faint">{actorName(m.requested_by_email, m.requested_by_platform)}</td>
                    <td>
                      {/* Item 3/4 — REQUESTED is attention (it is waiting on
                          the affiliate's own answer), USED is neutral (done,
                          not live); the raw state code through its word
                          helper rather than shown as-is. */}
                      <Chip tone={mandateStateTone(m.state)}>{mandateStateWords(m.state)}</Chip>
                    </td>
                    <td>
                      {m.state === 'REQUESTED' ? (
                        <div className="panel-aside">
                          <button
                            className="primary"
                            disabled={busy === m.id}
                            onClick={() => setConfirmingGrant({ id: m.id, reason: m.reason })}
                          >
                            {t('mandates.grant')}
                          </button>
                          {/* Item 2 — a bare, class-less button rendered at
                              the browser's own 21px; `.danger-quiet` is the
                              kit's destructive-secondary style. */}
                          <button
                            className="danger-quiet"
                            disabled={busy === m.id}
                            onClick={() => act(m.id, () => api.revokeAgentMandate(m.id))}
                          >
                            {t('mandates.revoke')}
                          </button>
                        </div>
                      ) : m.state === 'GRANTED' ? (
                        <button
                          className="danger-quiet"
                          disabled={busy === m.id}
                          onClick={() => act(m.id, () => api.revokeAgentMandate(m.id))}
                        >
                          {t('mandates.revoke')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
          <Callout>{t('mandates.explain')}</Callout>
        </>
      ) : null}
      {confirmingGrant ? (
        <Confirm
          title={t('mandates.grant')}
          confirmLabel={t('mandates.grant')}
          body={<>{t('mandates.act.dispute')} — {confirmingGrant.reason} {t('mandates.explain')}</>}
          onCancel={() => setConfirmingGrant(null)}
          onConfirm={() =>
            api.grantAgentMandate(confirmingGrant.id).then((r) => {
              setNotice(r.message);
              setVersion((v) => v + 1);
              setConfirmingGrant(null);
            })
          }
        />
      ) : null}
    </Panel>
  );
}

function Disputes({ api }: ScreenProps) {
  type DisputesSection = 'raise' | 'list' | 'detail';
  const [section, setSection] = useState<DisputesSection>('list');
  const [version, setVersion] = useState(0);
  const [category, setCategory] = useState('COMMISSION_FIGURE');
  const [brandId, setBrandId] = useState('');
  const [campaignVersionId, setCampaignVersionId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [what, setWhat] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [raiseBusy, setRaiseBusy] = useState(false);
  const [raiseFailure, setRaiseFailure] = useState<unknown>(null);
  const [selectedId, setSelectedId] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [justRaised, setJustRaised] = useState<string | null>(null);

  const loaded = useLoad(() => api.disputes(), [version]);
  // Every dispute names its brand. One brand is chosen for them; several
  // are a choice they have to make, not one made for them by list order.
  const brands = useLoad(() => api.brands(), []);
  const onlyBrand =
    brands.state === 'ready' && brands.value.items.length === 1 ? brands.value.items[0]!.brand_id : '';
  useEffect(() => {
    if (onlyBrand) setBrandId((b) => b || onlyBrand);
  }, [onlyBrand]);
  // Which campaign and which statement, when it is about one — the server
  // records that period's commission with the dispute, so the brand starts
  // from the figures rather than from a description of them.
  const campaigns = useLoad(() => api.campaigns(), []);
  const statements = useLoad(
    () => (brandId ? api.statements(brandId) : Promise.resolve(null)),
    [brandId],
  );

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  return (
    <ScreenWithSections
      title={t('disputes.title')}
      // Item 8 — the affiliate's own disputes list is the default section
      // (unchanged); raising one was reachable only from the sub-nav, so it
      // is now also the page-head's primary action.
      headAside={
        <button type="button" className="primary" onClick={() => setSection('raise')}>
          {t('disputes.section.raise')}
        </button>
      }
      ariaLabel={t('disputes.sections')}
      section={section}
      onSection={setSection}
      groups={[{
        label: t('disputes.title'),
        items: [
          { id: 'raise', label: t('disputes.section.raise') },
          { id: 'list', label: t('disputes.section.list') },
          { id: 'detail', label: t('disputes.section.detail') },
        ],
      }]}
    >
          {/* Item 8 — the raise form sits in a Panel like every other form. */}
          {section === 'raise' ? (
      <Panel title={t('disputes.section.raise')}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!what.trim() || !brandId || raiseBusy) return;
          setRaiseBusy(true);
          setRaiseFailure(null);
          try {
            const r = await api.raiseDispute({
              category,
              whatHappened: what,
              contextBrandId: brandId,
              ...(campaignVersionId ? { campaignVersionId } : {}),
              ...(periodId ? { periodId } : {}),
              context: { source: 'DISPUTES_FORM' },
              ...(files.length > 0 ? { attachments: await toAttachmentUploads(files) } : {}),
            });
            setWhat('');
            setFiles([]);
            setCampaignVersionId('');
            setPeriodId('');
            setVersion((v) => v + 1);
            setJustRaised(r.dispute_id);
            setSection('list');
          } catch (err) {
            setRaiseFailure(err);
          } finally {
            setRaiseBusy(false);
          }
        }}
      >
        {raiseFailure ? <Failure error={raiseFailure} /> : null}
        <Field label={t('disputes.field.brand')}>
          {brands.state === 'loading' ? (
            <Loading />
          ) : brands.state === 'error' ? (
            <Failure error={brands.error} />
          ) : brands.value.items.length === 0 ? (
            <Callout tone="caution">{t('disputes.noBrands')}</Callout>
          ) : (
            <Select
              value={brandId}
              onChange={(b) => {
                setBrandId(b);
                // Both belong to a brand; kept across a brand change they
                // would name another brand's campaign and period.
                setCampaignVersionId('');
                setPeriodId('');
              }}
              options={[
                { value: '', label: t('disputes.chooseBrand') },
                ...brands.value.items.map((b) => ({ value: b.brand_id, label: b.brand_name })),
              ]}
            />
          )}
        </Field>
        {brandId ? (
          <>
            <Field label={t('disputes.field.campaign')}>
              <Select
                value={campaignVersionId}
                onChange={setCampaignVersionId}
                options={[
                  { value: '', label: t('disputes.notOne') },
                  ...(campaigns.state === 'ready' ? campaigns.value.items : [])
                    .filter((c) => c.brand_id === brandId)
                    .map((c) => ({ value: c.campaign_version_id, label: c.campaign_name })),
                ]}
              />
            </Field>
            <Field label={t('disputes.field.period')}>
              <Select
                value={periodId}
                onChange={setPeriodId}
                options={[
                  { value: '', label: t('disputes.notOne') },
                  // One option per period: a corrected statement shares its
                  // period with the one it supersedes, and a dispute is about
                  // the period.
                  ...[...new Map(
                    (statements.state === 'ready' && statements.value ? statements.value.data : [])
                      .map((s) => [s.period_id, s] as const)
                      .reverse(),
                  ).values()].reverse().map((s) => ({
                    value: s.period_id,
                    label: `${s.statement_no} · ${formatDay(periodDays(s.period_from, s.period_to).first)} – ${formatDay(periodDays(s.period_from, s.period_to).last)}`,
                  })),
                ]}
              />
            </Field>
          </>
        ) : null}
        <Field label={t('disputes.field.about')}>
          <Select
            value={category}
            onChange={setCategory}
            options={DISPUTE_CATEGORY_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
          />
        </Field>
        <Field label={t('dispute.field.what')}>
          <textarea
            className="control"
            value={what}
            onChange={(e) => setWhat(e.target.value)}
            rows={4}
          />
        </Field>
        <Field group label={t('disputes.field.attachments')}>
          <AttachmentPicker files={files} onChange={setFiles} />
        </Field>
        <Callout>
          {t('disputes.notDelaying')}
        </Callout>
        {/* Item 8 — "Raise it" used to go disabled with nothing on screen
            saying why; this names what is still missing. */}
        {!raiseBusy && (!what.trim() || !brandId) ? (
          <p className="field-hint">{t('disputes.raiseIt.hint')}</p>
        ) : null}
        <div className="form-actions">
          <button type="submit" className="primary" disabled={raiseBusy || !what.trim() || !brandId}>
            {raiseBusy ? t('discrepancy.raising') : t('discrepancy.raiseIt')}
          </button>
        </div>
      </form>
      </Panel>
          ) : null}

          {section === 'list' ? (
            <>
      {/* §3.11 — above the list, because a request to act in your name is
          something to answer before reading what has already been raised.
          Renders nothing when nobody has asked. */}
      <MandatesPanel {...({ api } as ScreenProps)} />
      {justRaised ? (
        // Item 3 (batch 2) — same reasoning as the discrepancy screen's own
        // "Raised" note: the id must stay on screen, whole, to be copied.
        <Callout fold={false}>
          {t('discrepancy.raised')} <code>{justRaised}</code>{' '}
          <CopyButton text={justRaised} /> {t('discrepancy.raised.keep')}
        </Callout>
      ) : null}
      {loaded.value.data.length === 0 ? (
        <Empty>{t('disputes.empty')}</Empty>
      ) : (
        <Sorted rows={loaded.value.data} by={{ raised_at: (d) => sortableCell(d.raised_at), brand_name: (d) => sortableCell(d.brand_name), category: (d) => sortableCell(d.category), status: (d) => sortableCell(d.status), resolution: (d) => sortableCell(d.resolution) }}>
          {(ordered, sort) => (
        <TableScroll>
          <table>
            <caption>{t('disputes.section.list')}</caption>
            <thead>
              <tr>
                <SortTh sort={sort} col="raised_at">{t('col.raised')}</SortTh>
                <SortTh sort={sort} col="brand_name">{t('col.brand')}</SortTh>
                <SortTh sort={sort} col="category">{t('col.about')}</SortTh>
                <SortTh sort={sort} col="status">{t('col.status')}</SortTh>
                <SortTh sort={sort} col="resolution">{t('col.whatWeWereTold')}</SortTh>
              </tr>
            </thead>
            <tbody>
              {ordered.map((d) => (
                <tr
                  key={d.id}
                  className="row-clickable"
                  onClick={() => setViewingId(d.id)}
                  // Item 11 — the kit's shared Enter/Space handler, in place
                  // of a hand-rolled one that did the same thing.
                  {...rowOpen(() => setViewingId(d.id))}
                >
                  <td>{formatDate(d.raised_at)}</td>
                  <td>{d.brand_name ?? '—'}</td>
                  <td>
                    {disputeWords(d.category)}
                    {d.evidence.length > 0 ? (
                      <div className="faint">{t('disputes.attachedCount', { n: d.evidence.length })}</div>
                    ) : null}
                  </td>
                  <td>{disputeWords(d.status)}</td>
                  <td>{d.resolution ?? t('disputes.notAnswered')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
          )}
        </Sorted>
      )}
      <Callout>{loaded.value.note}</Callout>
            </>
          ) : null}

          {section === 'detail' ? (
            <DisputeDetail api={api} id={selectedId} onIdChange={setSelectedId} />
          ) : null}

      {viewingId ? (
        <Modal
          title={t('disputes.detail.panel')}
          wide
          onClose={() => {
            setViewingId(null);
            // A reply or a reopen from inside the popup changed the row it
            // was opened from.
            setVersion((v) => v + 1);
          }}
        >
          <DisputeDetailBody api={api} id={viewingId} />
        </Modal>
      ) : null}
    </ScreenWithSections>
  );
}

/**
 * Item 14 — the full back-and-forth, not just the current line: raised →
 * under review → [needs-info exchange] → accepted/declined/resolved
 * [→ reopened → closed], each entry with who, when, why. Item 13's reopen
 * and the NEEDS_INFO reply both live here too, since both only make sense
 * once the affiliate is already looking at the one dispute they apply to.
 */
function DisputeDetail({
  api,
  id,
  onIdChange,
}: {
  api: AffiliateApi;
  id: string;
  onIdChange: (id: string) => void;
}) {
  const [draftId, setDraftId] = useState(id);
  useEffect(() => setDraftId(id), [id]);

  return (
    <Panel title={t('disputes.detail.panel')}>
      <div className="form">
        <Field label={t('disputes.field.id')}>
          <div className="form-actions">
            <TextInput value={draftId} onChange={setDraftId} />
            <button className="primary" disabled={!draftId.trim()} onClick={() => onIdChange(draftId.trim())}>
              {t('disputes.open')}
            </button>
          </div>
        </Field>
      </div>
      {id ? <DisputeDetailBody api={api} id={id} /> : null}
    </Panel>
  );
}

/**
 * §33.4 — one dispute, whole: what the affiliate said and attached, what the
 * operator decided and the proof behind it, and the back-and-forth in order.
 * The popup a row opens and the lookup by id both show this.
 */
function DisputeDetailBody({ api, id }: { api: AffiliateApi; id: string }) {
  const [version, setVersion] = useState(0);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [reopenReason, setReopenReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const detail = useLoad(() => api.dispute(id), [id, version]);
  const timeline = useLoad(() => api.disputeTimeline(id), [id, version]);

  return (
      <div className="form">
        {failure ? <Failure error={failure} /> : null}
        {detail.state === 'loading' ? <Loading /> : null}
        {detail.state === 'error' ? <Failure error={detail.error} /> : null}

        {detail.state === 'ready' ? (
          <>
            <Callout>{disputeDetailLine(detail.value).summary}</Callout>

            <Facts
              items={[
                { label: t('col.brand'), value: detail.value.brand_name ?? '—' },
                { label: t('col.about'), value: disputeWords(detail.value.category) },
                {
                  label: t('col.status'),
                  // Item 4 — the status as a Chip with the tone table; OPEN
                  // (this endpoint's word for a just-raised dispute) now maps
                  // the same as the list's RAISED instead of showing raw.
                  value: <Chip tone={disputeStatusTone(detail.value.status)}>{disputeWords(detail.value.status)}</Chip>,
                },
                { label: t('col.raised'), value: formatDate(detail.value.raised_at) },
              ]}
            />

            <dl className="offer-details">
              <dt>{t('disputes.whatYouSaid')}</dt>
              <dd>{detail.value.what_happened}</dd>
            </dl>

            {detail.value.raised_context ? <DisputeContextPanel context={detail.value.raised_context} /> : null}

            {detail.value.evidence.length > 0 ? (
              <Field group label={t('disputes.files')}>
                <EvidenceList
                  items={detail.value.evidence}
                  onOpen={(e) => api.openDisputeEvidence(id, e.id)}
                  who={(e) =>
                    e.uploaded_by_role === 'AFFILIATE' ? t('disputes.file.fromYou') : t('disputes.file.fromOperator')}
                />
              </Field>
            ) : null}

            {detail.value.resolution_summary ? (
              <dl className="offer-details">
                <dt>{t('disputes.foundSummary')}</dt>
                <dd>{detail.value.resolution_summary}</dd>
                {/* Only an http(s) link becomes one: the operator typed it,
                    and anything else would run on this portal's origin. */}
                {detail.value.resolution_attachment_uri
                  && /^https?:\/\//i.test(detail.value.resolution_attachment_uri) ? (
                  <>
                    <dt>{t('disputes.proof')}</dt>
                    <dd>
                      <a href={detail.value.resolution_attachment_uri} target="_blank" rel="noreferrer">
                        {t('disputes.viewAttachment')}
                      </a>
                    </dd>
                  </>
                ) : null}
              </dl>
            ) : null}

            {detail.value.status === 'NEEDS_INFO' ? (
              <>
                <Field label={t('disputes.reply')}>
                  <textarea
                    className="control"
                    rows={3}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                </Field>
                <Field group label={t('disputes.replyFiles')}>
                  <AttachmentPicker files={replyFiles} onChange={setReplyFiles} />
                </Field>
                <div className="form-actions">
                  <button
                    className="primary"
                    disabled={busy || !replyText.trim()}
                    onClick={() => {
                      setBusy(true);
                      setFailure(null);
                      (replyFiles.length > 0 ? toAttachmentUploads(replyFiles) : Promise.resolve(undefined))
                        .then((attachments) => api.replyToDispute(id, replyText.trim(), attachments))
                        .then(() => {
                          setReplyText('');
                          setReplyFiles([]);
                          setVersion((v) => v + 1);
                        })
                        .catch((e: unknown) => setFailure(e))
                        .finally(() => setBusy(false));
                    }}
                  >
                    {busy ? t('disputes.sending') : t('disputes.sendReply')}
                  </button>
                </div>
              </>
            ) : null}

            {/* Item 13 — a decision the affiliate does not accept, reopened once. */}
            {/* Once only: a decision the operator changed after the reopen is
                still the answer to it, and the server refuses a second. */}
            {['ACCEPTED', 'DECLINED', 'RESOLVED'].includes(detail.value.status) && !detail.value.reopened ? (
              <>
                <Field label={t('disputes.reopenPrompt')}>
                  <textarea
                    className="control"
                    rows={3}
                    value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                  />
                </Field>
                <div className="form-actions">
                  <button
                    className="copy"
                    disabled={busy || !reopenReason.trim()}
                    onClick={() => {
                      setBusy(true);
                      setFailure(null);
                      api
                        .reopenDispute(id, reopenReason.trim())
                        .then(() => {
                          setReopenReason('');
                          setVersion((v) => v + 1);
                        })
                        .catch((e: unknown) => setFailure(e))
                        .finally(() => setBusy(false));
                    }}
                  >
                    {busy ? t('disputes.reopening') : t('disputes.reopen')}
                  </button>
                </div>
              </>
            ) : null}

            {/*
              A timeline is a sequence, not a set of comparable rows. As a
              four-column table the reader scanned down "When" to find the
              order, and the reason — the only part with anything in it — was
              a cell squeezed between two short ones. Each step is a card:
              what happened, in the size it deserves, with when and who under
              it, and the reason in full underneath rather than clipped.
            */}
            {timeline.state === 'ready' && timeline.value.data.length > 0 ? (
              <section className="timeline" aria-label={t('disputes.timeline')}>
                <h3 className="timeline-head">{t('disputes.timeline')}</h3>
                <ol className="timeline-steps">
                  {timeline.value.data.map((row, i) => (
                    <li className="timeline-step" key={`${row.at}-${i}`}>
                      <p className="timeline-what">{disputeWords(row.to_status ?? row.action)}</p>
                      <p className="timeline-meta">
                        {formatDateTime(row.at)}
                        {' · '}
                        {row.by_role === 'AFFILIATE' ? t('disputes.by.you') : t('disputes.by.operator')}
                      </p>
                      {row.reason ? <p className="timeline-why">{row.reason}</p> : null}
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}
          </>
        ) : null}
      </div>
  );
}

function History({ api }: ScreenProps) {
  const loaded = useLoad(() => api.history(), []);
  // Item 1 — the screen had no page title, only a table caption floating
  // above it; the head names the screen the way every other tab's does.
  const head = <div className="page-head"><h1>{t('nav.history')}</h1></div>;
  if (loaded.state === 'loading') return <>{head}<Loading /></>;
  if (loaded.state === 'error') return <>{head}<Failure error={loaded.error} /></>;

  const rows = loaded.value.data;
  if (rows.length === 0) {
    return <>{head}<Empty>{t('history.empty')}</Empty></>;
  }

  return (
    <div className="stack">
      {head}
      {/* The caption's own words move into the panel title; a table inside a
          titled panel does not need to repeat the title as a caption. */}
      <Panel title={t('history.caption')}>
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('col.date')}</th>
                <th scope="col">{t('col.whatHappened')}</th>
                <th scope="col">{t('col.reasonGiven')}</th>
                <th scope="col">{t('col.by')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const line = historyLine(r);
                return (
                  <tr key={`${r.at}-${i}`}>
                    <td>{line.when}</td>
                    <td>{line.what}</td>
                    <td>{line.why}</td>
                    <td className="faint">{line.by}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
        {/* §42.7a — said out loud, because one login may cover several
            programmes and a timeline that silently covered one would be read
            as covering all of them. */}
        <Callout>{loaded.value.note}</Callout>
      </Panel>
    </div>
  );
}

function Statements({ api, range }: ScreenProps) {
  const loaded = useLoad(() => api.statements(range.brand_id), [range.brand_id]);
  // §39.13 — the sampled-away count belongs to a period, so it is read for the
  // period somebody opens rather than for every row in the table.
  const [openPeriod, setOpenPeriod] = useState<string | null>(null);
  const detail = useLoad(
    () => (openPeriod ? api.statement(openPeriod) : Promise.resolve(null)),
    [openPeriod],
  );
  // §24.2b — the period's first deposits that earned no CPA. Read beside the
  // statement rather than inside it: the statement is frozen at issue, and a
  // verdict on a player still inside their time to qualify is not.
  const outcomes = useLoad(
    () => (openPeriod ? api.statementQualification(openPeriod) : Promise.resolve(null)),
    [openPeriod],
  );
  // Item 1 — the same page-head every other tab shows; the table's caption
  // used to be the only thing naming this screen.
  const head = <div className="page-head"><h1>{t('nav.statements')}</h1></div>;
  if (loaded.state === 'loading') return <>{head}<Loading /></>;
  if (loaded.state === 'error') return <>{head}<Failure error={loaded.error} /></>;

  const rows = loaded.value.data;
  if (rows.length === 0) {
    return (
      <>{head}<Empty>{t('statements.noneIssued')}</Empty></>
    );
  }

  // §21.7f — a statement is not restated. Every figure below is the amount
  // this statement was billed in, and the note appears only for a reader whose
  // reports are being converted, because they are the only one for whom the
  // difference is visible.
  const billingNote = billingCurrencyNote();

  return (
    <div className="stack">
      {head}
      {billingNote ? <p className="currency-view-note">{billingNote}</p> : null}
      <Panel title={t('statements.caption')}>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">{t('col.period')}</th>
              <th scope="col">{t('col.statement')}</th>
              <th scope="col" className="num">{t('col.earned')}</th>
              <th scope="col" className="num">{t('col.held')}</th>
              <th scope="col" className="num">{t('col.payable')}</th>
              <th scope="col" className="num">{t('col.carriedForward')}</th>
              <th scope="col">{t('col.invoice')}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{t('statements.periodRange', {
                  from: formatDay(periodDays(r.period_from, r.period_to).first),
                  to: formatDay(periodDays(r.period_from, r.period_to).last),
                })}</td>
                <td>
                  {/* The number on one line, its state on the next: "ST-2026-000078Issued"
                      read as one token. */}
                  <span className="cell-stack">
                    <span>{r.statement_no}</span>
                    <span className="figure-note">{statementRow(r).version}</span>
                  </span>
                </td>
                <td className="num">{statementRow(r).earned}</td>
                <td className="num">{statementRow(r).held}</td>
                <td className="num">{statementRow(r).payable}</td>
                <td className="num">{statementRow(r).carriedForward}</td>
                <td>{statementRow(r).invoiceNo}</td>
                <td className="num">
                  <button
                    className="link-button"
                    onClick={() => setOpenPeriod(r.period_id)}
                  >
                    {t('statements.details')}
                  </button>
                  {' '}
                  <button
                    className="copy"
                    onClick={() => {
                      void api.openInNewTab(`/v1/statements/${r.period_id}.pdf`, {});
                    }}
                  >
                    {t('statements.pdf')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      </Panel>

      {/*
        §39.13 — what sampling did to this period, if anything. A conversion
        that was sampled away qualified and was not paid, which is a different
        thing from one that failed to qualify; saying so here is what stops the
        affiliate reconciling their own conversion count against their
        commission and finding a gap nobody will explain.
      */}
      {openPeriod ? (
        <Modal
          title={(() => {
            const r = rows.find((x) => x.period_id === openPeriod);
            return r ? `${r.statement_no} · ${statementRow(r).period}` : t('statements.details');
          })()}
          onClose={() => setOpenPeriod(null)}
          wide
        >
          {(() => {
            const r = rows.find((x) => x.period_id === openPeriod);
            if (!r) return null;
            const head = statementRow(r);
            // Item 13 — the kit's Figure card: the smaller currency code and
            // the hero treatment on Payable, the figure the affiliate opens
            // the statement to check. None of these are provisional — a
            // statement is the issued record, not a still-open period.
            return (
              <div className="figures">
                <Figure view={{ label: t('col.earned'), value: head.earned, provisional: false }} />
                <Figure view={{ label: t('col.held'), value: head.held, provisional: false }} />
                <Figure view={{ label: t('col.payable'), value: head.payable, provisional: false }} emphasis="hero" />
                <Figure view={{ label: t('col.carriedForward'), value: head.carriedForward, provisional: false }} />
              </div>
            );
          })()}
          {detail.state === 'loading' ? <Loading rows={3} /> : null}
          {detail.state === 'error' ? <Failure error={detail.error} /> : null}
          {detail.state === 'ready' && detail.value ? (
            (() => {
              const lines = statementLineRows(detail.value.lines);
              const sampled = periodSampling(detail.value.sampling);
              const st = detail.value.statement;
              const broughtForward = statementBroughtForwardAmount(st);
              return (
                <>
                  {/* §30.2a — earned − held = payable reads straight across the
                      figures above; this says how much of earned is older. */}
                  {broughtForward ? (
                    <Callout>
                      {t('statements.broughtForward', { amount: broughtForward })}
                    </Callout>
                  ) : null}
                  {/* §30.4e — issued without the minimum, and said so. */}
                  {st['minimum_payout_unchecked'] ? (
                    <Callout>{t('statements.minimumNotChecked')}</Callout>
                  ) : null}
                  {/* §30.2, §33.4a — deal by deal, from the brand's figures to
                      the payable, so the affiliate can redo every step. */}
                  {(() => {
                    const calc = statementCalculation(
                      st,
                      detail.value.lines,
                      detail.value.deals ?? [],
                      outcomes.state === 'ready'
                        ? outcomes.value
                        : outcomes.state === 'error' ? 'unavailable' : null,
                    );
                    return (
                      <Panel title={t('calc.panel')}>
                        <Callout>{t('calc.hint')}</Callout>
                        {calc.deals.map((deal) => (
                          <section key={deal.key}>
                            <h3 className="offer-title">{deal.title}</h3>
                            {deal.parts.map((part) => (
                              <div key={part.key}>
                                <h4>{part.title}</h4>
                                {part.note ? <p className="figure-note">{part.note}</p> : null}
                                {part.rows.length > 0 ? <CalculationTable rows={part.rows} /> : null}
                              </div>
                            ))}
                            <CalculationTable rows={[deal.total]} />
                          </section>
                        ))}
                        <h3 className="offer-title">{t('calc.recon.title')}</h3>
                        <CalculationTable rows={calc.reconciliation.rows} />
                        {calc.reconciliation.mismatch ? <Callout>{calc.reconciliation.mismatch}</Callout> : null}
                        {calc.outcomesNote ? <p className="figure-note">{calc.outcomesNote}</p> : null}
                      </Panel>
                    );
                  })()}
                  {/* §30.2 — the lines add up to Earned; this is where the
                      affiliate checks the total against their own count. */}
                  <Panel title={t('statements.breakdown')} aside={<span className="count count-badge">{lines.length}</span>}>
                    <Callout>{t('statements.breakdownHint')}</Callout>
                    {lines.length === 0 ? (
                      <Empty>{t('statements.noLines')}</Empty>
                    ) : (
                      <Sorted rows={lines} by={{ player: (l) => sortableCell(l.player), campaign: (l) => sortableCell(l.campaign), link: (l) => sortableCell(l.link), kind: (l) => sortableCell(l.kind), how: (l) => sortableCell(l.how), amount: (l) => sortableCell(l.amount), state: (l) => sortableCell(l.state) }}>
                        {(ordered, sort) => (
                      <TableScroll>
                        <table>
                          <thead>
                            <tr>
                              <SortTh sort={sort} col="player">{t('col.player')}</SortTh>
                              <SortTh sort={sort} col="campaign">{t('col.campaign')}</SortTh>
                              <SortTh sort={sort} col="link">{t('col.linkTitle')}</SortTh>
                              <SortTh sort={sort} col="kind">{t('col.type')}</SortTh>
                              <SortTh sort={sort} col="how">{t('col.howCounted')}</SortTh>
                              <SortTh sort={sort} col="amount" align="num">{t('col.amount')}</SortTh>
                              <SortTh sort={sort} col="state">{t('col.status')}</SortTh>
                            </tr>
                          </thead>
                          <tbody>
                            {ordered.map((l) => (
                              <tr key={l.key}>
                                <td><code>{l.player}</code></td>
                                <td>{l.campaign}</td>
                                <td>{l.link}</td>
                                <td>{l.kind}</td>
                                <td className="wrap">{l.how}</td>
                                <td className="num money">{l.amount}</td>
                                <td>{l.state}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableScroll>
                        )}
                      </Sorted>
                    )}
                  </Panel>
                  {sampled.rows.length > 0 ? (
                    <Panel title={t('statements.sampledPanel')}>
                      <TableScroll>
                        <table>
                          <thead>
                            <tr>
                              <th scope="col">{t('col.campaign')}</th>
                              <th scope="col" className="num">{t('col.rate')}</th>
                              <th scope="col" className="num">{t('col.conversions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sampled.rows.map((sr) => (
                              <tr key={`${sr.campaign}-${sr.rate}`}>
                                <td>{sr.campaign}</td>
                                <td className="num">{sr.rate}</td>
                                <td className="num">{formatCount(sr.sampledAway)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableScroll>
                      <Callout>{sampled.note}</Callout>
                    </Panel>
                  ) : (
                    <Callout>{sampled.note}</Callout>
                  )}
                  <div className="form-actions">
                    <button
                      className="primary"
                      onClick={() => { void api.openInNewTab(`/v1/statements/${openPeriod}.pdf`, {}); }}
                    >
                      {t('statements.pdf')}
                    </button>
                  </div>
                </>
              );
            })()
          ) : null}
        </Modal>
      ) : null}

      <Callout>{loaded.value.note}</Callout>
    </div>
  );
}

/**
 * One block of the statement's calculation: the step, the figure, and for a
 * deduction the running balance after it (§20.4b), so the order the terms
 * apply them in is visible rather than implied.
 */
function CalculationTable({ rows }: { rows: readonly CalculationRow[] }) {
  return (
    <TableScroll>
      <table>
        {rows.some((r) => r.balance) ? (
          <thead>
            <tr>
              <th scope="col" />
              <th scope="col" className="num">{t('col.amount')}</th>
              <th scope="col" className="num">{t('calc.col.runningTotal')}</th>
            </tr>
          </thead>
        ) : null}
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row.label}-${i}`}>
              <td className={row.tone === 'sub' ? 'faint' : undefined}>
                {row.tone === 'subtotal' || row.tone === 'total' ? <strong>{row.label}</strong> : row.label}
                {row.detail ? <div className="figure-note wrap">{row.detail}</div> : null}
              </td>
              <td className="num money">
                {row.tone === 'subtotal' || row.tone === 'total' ? <strong>{row.value}</strong> : row.value}
              </td>
              <td className="num money faint">{row.balance ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

function Payments({ api }: ScreenProps) {
  // One list, one screen. The billing documents an affiliate is paid against
  // are the invoices; a second table headed "Statements" showed the same
  // invoices with their payment status and nothing else, and read as
  // statements that were missing (an affiliate below the minimum payout has
  // statements but no invoice). The payment status sits on the invoice row
  // itself (§31.2b), and a screen with one list needs no section menu.
  const history = useLoad(() => api.payments(), []);
  const invoices = useLoad(() => api.invoices(), []);
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);
  const detail = useLoad(
    () => (openInvoice ? api.invoice(openInvoice) : Promise.resolve(null)),
    [openInvoice],
  );
  if (history.state === 'loading') return <Loading />;
  if (history.state === 'error') return <Failure error={history.error} />;

  const view = payments(history.value.data ?? []);
  // §31.2b — due date, days outstanding and the operator's mark, keyed by the
  // document they belong to, so each invoice row can carry its own.
  const paymentOf = new Map(view.rows.map((r) => [r.documentNo, r] as const));
  // §21.7f — neither the statement amounts nor the invoices below are
  // converted: each is the amount its document is paid against.
  const billingNote = billingCurrencyNote();

  return (
    <>
      <div className="page-head"><h1>{t('payments.title')}</h1></div>
      {billingNote ? <p className="currency-view-note">{billingNote}</p> : null}
      <Panel title={t('payments.title')}>
        {invoices.state === 'loading' ? <Loading rows={2} /> : null}
        {invoices.state === 'error' ? <Failure error={invoices.error} /> : null}
        {invoices.state === 'ready' && invoices.value.data.length === 0 ? (
          <Empty>{t('payments.invoices.empty')}</Empty>
        ) : null}
        {invoices.state === 'ready' && invoices.value.data.length > 0 ? (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('col.invoice')}</th>
                  <th className="num">{t('col.total')}</th>
                  <th className="num">{t('col.settled')}</th>
                  <th>{t('col.due')}</th>
                  <th>{t('col.status')}</th>
                  <th>{t('payments.col.payment')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.value.data.map((row) => {
                  const line = invoiceRow(row);
                  return (
                    <tr key={row.id}>
                      <td>{line.number}</td>
                      <td className="num money">{line.total}</td>
                      <td className="num money">{line.settled}</td>
                      <td className="num faint">{line.due}</td>
                      {/* Item 4 — the raw invoice status (ISSUED,
                          PARTIALLY_SETTLED…) through the word helper, as a
                          Chip with the tone table. */}
                      <td><Chip tone={invoiceStatusTone(line.status)}>{invoiceStatusWords(line.status)}</Chip></td>
                      <td>
                        {(() => {
                          const pay = paymentOf.get(line.number);
                          if (!pay) return <span className="faint">—</span>;
                          return (
                            <>
                              {/* §31.1a — never the bare word "Paid". The status names
                                  who claimed it, because the platform did not see it. */}
                              {pay.overdue
                                ? <Chip tone="overdue">{pay.status}</Chip>
                                : pay.caveat
                                  ? <Chip tone="neutral">{pay.status}</Chip>
                                  : <Chip tone="provisional">{pay.status}</Chip>}
                              {pay.caveat ? <div className="wrap">{pay.caveat}</div> : null}
                            </>
                          );
                        })()}
                      </td>
                      <td className="num">
                        <button
                          className="copy"
                          onClick={() => setOpenInvoice(row.id)}
                        >
                          {t('payments.detail')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        ) : null}
      </Panel>

      {openInvoice ? (
        <Modal
          title={
            detail.state === 'ready' && detail.value
              ? t('invoice.modal', { number: invoiceDetailView(detail.value).number })
              : t('invoice.modal.plain')
          }
          onClose={() => setOpenInvoice(null)}
        >
          {detail.state === 'loading' ? <Loading rows={2} /> : null}
          {detail.state === 'error' ? <Failure error={detail.error} /> : null}
          {detail.state === 'ready' && detail.value ? (
            <>
              {/* Item 13 — the kit's Figure card, for the smaller currency
                  code; neither figure is provisional (an issued invoice is
                  the billed record, not a still-open period). */}
              <div className="figures">
                <Figure view={{ label: t('col.total'), value: invoiceDetailView(detail.value).total, provisional: false }} />
                <Figure view={{ label: t('col.settled'), value: invoiceDetailView(detail.value).settled, provisional: false }} />
              </div>
              <Facts
                items={[{
                  label: t('col.status'),
                  // Item 4 — the raw status code (ISSUED, PARTIALLY_SETTLED…)
                  // through the same word helper the invoice list now uses,
                  // shown as a Chip rather than plain text.
                  value: <Chip tone={invoiceStatusTone(invoiceDetailView(detail.value).status)}>{invoiceStatusWords(invoiceDetailView(detail.value).status)}</Chip>,
                }]}
              />
              <Callout>
                {t('invoice.issuedDue', { issued: invoiceDetailView(detail.value).issued, due: invoiceDetailView(detail.value).due })}
              </Callout>
            </>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Players
 * ------------------------------------------------------------------ */

/*
 * The three things a matrix cell can hold — the same choice the operator
 * console offers, because it is the same report. Retention answers "did they
 * stay"; the other two answer "what were they worth". A cell holding all
 * three is unreadable at twelve cohorts by seven ages.
 */
const COHORT_CELL_METRICS = [
  { value: 'retention', labelKey: 'cohort.stillActive' },
  { value: 'revenue', labelKey: 'cohort.revenuePerPlayer' },
  { value: 'deposits', labelKey: 'cohort.depositsPerPlayer' },
] as const;
type CohortCellMetric = (typeof COHORT_CELL_METRICS)[number]['value'];

/**
 * §17.8b/§35.3 — the by-cohort report as its own screen.
 *
 * Its own loader rather than a branch of the Players one: that load exists to
 * try per-player reporting and fall back when it is refused, and hanging a
 * second report off the failure path would make this screen's data depend on
 * a permission it does not need.
 */
function CohortAnalysisPanel(
  { api, range, now, me }: Pick<ScreenProps, 'api' | 'range' | 'now' | 'me'>,
) {
  // §21.7 — this report's own view while the affiliate is on it, the same
  // component state the cohort table beside it keeps.
  const [currencyView, setCurrencyView] = useState<CurrencyView>('CONVERTED');
  const [metric, setMetric] = useState<CohortCellMetric>('retention');
  const loaded = useLoad(
    () =>
      api.cohortAnalysis({
        ...range,
        ...(currencyView === 'ORIGINAL' ? { display_currency: DISPLAY_CURRENCY_ORIGINAL } : {}),
      }),
    [range.from, range.to, range.brand_id, currencyView],
  );
  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const view = cohortAnalysis(loaded.value, now, {
    view: currencyView,
    chosen: me.display_currency,
    operatorDefault: me.operator_default_display_currency,
  });

  const cellOf = (cell: (typeof view.matrix)[number]['cells'][number]) =>
    metric === 'retention'
      ? cell.retention
      : metric === 'revenue'
        ? cell.ggrPerPlayer
        : cell.depositsPerPlayer;

  /*
   * Item 6 — the age-below matrix already says "—" for a checkpoint that has
   * not been reached (`assessable`); the totals table's own retention column
   * has no such flag and read the API's literal 0% for the running month as
   * a real zero. It is not one: nobody registered this month has had a later
   * month to come back in yet, so there is nothing to measure, the same
   * absence the matrix marks — this just marks it on the one column above
   * that carries the same number without that flag.
   */
  const currentMonth = now.toISOString().slice(0, 7);

  // §21.7b — one mark for the table's money columns, on the headers rather
  // than in every cell.
  const mark = view.convertedMark ? (
    <span className={`figure-converted${view.convertedMixed ? ' is-mixed' : ''}`}>
      {view.convertedMark}
    </span>
  ) : null;

  return (
    <>
      <Panel title={t('cohort.totals')} aside={<span className="updated">{view.updated}</span>}>
        <CurrencyViewSwitch control={view.currencyView} onView={setCurrencyView} />
        {/* The figures that are absent rather than zero, said once, above them. */}
        {view.unavailableBecause ? <Callout>{view.unavailableBecause}</Callout> : null}
        {view.rows.length === 0 ? (
          <Empty>{view.empty}</Empty>
        ) : (
          <Sorted rows={view.rows} by={{ month: (r) => sortableCell(r.month), players: (r) => sortableCell(r.players), ftds: (r) => sortableCell(r.ftds), ftdRate: (r) => sortableCell(r.ftdRate), deposits: (r) => sortableCell(r.deposits), withdrawals: (r) => sortableCell(r.withdrawals), ggr: (r) => sortableCell(r.ggr), earned: (r) => sortableCell(r.earned) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="month">{t('col.cohort')}</SortTh>
                  <SortTh sort={sort} col="players" align="num">{t('col.players')}</SortTh>
                  <SortTh sort={sort} col="ftds" align="num">{t('col.firstDeposits')}</SortTh>
                  <SortTh sort={sort} col="ftdRate" align="num">{t('cohort.ftdRate')}</SortTh>
                  <SortTh sort={sort} col="month" align="num">{t('cohort.retention')}</SortTh>
                  <SortTh sort={sort} col="deposits" align="num">{t('col.deposits')}{mark}</SortTh>
                  <SortTh sort={sort} col="withdrawals" align="num">{t('col.withdrawals')}{mark}</SortTh>
                  <SortTh sort={sort} col="ggr" align="num">{t('col.grossRevenue')}{mark}</SortTh>
                  <SortTh sort={sort} col="earned" align="num">{t('cohort.earned')}{mark}</SortTh>
                </tr>
              </thead>
              <tbody>
                {ordered.map((r) => (
                  <tr key={`${r.month}-${r.currency}`}>
                    <td className="num faint">{r.month}</td>
                    <td className="num">{r.players === null ? r.withheld : formatCount(r.players)}</td>
                    <td className="num">{r.ftds === null ? r.withheld : formatCount(r.ftds)}</td>
                    <td className="num">{r.ftdRate}</td>
                    <td className="num">{r.month === currentMonth ? '—' : r.retention}</td>
                    <td className="num money faint">{r.deposits}</td>
                    <td className="num money">{r.withdrawals}</td>
                    <td className="num money">{r.ggr}</td>
                    <td className="num money">{r.earned}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
        )}
        <Callout>{t('cohort.retentionWhy')}</Callout>
        {/* §16.4 — RevShare is priced for a period, not for a named player, so
            it is reported in full beside the table rather than apportioned
            across cohorts by size. */}
        {view.outsideCohortsBecause ? (
          <Callout>
            {t('cohort.outside')}: {view.outsideCohorts.join(', ')}. {view.outsideCohortsBecause}
          </Callout>
        ) : null}
        <ConversionNotes view={view.conversions} />
      </Panel>

      <Panel title={t('cohort.byAge')}>
        <Callout>{t('cohort.byAgeWhy')}</Callout>
        {/* Item 6 — a row of identical `.copy` buttons with an `aria-current`
            nothing styled, so the chosen metric looked like the other two;
            the kit's segmented control shows which one is selected. */}
        <TabBar aria-label={t('cohort.show')}>
          {COHORT_CELL_METRICS.map((m) => (
            <Tab key={m.value} selected={metric === m.value} onClick={() => setMetric(m.value)}>
              {t(m.labelKey)}
            </Tab>
          ))}
        </TabBar>
        {view.matrix.length === 0 ? (
          <Empty>{t('players.cohorts.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('col.cohort')}</th>
                  <th className="num">{t('col.players')}</th>
                  {view.checkpointsMonths.map((m) => (
                    <th key={m} className="num">{t('cohort.monthShort', { n: m })}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.matrix.map((r) => (
                  <tr key={`${r.month}-${r.currency}`}>
                    <td className="num faint">{r.month}</td>
                    <td className="num">{r.players === null ? r.withheld : formatCount(r.players)}</td>
                    {r.cells.map((c) => (
                      <td
                        key={c.monthsSince}
                        className={c.assessable ? 'num' : 'num faint'}
                        // §35.4a — the reason the cell is blank, on the cell.
                        // A dash with nothing behind it is read as a zero.
                        {...(c.assessable ? {} : { title: t('cohort.notAssessable') })}
                      >
                        {c.assessable ? cellOf(c) : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
    </>
  );
}

/**
 * §17.8b (R2) — the two cohort reports, asked for in the one shape the
 * server will answer: when a brand protects small cohorts, no brand filter
 * and no split, with the reason said once, on the first of them.
 */
function CohortSection({ api, range, now, me }: Pick<ScreenProps, 'api' | 'range' | 'now' | 'me'>) {
  const privacy = useLoad(() => api.cohortPrivacy().catch(() => null), []);
  if (privacy.state === 'loading') return <Loading />;
  const known = privacy.state === 'ready' ? privacy.value : null;
  return (
    <>
      <AffiliateCohortPanel api={api} range={range} now={now} privacy={known} />
      <CohortAnalysisPanel api={api} range={cohortRange(range, known)} now={now} me={me} />
    </>
  );
}

type PlayersSection = 'qualification' | 'activity' | 'cohortAnalysis' | 'quarantine';

/*
 * On the source row, never the view's: a view row holds "2,500.00 NGN", and
 * ordering that as text puts 10 before 9.
 */
const AFF_DETAILED_ACTIVITY_SORT = {
  day: (r: { day: string }) => r.day,
  clicks: (r: { clicks: number }) => r.clicks,
  impressions: (r: { impressions: number }) => r.impressions,
  signups: (r: { signups: number }) => r.signups,
  ftds: (r: { ftd_count: number }) => r.ftd_count,
  deposits: (r: { deposits_minor: string }) => Number(r.deposits_minor),
  withdrawals: (r: { withdrawals_minor: string }) => Number(r.withdrawals_minor),
  chargebacks: (r: { chargebacks_minor: string }) => Number(r.chargebacks_minor),
  netDeposits: (r: { adjusted_net_deposits_minor: string }) => Number(r.adjusted_net_deposits_minor),
  income: (r: { commission_minor: string }) => Number(r.commission_minor),
} as const;

function DetailedActivityPanel({ api, range, now }: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  now: Date;
}) {
  const report = useLoad(() => api.detailedActivity(range), [range.from, range.to, range.brand_id]);
  const { rows: ordered, sort } = useSort(
    report.state === 'ready' ? report.value.data : [],
    AFF_DETAILED_ACTIVITY_SORT,
    { key: 'day' },
  );
  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') return <Failure error={report.error} />;
  const view = detailedActivityView({ ...report.value, data: ordered }, now);
  return (
    <Panel title={t('detailedActivity.title')} aside={<span className="updated">{view.updated}</span>}>
      <p className="link-note">{t('detailedActivity.lead')}</p>
      {/* Said only where it is true: one currency on screen needs no warning. */}
      {view.currencyNote ? <Callout>{view.currencyNote}</Callout> : null}
      {view.rows.length === 0 ? (
        <Empty>{t('detailedActivity.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <SortTh sort={sort} col="day" align="date">{t('col.when')}</SortTh>
                <th>{t('col.currency')}</th>
                <SortTh sort={sort} col="clicks" align="num">{t('col.clicks')}</SortTh>
                <SortTh sort={sort} col="impressions" align="num">{t('col.impressions')}</SortTh>
                <SortTh sort={sort} col="signups" align="num">{t('col.signups')}</SortTh>
                <SortTh sort={sort} col="ftds" align="num">{t('col.ftds')}</SortTh>
                <SortTh sort={sort} col="deposits" align="num">{t('col.deposits')}</SortTh>
                <SortTh sort={sort} col="withdrawals" align="num">{t('col.withdrawals')}</SortTh>
                <SortTh sort={sort} col="chargebacks" align="num">{t('col.chargebacks')}</SortTh>
                <SortTh sort={sort} col="netDeposits" align="num">{t('col.adjustedNetDeposits')}</SortTh>
                <SortTh sort={sort} col="income" align="num">{t('col.income')}</SortTh>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((r) => (
                <tr key={r.key}>
                  <td className="date">{r.day}</td>
                  <td className="faint">{r.currency || '\u2014'}</td>
                  <td className="num">{r.clicks}</td>
                  <td className="num faint">{r.impressions}</td>
                  <td className="num">{r.signups}</td>
                  <td className="num">{r.ftds}</td>
                  <td className="num money">{r.deposits}</td>
                  <td className="num money">{r.withdrawals}</td>
                  <td className="num money">{r.chargebacks}</td>
                  <td className="num money">{r.adjustedNetDeposits}</td>
                  <td className="num money">{r.commission}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

function ClicksImpressionsPanel({ api, range, now }: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  now: Date;
}) {
  const report = useLoad(() => api.clicksImpressions(range), [range.from, range.to, range.brand_id]);
  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') return <Failure error={report.error} />;
  const view = clicksImpressionsView(report.value, now);
  return (
    <Panel title={t('clicksImpressions.title')} aside={<span className="updated">{view.updated}</span>}>
      <p className="link-note">{t('clicksImpressions.lead')}</p>
      {view.rows.length === 0 ? (
        <Empty>{t('clicksImpressions.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th className="date">{t('col.when')}</th>
                {/* A column pair per brand, laid out from the whole range so
                    the table reads down rather than shifting per row. */}
                {view.brands.map((b) => (
                  <Fragment key={b}>
                    <th className="num">{b} · {t('col.impressions')}</th>
                    <th className="num">{b} · {t('col.clicks')}</th>
                    <th className="num">{b} · {t('col.ctr')}</th>
                  </Fragment>
                ))}
                <th className="num">{t('clicksImpressions.totalImpressions')}</th>
                <th className="num">{t('clicksImpressions.totalClicks')}</th>
                <th className="num">{t('clicksImpressions.overallCtr')}</th>
                <th className="num">{t('clicksImpressions.overallReach')}</th>
                <th className="num">{t('col.frequency')}</th>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((r) => (
                <tr key={r.day}>
                  <td className="date">{r.day}</td>
                  {r.perBrand.map((c) => (
                    <Fragment key={c.brand}>
                      <td className="num faint">{c.impressions}</td>
                      <td className="num">{c.clicks}</td>
                      <td className="num faint">{c.ctr}</td>
                    </Fragment>
                  ))}
                  <td className="num faint">{r.totalImpressions}</td>
                  <td className="num">{r.totalClicks}</td>
                  <td className="num">{r.overallCtr}</td>
                  <td className="num faint">{r.overallReach}</td>
                  <td className="num faint">{r.overallFrequency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

/**
 * Every report the console has, grouped the way an affiliate asks for them.
 *
 * Three groups and thirteen entries, which is the shape of the question rather
 * than the shape of our code: "how did the account do" (one report), "who did
 * I bring" (four), "where did the traffic come from" (eight).
 *
 * Nothing here is new data. Every panel below already existed somewhere in
 * this console — the funnel on the dashboard, the click log under Links,
 * quarantine under Players, the pivot under Sources — and stayed where it was,
 * because a link somebody has bookmarked should keep working. This is the
 * index that was missing, not a second copy of the reports.
 */
type ReportsSection =
  | 'detailed'
  | 'customer-activity' | 'customer-daily' | 'customer-registrations' | 'quarantine'
  | 'clicks-impressions' | 'device' | 'media' | 'countries' | 'referrer'
  | 'clicks-log' | 'payload' | 'funnel';

function Reports({ api, range, now, me, filters }: ScreenProps) {
  const [section, setSection] = useState<ReportsSection>('detailed');

  return (
    <ScreenWithSections
      title={t('nav.reports')}
      ariaLabel={t('nav.reports')}
      section={section}
      onSection={(s) => setSection(s as ReportsSection)}
      groups={[
        {
          label: t('reports.group.activity'),
          items: [{ id: 'detailed', label: t('detailedActivity.title') }],
        },
        {
          label: t('reports.group.customer'),
          items: [
            { id: 'customer-activity', label: t('reports.customerActivity') },
            { id: 'customer-daily', label: t('reports.customerDaily') },
            { id: 'customer-registrations', label: t('reports.customerRegistrations') },
            { id: 'quarantine', label: t('quarantine.title') },
          ],
        },
        {
          label: t('reports.group.traffic'),
          items: [
            { id: 'clicks-impressions', label: t('clicksImpressions.title') },
            { id: 'device', label: t('reports.device') },
            { id: 'media', label: t('reports.media') },
            { id: 'countries', label: t('reports.countries') },
            { id: 'referrer', label: t('reports.referrer') },
            { id: 'clicks-log', label: t('reports.clicksLog') },
            { id: 'payload', label: t('reports.payload') },
            { id: 'funnel', label: t('reports.funnel') },
          ],
        },
      ]}
    >
      {filters}

      {section === 'detailed'
        ? <DetailedActivityPanel api={api} range={range} now={now} /> : null}

      {section === 'customer-activity'
        ? <PlayerListPanel api={api} range={range} now={now} registrations={false} /> : null}
      {section === 'customer-registrations'
        ? <PlayerListPanel api={api} range={range} now={now} registrations /> : null}
      {section === 'customer-daily'
        ? <DailyActivity api={api} range={range} now={now} /> : null}
      {section === 'quarantine'
        ? <QuarantinePanel api={api} range={range} now={now} /> : null}

      {section === 'clicks-impressions'
        ? <ClicksImpressionsPanel api={api} range={range} now={now} /> : null}
      {section === 'clicks-log'
        ? <ClickLog api={api} range={range} now={now} /> : null}
      {section === 'funnel'
        ? <FunnelReportPanel api={api} range={range} now={now} /> : null}

      {/*
        The five that are one pivot with the dimension fixed. Written out one
        by one rather than looked up from a map: check-surfaces reads this file
        to prove every tab renders something, and a lookup hides the answer
        from it — which is the same thing as hiding it from a reader.

        Named rather than offered as a dimension picker, because "Countries
        report" is what somebody was told to look at, and making them find
        `geo` in a dropdown is the console asking them to know how it is built.
      */}
      {section === 'device'
        ? <FixedPivotPanel api={api} range={range} now={now} dimension="device" /> : null}
      {section === 'countries'
        ? <FixedPivotPanel api={api} range={range} now={now} dimension="geo" /> : null}
      {/* D3 — the country each row is keyed on comes from DB-IP. */}
      {section === 'countries' ? <IpGeolocationNote /> : null}
      {section === 'referrer'
        ? <FixedPivotPanel api={api} range={range} now={now} dimension="referrer" /> : null}
      {section === 'media'
        ? <FixedPivotPanel api={api} range={range} now={now} dimension="creative" /> : null}
      {section === 'payload'
        ? <FixedPivotPanel api={api} range={range} now={now} dimension="sub_id_1" /> : null}
    </ScreenWithSections>
  );
}

/**
 * The affiliate's own players, as a list.
 *
 * Two reports off one endpoint, because the reference console splits them and
 * the split is real: "customer activity" asks what each player has been worth,
 * "customer registrations" asks where each came from. Same rows, different
 * columns, so one panel with a flag rather than two that drift apart.
 *
 * §17.8b — per-player reporting is off by default and the 404 that says so is
 * not a fault. It reads as the operator's choice, in words, rather than as an
 * error the affiliate might report as a bug. Only that 404 is treated this
 * way; §4.7 — any other refusal is a fault and is shown as one.
 */
function PlayerListPanel({ api, range, now, registrations }: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  now: Date;
  registrations: boolean;
}) {
  const report = useLoad(() => api.players(range), [range.from, range.to, range.brand_id]);
  /*
   * Sorted here rather than by the API: this endpoint hands back the whole
   * page, so ordering it in the browser orders everything the reader can see.
   * Above the guards, because it is a hook.
   */
  const loadedRows = report.state === 'ready' ? report.value.data.data : [];
  /*
   * Ordered on the values the API sent, and formatted afterwards — not the
   * other way round.
   *
   * Sorting the formatted rows sorted the *rendering* of each value: a
   * date-time printed month-first put 2024 above 2026 because the year is
   * compared last, and a twelve-hour clock put 09 PM between 01 and 03. The
   * raw row holds an ISO timestamp, which sorts chronologically, and a
   * minor-unit amount, which sorts as the number it is rather than as text
   * beginning with a currency symbol.
   */
  const { sort } = useSort(loadedRows, playerSortValues, { key: 'registered_at', dir: 'desc' });
  const playerTotals = playerColumnTotals(loadedRows);
  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') {
    if (readsAsPerPlayerOff(report.error)) {
      return (
        <Panel title={registrations ? t('reports.customerRegistrations') : t('reports.customerActivity')}>
          <Callout>{t('players.cohortOnly')}</Callout>
        </Panel>
      );
    }
    return <Failure error={report.error} />;
  }

  const ordered = playersInOrder(loadedRows, sort.key, sort.dir);
  const rows = report.value.data.data.map((r) => playerColumns(r));
  const acquisition = [
    'join_country', 'referring_url', 'media', 'payload', 'channel', 'brand', 'landing_page', 'campaign_group',
  ];
  const money = [
    'deposits_minor', 'withdrawals_minor', 'turnover_minor',
    'ggr_minor', 'ngr_minor', 'commission_minor', 'clicks',
  ];
  const columns = rows.length === 0
    ? []
    : Object.keys(rows[0]!).filter((c) =>
      registrations ? !money.includes(c) : !acquisition.includes(c));

  return (
    <Panel
      title={registrations ? t('reports.customerRegistrations') : t('reports.customerActivity')}
      aside={<span className="updated">{freshness(report.value.data_as_of, now)}</span>}
    >
      {rows.length === 0 ? (
        <Empty>{t('players.attributed.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                {columns.map((c) => (
                  <SortTh
                    key={c}
                    sort={sort}
                    col={c}
                    {...(/_minor$|_at$|^clicks$/.test(c) ? { align: 'num' as const } : {})}
                  >
                    {t(`col.player.${c}` as AffiliateMessageKey)}
                  </SortTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordered.map((row, i) => (
                <tr key={String(row['player_ref'] ?? i)} className={i % 2 === 1 ? 'row-alt' : ''}>
                  {columns.map((c) => (
                    <td key={c} className={/_minor$|_at$|^clicks$/.test(c) ? 'num' : undefined}>
                      {row[c] === null || row[c] === undefined ? '—' : String(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {/* The reference console totals its customer report, and so does
                this one: a list of forty players whose deposits the reader has
                to add up themselves is a list they will add up wrongly. Only
                the countable columns are totalled — a currency code and a date
                have no sum, and a money total is per currency. */}
            <tfoot>
              <tr>
                {columns.map((c, i) => (
                  <td key={c} className={/_minor$|^clicks$/.test(c) ? 'num' : undefined}>
                    {i === 0 ? t('col.total') : playerTotals[c] ?? ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

/**
 * One dimension of the traffic pivot, under its own name.
 *
 * The same endpoint the Sources screen calls, with the dimension fixed. Its
 * own component rather than reusing `Sources` because that screen owns a
 * dimension picker, a placement filter bar and an export block, and a report
 * called "Countries" that opens with a dropdown reading "Link label" is a
 * report that has not answered its own title.
 */
function FixedPivotPanel({ api, range, now, dimension }: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  now: Date;
  dimension: string;
}) {
  const report = useLoad(
    () => api.performance(dimension, range),
    [dimension, range.from, range.to, range.brand_id],
  );
  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') return <Failure error={report.error} />;
  return <FixedPivotTable view={performance(report.value, now, dimension)} dimension={dimension} />;
}

/*
 * The pivot's table, split from the panel that loads it so the sort hook sits
 * above no conditional return. `FixedPivotPanel` has to guard on the load
 * before it can compute `view`, and a hook after that guard is called on the
 * ready render and not on the loading one — React counts hooks per render, so
 * the panel threw the moment its data arrived. Nothing in the build sees this:
 * there is no lint step, and both orders typecheck.
 */
function FixedPivotTable({ view, dimension }: {
  view: ReturnType<typeof performance>;
  dimension: string;
}) {
  const label = dimensions().find((d) => d.value === dimension)?.label ?? dimension;
  /*
   * A country arrives from `performance()` already as "🇳🇬 Nigeria", flag in
   * front of the name and never instead of it (Windows ships no flag glyphs).
   * Sorting goes by the name, though — the flag is two regional-indicator
   * letters, and ordering by the whole string would order by country code.
   */
  const isGeo = dimension === 'geo';

  const { rows: ordered, sort } = useSort(view.rows, {
    value: (r) => (isGeo && r.geo ? countryName(r.geo) : r.value),
    clicks: (r) => r.counted,
    share: (r) => r.sharePct,
    excluded: (r) => r.excluded,
    reach: (r) => Number(r.reach) || 0,
    frequency: (r) => Number(r.frequency) || 0,
    botShare: (r) => Number(String(r.botShare).replace('%', '')) || 0,
  }, { key: 'clicks' });

  return (
    <Panel
      title={label}
      aside={
        <>
          <TableActions
            title={label}
            headers={[label, t('col.clicks'), t('col.share'), t('col.excluded'),
                      t('col.reach'), t('col.frequency'), t('col.automatedShare')]}
            keys={['value', 'clicks', 'share', 'excluded', 'reach', 'frequency', 'automated_share']}
            rows={ordered.map((r) => [
              r.value, String(r.counted), `${r.sharePct}%`, String(r.excluded),
              String(r.reach), String(r.frequency), String(r.botShare),
            ])}
            filename={`${dimension}-report`}
          />
          <span className="updated">{view.updated}</span>
        </>
      }
    >
      {view.rows.length === 0 ? (
        <Empty>{t('clicksImpressions.empty')}</Empty>
      ) : (
        <>
        {/* Where the traffic came from, before the rows that count it. */}
        {isGeo ? (
          <WorldMap
            values={view.rows.map((r) => [r.geo, r.counted] as const)}
            label={(code, n) => `${geoLabel(code)}: ${t(n === 1 ? 'map.clicks.one' : 'map.clicks.other', { n: formatCount(n) })}`}
            title={t('map.title')}
          />
        ) : null}
        <TableScroll>
          <table className="banded-by-index">
            <thead>
              <tr>
                <SortTh sort={sort} col="value">{label}</SortTh>
                <SortTh sort={sort} col="clicks" align="num">{t('col.clicks')}</SortTh>
                <SortTh sort={sort} col="share">{t('col.share')}</SortTh>
                <SortTh sort={sort} col="excluded" align="num">{t('col.excluded')}</SortTh>
                <SortTh sort={sort} col="reach" align="num">{t('col.reach')}</SortTh>
                <SortTh sort={sort} col="frequency" align="num">{t('col.frequency')}</SortTh>
                <SortTh sort={sort} col="botShare" align="num">{t('col.automatedShare')}</SortTh>
              </tr>
            </thead>
            <tbody>
              {ordered.map((row, i) => (
                <tr key={row.value} className={i % 2 === 1 ? 'row-alt' : ''}>
                  <td>{row.value}</td>
                  <td className="num">{formatCount(row.counted)}</td>
                  <td>
                    <div className="share-cell">
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${row.sharePct}%` }} />
                      </div>
                      <span className="share-pct">{row.sharePct}%</span>
                    </div>
                  </td>
                  <td className="num faint">{row.excluded || '—'}</td>
                  <td className="num faint">{row.reach}</td>
                  <td className="num faint">{row.frequency}</td>
                  <td className="num faint">{row.botShare}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        </>
      )}
      <Callout>{view.unlabelled}</Callout>
    </Panel>
  );
}

/** The funnel's own numbers, day by day. */
function FunnelReportPanel({ api, range, now }: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  now: Date;
}) {
  const report = useLoad(() => api.funnel(range), [range.from, range.to, range.brand_id]);
  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') return <Failure error={report.error} />;
  const rows = report.value.data.map((r) => funnelRow(r));
  return (
    <Panel title={t('reports.funnel')}>
      {rows.length === 0 ? (
        <Empty>{t('clicksImpressions.empty')}</Empty>
      ) : (
        <Sorted rows={rows} by={{ day: (r) => sortableCell(r.day), impressions: (r) => sortableCell(r.impressions), counted: (r) => sortableCell(r.counted), excluded: (r) => sortableCell(r.excluded), registrations: (r) => sortableCell(r.registrations), ftds: (r) => sortableCell(r.ftds) }}>
          {(ordered, sort) => (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <SortTh sort={sort} col="day" align="date">{t('col.when')}</SortTh>
                <SortTh sort={sort} col="impressions" align="num">{t('col.impressions')}</SortTh>
                <SortTh sort={sort} col="counted" align="num">{t('col.clicks')}</SortTh>
                <SortTh sort={sort} col="excluded" align="num">{t('col.excluded')}</SortTh>
                <SortTh sort={sort} col="registrations" align="num">{t('col.registrations')}</SortTh>
                <SortTh sort={sort} col="ftds" align="num">{t('col.ftds')}</SortTh>
              </tr>
            </thead>
            <tbody>
              {ordered.map((r) => (
                <tr key={r.day}>
                  <td className="date">{r.day}</td>
                  <td className="num faint">{r.impressions ?? UNAVAILABLE}</td>
                  <td className="num">{r.counted}</td>
                  <td className="num faint">{r.excluded}</td>
                  <td className="num">{r.registrations}</td>
                  <td className="num">{r.ftds}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
          )}
        </Sorted>
      )}
    </Panel>
  );
}

/**
 * §27.9f — the players held out of revenue share, and how far each has to go.
 *
 * A policy that quietly suppresses revenue is one the affiliate reconciles
 * against their own tracker, fails to match, and disputes. So it is stated:
 * these players, this much, and what has to happen before they count again.
 */
function QuarantinePanel({ api, range, now }: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  now: Date;
}) {
  const report = useLoad(() => api.quarantine(range), [range.from, range.to, range.brand_id]);
  if (report.state === 'loading') return <Loading />;
  if (report.state === 'error') return <Failure error={report.error} />;
  const view = playerQuarantineView(report.value, now);
  return (
    <Panel title={t('quarantine.title')} aside={<span className="updated">{view.updated}</span>}>
      <p className="link-note">{view.lead}</p>
      {view.offBecause ? <Callout>{view.offBecause}</Callout> : null}
      {!view.offBecause && view.rows.length === 0 ? <Empty>{view.empty}</Empty> : null}
      {view.rows.length > 0 ? (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th>{t('quarantine.colPlayer')}</th>
                <th className="num">{t('quarantine.colLifetime')}</th>
                <th className="num">{t('quarantine.colShortfall')}</th>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((r) => (
                <tr key={r.playerRef}>
                  <td>{r.playerRef}</td>
                  <td className="num">{r.lifetime}</td>
                  <td className="num faint">{r.shortfall}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}
    </Panel>
  );
}

function Players({ api, range, now, me }: ScreenProps) {
  // Two questions, two screens: "is this player earning me anything, and if
  // not why" (qualification) and "what did my players do" (the cohort or
  // per-player table). Stacked on one page, the second pushed the first below
  // the fold, and the first is the one an affiliate opens Players for.
  const [section, setSection] = useState<PlayersSection>('qualification');
  /*
   * §21.7 — which of the two views the cohort table is in. Component state,
   * not a saved preference: the saved choice is on the Account panel, and
   * this asks *this* report a different question while the affiliate is on
   * it. Leaving the screen puts it back to what they chose.
   */
  const [currencyView, setCurrencyView] = useState<CurrencyView>('CONVERTED');
  const result = useLoad(
    () =>
      api.players(range).then(
        (report) => ({ ok: true as const, report }),
        async (error: unknown) => {
          /*
           * §4.7 — only the 404 means "per-player reporting is off". Any
           * other refusal is a fault, and falling back on it told the
           * affiliate their operator had chosen cohort-level reporting when
           * the truth was a 500.
           */
          if (!readsAsPerPlayerOff(error)) throw error;
          // §17.8b (R2) — the brand is a filter too while a brand protects small cohorts.
          const privacy: CohortPrivacy | null = await api.cohortPrivacy().catch(() => null);
          const cohortReport = await api
            .cohorts({
              ...cohortRange(range, privacy),
              // §21.7 — the original-currency view is the same report asked a
              // different way, not a different report.
              ...(currencyView === 'ORIGINAL'
                ? { display_currency: DISPLAY_CURRENCY_ORIGINAL }
                : {}),
            })
            .catch(() => null);
          return { ok: false as const, error, cohortReport, fixedReason: cohortFixedReason(privacy) };
        },
      ),
    [range.from, range.to, range.brand_id, currencyView],
  );
  // Item 6 — a version counter so the panel's own retry can re-run this
  // load without touching the filters that drove it.
  const [qualificationVersion, setQualificationVersion] = useState(0);
  const qualification = useLoad(
    () => api.qualification(range),
    [range.from, range.to, range.brand_id, qualificationVersion],
  );

  if (result.state === 'loading') return <Loading />;
  if (result.state === 'error') return <Failure error={result.error} />;

  const loaded = result.value;
  const view = loaded.ok
    ? players(loaded, now)
    : loaded.cohortReport
      ? cohorts(loaded.cohortReport, loaded.error, now, {
          view: currencyView,
          chosen: me.display_currency,
          operatorDefault: me.operator_default_display_currency,
        })
      : players(loaded, now);

  const sections = (body: ReactNode) => (
    <ScreenWithSections
      title={t('players.title')}
      ariaLabel={t('players.sections')}
      section={section}
      onSection={setSection}
      groups={[{
        label: t('players.title'),
        items: [
          { id: 'qualification', label: t('players.section.qualification') },
          { id: 'activity', label: view.kind === 'players' ? t('players.section.attributed') : t('players.section.activity') },
          // §17.8b/§35.3 — the cohort report an affiliate opens on purpose,
          // beside the one they land on when per-player is refused.
          { id: 'cohortAnalysis', label: t('players.section.cohortAnalysis') },
          { id: 'quarantine', label: t('quarantine.title') },
        ],
      }]}
    >
      {section === 'activity' && 'updated' in view ? <span className="updated">{view.updated}</span> : null}
      {section === 'quarantine' ? <QuarantinePanel api={api} range={range} now={now} /> : null}
      {section === 'qualification' ? (
        <QualificationPanel loaded={qualification} onRetry={() => setQualificationVersion((v) => v + 1)} />
      ) : null}
      {section === 'cohortAnalysis' ? (
        <>
          <CohortSection api={api} range={range} now={now} me={me} />
        </>
      ) : null}
      {section === 'activity' ? body : null}
    </ScreenWithSections>
  );

  if (view.kind === 'unavailable') {
    return sections(
      <Callout {...(view.reference ? { reference: view.reference } : {})}>{view.reason}</Callout>,
    );
  }

  if (view.kind === 'cohorts') {
    return sections(
      <>
        <Callout {...(view.reference ? { reference: view.reference } : {})}>{view.reason}</Callout>
        {!loaded.ok && loaded.fixedReason ? <Callout tone="note">{loaded.fixedReason}</Callout> : null}
        <Panel title={t('players.byCohort')} aside={<span className="count count-badge">{t(view.rows.length === 1 ? 'players.months.one' : 'players.months.other', { n: view.rows.length })}</span>}>
          {/* §21.7 — which of the two views this table is, and the way to the
              other one. Above the table, because it describes every row. */}
          <CurrencyViewSwitch control={view.currencyView} onView={setCurrencyView} />
          {view.rows.length === 0 ? (
            <Empty>{t('players.cohorts.empty')}</Empty>
          ) : (
            <Sorted rows={view.rows} by={{ month: (row) => sortableCell(row.month), players: (row) => sortableCell(row.players), ftds: (row) => sortableCell(row.ftds), deposits: (row) => sortableCell(row.deposits), withdrawals: (row) => sortableCell(row.withdrawals), ggr: (row) => sortableCell(row.ggr) }}>
              {(ordered, sort) => (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <SortTh sort={sort} col="month">{t('col.cohort')}</SortTh>
                    <SortTh sort={sort} col="players" align="num">{t('col.players')}</SortTh>
                    <SortTh sort={sort} col="ftds" align="num">{t('col.firstDeposits')}</SortTh>
                    {/* §21.7b — the mark is on the money columns' headers
                        rather than in every cell: twelve months times three
                        columns is thirty-six copies of one sentence, and the
                        rate itself is under the table, once. */}
                    <SortTh sort={sort} col="deposits" align="num">
                      {t('col.deposits')}
                      {view.convertedMark ? (
                        <span className={`figure-converted${view.convertedMixed ? ' is-mixed' : ''}`}>
                          {view.convertedMark}
                        </span>
                      ) : null}
                    </SortTh>
                    <SortTh sort={sort} col="withdrawals" align="num">
                      {t('col.withdrawals')}
                      {view.convertedMark ? (
                        <span className={`figure-converted${view.convertedMixed ? ' is-mixed' : ''}`}>
                          {view.convertedMark}
                        </span>
                      ) : null}
                    </SortTh>
                    <SortTh sort={sort} col="ggr" align="num">
                      {t('col.grossRevenue')}
                      {view.convertedMark ? (
                        <span className={`figure-converted${view.convertedMixed ? ' is-mixed' : ''}`}>
                          {view.convertedMark}
                        </span>
                      ) : null}
                    </SortTh>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((row) => (
                    <tr key={row.month}>
                      <td className="num faint">{row.month}</td>
                      <td className="num">{row.players === null ? row.withheld : formatCount(row.players)}</td>
                      <td className="num">{row.ftds === null ? row.withheld : formatCount(row.ftds)}</td>
                      <td className="num money faint">{row.deposits}</td>
                      <td className="num money">{row.withdrawals}</td>
                    <td className="num money">{row.ggr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
              )}
            </Sorted>
          )}
          {/* §21.7b — the rate, its source, its `as_of` and the rate policy,
              said once for the table above. */}
          <ConversionNotes view={view.conversions} />
        </Panel>
      </>,
    );
  }

  const columns = Object.keys(view.rows[0] ?? {});

  return sections(
    <>
      <Panel title={t('players.attributed')} aside={<span className="count count-badge">{t(view.rows.length === 1 ? 'players.count.one' : 'players.count.other', { n: view.rows.length })}</span>}>
        {view.rows.length === 0 ? (
          <Empty>{t('players.attributed.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} className={/_minor$|_at$/.test(c) ? 'num' : undefined}>
                      {c.replace(/_minor$/, '').replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.rows.map((row, i) => (
                  <tr key={String(row['player_ref'] ?? i)}>
                    {columns.map((c) => (
                      <td key={c} className={/_minor$|_at$/.test(c) ? 'num' : undefined}>
                        {String(row[c] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
    </>,
  );
}

/**
 * What the Clicks card opens: the clicks themselves.
 *
 * The card says how many; this says which — when each arrived, what campaign
 * and placement it came through, from where, on what, and whether it went on
 * to win a player. §39.6's own log, reached from the figure it explains
 * rather than only from a report three screens away.
 *
 * Capped and honest about it: the log is paged at the API and this asks for
 * one page. A popup is for understanding the number, not for exporting it, so
 * it says how many it is showing and sends the reader to the full report for
 * the rest rather than paging inside a dialog.
 */
const CARD_DETAIL_ROWS = 100;

function ClicksBehindTheCard({ api, range, onClose }: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  onClose: () => void;
}) {
  const loaded = useLoad(
    // The card counts counted (VALID) clicks only; the popup lists those.
    () => api.clickLog({ ...range, counted: '1', limit: String(CARD_DETAIL_ROWS) }),
    [range.from, range.to, range.brand_id],
  );
  // The envelope carries the page, and the page carries the rows.
  const rows = loaded.state === 'ready' ? loaded.value.data.data : [];
  const { rows: ordered, sort } = useSort(rows, {
    campaign: (r) => r.campaign,
    country: (r) => (r.ip_country ? countryName(r.ip_country) : null),
    registered: (r) => (r.player_country ? countryName(r.player_country) : null),
    device: (r) => deviceLabel(r.device, r.os, r.browser),
    when: (r) => r.occurred_at,
    status: (r) => r.status,
  }, { key: 'when' });

  return (
    <Modal title={t('dash.clicks.popupTitle')} onClose={onClose} wide>
      {loaded.state === 'loading' ? <Loading /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'ready' ? (
        rows.length === 0 ? (
          <Empty>{t('clicksImpressions.empty')}</Empty>
        ) : (
          <>
            <TableActions
              title={t('dash.clicks.popupTitle')}
              headers={[t('col.campaign'), t('col.clickCountry'), t('col.registeredIn'),
                        t('col.device'), t('col.when'), t('col.status'), t('col.attributed')]}
              keys={['campaign', 'country', 'registered_in', 'device', 'when', 'status', 'attributed']}
              rows={ordered.map((r) => [
                r.campaign,
                r.ip_country ? countryName(r.ip_country) : t('col.countryUnknown'),
                r.player_country ? countryName(r.player_country) : t('col.countryUnknown'),
                deviceLabel(r.device, r.os, r.browser), formatDateTime(r.occurred_at),
                clickStatusWords(r.status), r.attributed ? t('attributed.yes') : t('attributed.no'),
              ])}
              filename="clicks-detail"
            />
            <TableScroll>
              <table className="banded-by-index">
                <thead>
                  <tr>
                    <SortTh sort={sort} col="campaign">{t('col.campaign')}</SortTh>
                    <SortTh sort={sort} col="country">{t('col.clickCountry')}</SortTh>
                    <SortTh sort={sort} col="registered">{t('col.registeredIn')}</SortTh>
                    <SortTh sort={sort} col="device">{t('col.device')}</SortTh>
                    <SortTh sort={sort} col="when">{t('col.when')}</SortTh>
                    <SortTh sort={sort} col="status">{t('col.status')}</SortTh>
                    <th><HeaderTip label={t('col.attributed')} tip={t('col.attributed.tip')} /></th>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((r, i) => (
                    <tr key={`${r.occurred_at}-${r.link_token ?? ''}-${i}`} className={i % 2 === 1 ? 'row-alt' : ''}>
                      <td>{r.campaign}</td>
                      <td>
                        {r.ip_country
                          ? `${countryFlag(r.ip_country)} ${countryName(r.ip_country)}`
                          : <span className="faint">{t('col.countryUnknown')}</span>}
                      </td>
                      <td>
                        {r.player_country
                          ? `${countryFlag(r.player_country)} ${countryName(r.player_country)}`
                          : <span className="faint">—</span>}
                      </td>
                      <td className="faint">{deviceLabel(r.device, r.os, r.browser)}</td>
                      <td className="num faint">{formatDateTime(r.occurred_at)}</td>
                      <td>{clickStatusWords(r.status)}</td>
                      <td>
                        {/* One click can win more than one player (one browser,
                            two accounts); say how many rather than repeat the
                            click (owner, 2026-10-07). */}
                        {r.attributed
                          ? ((r.players_won ?? 1) > 1 ? t((r.players_won ?? 1) === 1 ? 'attributed.yesMany.one' : 'attributed.yesMany.other', { n: String(r.players_won ?? 1) }) : t('attributed.yes'))
                          : <span className="faint">{t('attributed.no')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <IpGeolocationNote />
            {loaded.value.data.has_more ? (
              <Callout>{t('dash.popup.capped', { n: String(rows.length) })}</Callout>
            ) : null}
          </>
        )
      ) : null}
    </Modal>
  );
}

/**
 * What the Registrations card opens: which brand, from which country, how many
 * — the owner's own words on 2026-09-29.
 *
 * The country is the one the brand verified at registration (§36.2b(iii)),
 * which is what decides eligibility. It is not the country the click came
 * from: that is a signal, it lives in the click log beside the click, and on
 * this deployment it is currently unresolved for every click — a breakdown
 * built on it would report every registration as unknown while the brand knew
 * exactly where each player was. The note under the table says so, because a
 * reader comparing this against the click log will otherwise find two
 * different countries for one player and no explanation.
 */
function RegistrationsBehindTheCard({ api, range, onClose }: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  onClose: () => void;
}) {
  const loaded = useLoad(
    () => api.registrationBreakdown(range),
    [range.from, range.to, range.brand_id],
  );
  const rows = loaded.state === 'ready' ? loaded.value.data : [];
  const { rows: ordered, sort } = useSort(rows, {
    brand: (r) => r.brand,
    country: (r) => (r.country ? countryName(r.country) : null),
    registrations: (r) => r.registrations,
    ftds: (r) => r.ftds,
  }, { key: 'registrations' });
  const total = rows.reduce((n, r) => n + r.registrations, 0);

  return (
    <Modal title={t('dash.registrations.popupTitle')} onClose={onClose} wide>
      {loaded.state === 'loading' ? <Loading /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'ready' ? (
        rows.length === 0 ? (
          <Empty>{t('dash.registrations.none')}</Empty>
        ) : (
          <>
            <TableActions
              title={t('dash.registrations.popupTitle')}
              headers={[t('col.brand'), t('col.country'),
                        t('dash.chart.registrations'), t('col.ftds')]}
              keys={['brand', 'country', 'registrations', 'ftds']}
              rows={ordered.map((r) => [
                r.brand, r.country ? countryName(r.country) : t('col.countryUnknown'),
                String(r.registrations), String(r.ftds),
              ])}
              filename="registrations-by-brand-and-country"
            />
            <TableScroll>
              <table className="banded-by-index">
                <thead>
                  <tr>
                    <SortTh sort={sort} col="brand">{t('col.brand')}</SortTh>
                    <SortTh sort={sort} col="country">{t('col.country')}</SortTh>
                    <SortTh sort={sort} col="registrations" align="num">{t('dash.chart.registrations')}</SortTh>
                    <SortTh sort={sort} col="ftds" align="num">{t('col.ftds')}</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((r, i) => (
                    <tr key={`${r.brand}-${r.country ?? '?'}`} className={i % 2 === 1 ? 'row-alt' : ''}>
                      <td>{r.brand}</td>
                      <td>
                        {r.country
                          ? `${countryFlag(r.country)} ${countryName(r.country)}`
                          : <span className="faint">{t('col.countryUnknown')}</span>}
                      </td>
                      <td className="num">{formatCount(r.registrations)}</td>
                      <td className="num faint">{formatCount(r.ftds)}</td>
                    </tr>
                  ))}
                </tbody>
                {/* The rows have to add up to the card that opened them. A
                    breakdown whose parts do not reach the whole is the one
                    thing a reader will notice and never trust again. */}
                <tfoot>
                  <tr>
                    <th colSpan={2}>{t('col.total')}</th>
                    <td className="num">{formatCount(total)}</td>
                    <td className="num faint">{formatCount(rows.reduce((n, r) => n + r.ftds, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </TableScroll>
            <Callout>{loaded.value.note}</Callout>
          </>
        )
      ) : null}
    </Modal>
  );
}

function QualificationPanel({
  loaded,
  onRetry,
}: {
  loaded: ReturnType<typeof useLoad<{
    data: { player_ref: string; status: string; failing_criterion: string | null; reason?: string | null; link_label?: string | null }[];
  }>>;
  onRetry: () => void;
}) {
  /*
   * §11.2 — an affiliate reads this table by placement, so the link-title
   * column sorts, and its header says which way. Ascending first: the way a
   * list of names is read.
   *
   * Above the loading and error returns, not beside the table it orders: a
   * hook that sits after a conditional `return` is called on the ready render
   * and not on the loading one, and React counts hooks per render. The panel
   * would throw the moment its data arrived. Nothing in the build catches
   * this — there is no lint step, and the types are identical either way.
   */
  const { rows, sort } = useSort(
    loaded.state === 'ready' ? loaded.value.data : [],
    {
      player: (r) => r.player_ref,
      // Blank last, whichever way it is pointing — the same rule
      // this panel's own helper had, and now the kit's for every table.
      link: (r) => r.link_label?.trim() || null,
      status: (r) => r.status,
    },
    { key: 'link', dir: 'asc' },
  );

  // Item 6 — the panel used to render nothing at all while loading and on a
  // failed load, which reads as "no players" rather than as either state.
  if (loaded.state === 'loading') return <Panel title={t('qualification.panel')}><Loading /></Panel>;
  if (loaded.state === 'error') {
    return (
      <Panel title={t('qualification.panel')}>
        <ErrorNotice error={loaded.error} onRetry={onRetry} />
      </Panel>
    );
  }
  return (
    <Panel title={t('qualification.panel')} aside={<span className="count count-badge">{loaded.value.data.length}</span>}>
      {loaded.value.data.length === 0 ? (
        <Empty>{t('qualification.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                {/* The kit's heading, not this panel's own. It had a
                    hand-rolled toggle with its two arrows stacked — a second
                    sort design, 18px tall, on one table out of every table in
                    three consoles. Found by the screen sweep of 2026-09-29. */}
                <SortTh sort={sort} col="player">{t('col.player')}</SortTh>
                <SortTh sort={sort} col="link">{t('col.linkTitle')}</SortTh>
                <SortTh sort={sort} col="status">{t('col.status')}</SortTh>
                <th>{t('col.failingCriterion')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.player_ref}>
                  <td><code>{row.player_ref}</code></td>
                  <td>{row.link_label ? row.link_label : <span className="faint" title={t('qualification.noLink')}>—</span>}</td>
                  {/* Item 4 — QUALIFIED / NOT_QUALIFIED / PENDING through the
                      word helper, as a Chip with the tone table. */}
                  <td><Chip tone={qualificationStatusTone(row.status)}>{qualificationStatusWords(row.status)}</Chip></td>
                  <td className="wrap">{qualificationReasonWords(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ *
 * Account
 * ------------------------------------------------------------------ */

function PayoutPreference({ api }: { api: AffiliateApi }) {
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [label, setLabel] = useState('');
  // #34 — a link to wherever the details actually live, which is the only
  // thing this platform will ever hold about paying you.
  const [detailsUrl, setDetailsUrl] = useState('');
  const [detailsUrlLabel, setDetailsUrlLabel] = useState('');
  /*
   * §30.4 — the affiliate's own minimum, in the major unit. The API converts
   * it against the currency's own scale: two places for USD, none for JPY,
   * three for KWD, and a console that guessed two would file a JPY threshold
   * a hundred times too large.
   */
  const [minimumAmount, setMinimumAmount] = useState('');
  const [minimumCurrency, setMinimumCurrency] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [loadedOnce, setLoadedOnce] = useState(false);
  // §30.4d — bumped on a successful save so the minimum-by-method figure
  // below is re-read against the newly saved method, not the one in force
  // when the screen opened.
  const [savedVersion, setSavedVersion] = useState(0);

  // The form was write-only: it opened on BANK_TRANSFER and an empty box
  // whatever had been saved, so nobody could tell what their operator was
  // actually seeing, and changing the link meant retyping the method.
  const current = useLoad(() => api.paymentPreference(), []);
  // The scales, so a saved minimum can be shown back in the major unit it was
  // typed in rather than as a count of minor units.
  const currencies = useLoad(() => api.currencies(), []);
  const currencyScales = currencies.state === 'ready' ? currencies.value.items : [];
  useEffect(() => {
    if (loadedOnce || current.state !== 'ready') return;
    const v = current.value;
    if (v.method) setMethod(v.method);
    setLabel(v.label ?? '');
    setDetailsUrl(v.details_url ?? '');
    setDetailsUrlLabel(v.details_url_label ?? '');
    setMinimumCurrency(v.minimum_payout_currency ?? '');
    setMinimumAmount(
      v.minimum_payout_minor !== null && v.minimum_payout_currency !== null
        ? majorUnits(v.minimum_payout_minor, v.minimum_payout_currency, currencyScales)
        : '',
    );
    setLoadedOnce(true);
  }, [current, loadedOnce]);

  /*
   * §30.4d/§8.7 — the minimum payout the affiliate is actually held to
   * before a statement is raised, so it is visible on this form rather than
   * discovered later on a statement that carried the balance forward.
   *
   * `terms()` is the one place the affiliate console reads it (`minimum_payout`
   * on each deal item — task A4), and it is resolved at the operator level
   * alone (never brand or deal), so every item in `items` carries the same
   * value; reading `items[0]` is not "picking a deal", it is reading the one
   * figure that happens to be repeated on every one of them.
   *
   * It only ever describes the affiliate's *saved* method (`minimum_payout.
   * method`), because that is the only method the API resolves a figure for.
   * So the specific figure is shown only while the picker still shows that
   * same method; the moment the affiliate picks a different one — before
   * saving it — there is no verified figure for it yet, and the general
   * threshold is shown instead rather than a specific-looking number this
   * screen cannot actually vouch for.
   */
  const termsLoad = useLoad(() => api.terms(), [savedVersion]);
  const minimumPayout = termsLoad.state === 'ready' && termsLoad.value.items.length > 0
    ? termsLoad.value.items[0]!.minimum_payout
    : null;
  const methodLabel = payoutMethods().find((m) => m.value === method)?.label ?? method;
  const minimumForMethod =
    minimumPayout && minimumPayout.method === method
      && minimumPayout.method_amount_minor !== null && minimumPayout.method_currency !== null
      ? {
          label: t('figure.payout.minimumForMethod', { method: methodLabel }),
          value: disputeDetailAmount(minimumPayout.method_amount_minor, minimumPayout.method_currency),
        }
      : minimumPayout
      ? {
          label: t('figure.payout.minimumGeneral'),
          value: disputeDetailAmount(minimumPayout.amount_minor, minimumPayout.currency),
        }
      : null;

  const error = checkPayoutLabel(label);
  const urlError = checkPayoutDetailsUrl(detailsUrl);
  const urlLabelError = checkPayoutDetailsUrlLabel(detailsUrlLabel, detailsUrl);
  const blocked = error !== null || urlError !== null || urlLabelError !== null;

  if (current.state === 'loading') return <Loading rows={3} />;

  return (
    <div className="form">
      {failure ? <Failure error={failure} /> : null}

      <div className="form-row">
        <Field label={t('payout.method')}>
          <Select value={method} onChange={setMethod} options={payoutMethods()} />
        </Field>
        <Field
          label={t('payout.label')}
          hint={t('payout.label.hint')}
          {...(error ? { error } : {})}
        >
          <TextInput
            value={label}
            invalid={error !== null}
            onChange={setLabel}
            placeholder={t('payout.label.placeholder')}
          />
        </Field>
      </div>

      {/* §30.4d — the figure this method (or, until it is saved, the general
          threshold) actually holds the affiliate to, shown where the method
          is chosen rather than only on a later statement. */}
      {minimumForMethod ? (
        <Figure view={{ label: minimumForMethod.label, value: minimumForMethod.value, provisional: false }} />
      ) : null}

      {/*
        * §30.4 — the affiliate's own figure on top of the operator's.
        *
        * It can only raise the bar. Somebody paid in crypto pays a network fee
        * per payment and would rather be paid once at a larger figure than
        * five times at a small one; the hint says so, because a field that
        * looks like it could get you paid sooner would be tried for that.
        */}
      <div className="form-row">
        <Field label={t('payout.minimum')} hint={t('payout.minimum.hint')}>
          <TextInput
            value={minimumAmount}
            onChange={setMinimumAmount}
            placeholder={t('payout.minimum.placeholder')}
          />
        </Field>
        <Field label={t('payout.minimum.currency')}>
          <Select
            value={minimumCurrency}
            onChange={setMinimumCurrency}
            options={[
              { value: '', label: t('payout.minimum.noCurrency') },
              ...currencyScales.map((c) => ({ value: c.code, label: c.code })),
            ]}
          />
        </Field>
      </div>

      {/* §31.1b — said before the box, not after a rejection. An affiliate who
          has already typed their IBAN has put it in a request log and a
          browser autofill store, whatever the server then does with it. */}
      <Callout tone="caution">
        {t('payout.noCredentials')}
      </Callout>

      {/* #34 — the answer to "then where do I put them": somewhere else, and
          this is the pointer to it. */}
      <div className="form-row">
        <Field
          label={t('payout.link')}
          hint={t('payout.link.hint')}
          {...(urlError ? { error: urlError } : {})}
        >
          <TextInput
            value={detailsUrl}
            invalid={urlError !== null}
            onChange={setDetailsUrl}
            placeholder="https://…"
          />
        </Field>
        <Field
          label={t('payout.linkLabel')}
          hint={t('payout.linkLabel.hint')}
          {...(urlLabelError ? { error: urlLabelError } : {})}
        >
          <TextInput
            value={detailsUrlLabel}
            invalid={urlLabelError !== null}
            onChange={setDetailsUrlLabel}
            placeholder={t('payout.linkLabel.placeholder')}
          />
        </Field>
      </div>

      <div className="form-actions">
        <button
          className="primary"
          disabled={blocked || busy}
          onClick={() => {
            setBusy(true);
            setFailure(null);
            api
              .setPaymentPreference({
                method,
                ...(label ? { label } : {}),
                // Sent even when empty, so clearing the box clears the link
                // rather than silently leaving the old one in place.
                detailsUrl: detailsUrl.trim(),
                detailsUrlLabel: detailsUrlLabel.trim(),
                // Sent even when empty, for the same reason as the link
                // above: clearing the box clears the preference.
                minimumAmount: minimumAmount.trim(),
                minimumCurrency: minimumCurrency.trim(),
              })
              .then((r) => {
                setSaved(r.note);
                // §30.4d — re-read the minimum against the method that is
                // now actually saved, not the one in force when this screen
                // opened.
                setSavedVersion((n) => n + 1);
              })
              .catch((e: unknown) => setFailure(e))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? kitText('action.saving') : t('payout.save')}
        </button>
      </div>

      {/* Item 3 (batch 2) — the save's own confirmation, read whole. */}
      {saved ? <Callout fold={false}>{saved}</Callout> : null}
    </div>
  );
}

function LocalePicker({
  api,
  chosen,
  operatorDefault,
  onSaved,
}: {
  api: AffiliateApi;
  chosen: string | null;
  operatorDefault: string;
  /**
   * Item 5 — the confirmation text goes up to `App`, since a save bumps
   * `meVersion` and this component does not survive the remount that
   * follows; it can no longer hold the confirmation in its own state.
   */
  onSaved: (note?: string) => void;
}) {
  // Seeded from what is actually in force — the chosen language, or the
  // operator's default when nothing has been chosen (§2.8d). A picker that
  // always opens on English tells somebody reading Portuguese that they are
  // reading English. The default is itself a shipped language (E37,
  // `SHIPPED_LOCALES`), so the option the picker opens on is one it offers.
  const [locale, setLocale] = useState(chosen ?? operatorDefault ?? 'en');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const defaultName =
    LOCALES.find((l) => l.value === operatorDefault)?.label ?? operatorDefault;

  return (
    <div className="form">
      {failure ? <Failure error={failure} /> : null}
      <div className="form-row">
        <Field label={t('language.field')} hint={t('language.hint')}>
          <Select value={locale} onChange={setLocale} options={[...LOCALES]} />
        </Field>
        <div className="form-actions">
          <button
            className="primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setFailure(null);
              api
                .setLocale(locale)
                .then((r) => onSaved(r.note))
                .catch((e: unknown) => setFailure(e))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? t('language.saving') : t('language.save')}
          </button>
        </div>
      </div>
      {/* Item 3 — a plain note, not a provisional-figure legend. */}
      {chosen === null ? (
        <p className="panel-note">{t('language.default', { locale: defaultName })}</p>
      ) : null}
    </div>
  );
}

/**
 * §21.7 — the currency this affiliate reads their reports in (E38).
 *
 * The same shape as the language picker above and for the same reason: an
 * operator default with a per-user override, where "not chosen" is a real state
 * and not an empty one. An affiliate on programmes in three currencies is the
 * reader §21.7 was written for.
 *
 * §21.7a and §21.7f are what the hint says, and they are the answer to the
 * question a control with "currency" in its name provokes: nothing stored
 * moves, and the statement still pays what it says it pays.
 */
function DisplayCurrencyPicker({
  api,
  chosen,
  operatorDefault,
  onSaved,
}: {
  api: AffiliateApi;
  chosen: string | null;
  operatorDefault: string;
  /** Item 5 — see `LocalePicker`'s `onSaved` for why this is lifted. */
  onSaved: (note?: string) => void;
}) {
  const currencies = useLoad(useCallback(() => api.currencies(), [api]), []);
  const [currency, setCurrency] = useState(chosen ?? operatorDefault ?? DISPLAY_CURRENCY_ORIGINAL);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  // A currency deactivated in the reference table after somebody chose it stays
  // on this list, because a native <select> handed a value it does not offer
  // shows its first option instead — which tells the reader they chose
  // something they did not. E37 recorded that failure for languages.
  const active = currencies.state === 'ready' ? currencies.value.items.map((c) => c.code) : [];
  const codes = [
    ...(chosen && chosen !== DISPLAY_CURRENCY_ORIGINAL && !active.includes(chosen) ? [chosen] : []),
    ...active,
  ];

  return (
    <div className="form">
      {failure ? <Failure error={failure} /> : null}
      {currencies.state === 'error' ? <Failure error={currencies.error} /> : null}
      <div className="form-row">
        <Field label={t('currency.field')} hint={t('currency.hint')}>
          <Select
            value={currency}
            onChange={setCurrency}
            options={[
              { value: DISPLAY_CURRENCY_ORIGINAL, label: t('currency.original') },
              ...codes.map((code) => ({ value: code, label: code })),
            ]}
          />
        </Field>
        <div className="form-actions">
          <button
            className="primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setFailure(null);
              api
                .setDisplayCurrency(currency)
                .then((r) => onSaved(r.note))
                .catch((e: unknown) => setFailure(e))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? t('language.saving') : t('language.save')}
          </button>
        </div>
      </div>
      {/* Item 3 — a plain note, not a provisional-figure legend. */}
      {chosen === null ? (
        <p className="panel-note">{t('currency.default', { currency: operatorDefault })}</p>
      ) : null}
    </div>
  );
}

/**
 * §8.9d — one email field and Send. The toast is always "Invitation sent."
 * (§6.14a's enumeration guard, the same reason an operator's own invitation
 * form never says whether the address is already known): this screen has no
 * way to tell, from the answer, whether the address was recognised.
 */
function InviteSubAffiliateForm({
  api,
  invitationsLeftToday,
  onSent,
}: {
  api: AffiliateApi;
  invitationsLeftToday: number;
  onSent: () => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [problem, setProblem] = useState<unknown>(null);

  const send = () => {
    setBusy(true);
    setFieldError(null);
    setProblem(null);
    api
      .inviteSubAffiliate(email.trim())
      .then(() => {
        setEmail('');
        onSent();
      })
      .catch((e: unknown) => {
        // 400 INVALID_EMAIL names the field; a stale 409 (standing changed
        // mid-type) or 429 (§8.9d's rate limit) is a plain notice instead.
        if (isApiClientError(e) && e.code === 'INVALID_EMAIL') {
          setFieldError(explainFailure(e).message);
        } else {
          setProblem(e);
        }
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="form">
      {problem ? <Callout tone="error">{explainFailure(problem).message}</Callout> : null}
      <div className="form-row">
        <Field label={kitText('field.email')} {...(fieldError ? { error: fieldError } : {})}>
          <EmailInput value={email} onChange={setEmail} placeholder="partner@example.com" />
        </Field>
        <div className="form-actions">
          <button
            className="primary"
            disabled={busy || email.trim() === '' || invitationsLeftToday <= 0}
            onClick={send}
          >
            {busy ? kitText('action.saving') : t('subaffiliates.invite.send')}
          </button>
        </div>
      </div>
      <p className="panel-note">
        {t(
          invitationsLeftToday === 1
            ? 'subaffiliates.invitationsLeft.one'
            : 'subaffiliates.invitationsLeft.other',
          { n: invitationsLeftToday },
        )}
      </p>
    </div>
  );
}

/**
 * Sub-affiliates, self-service — §8.9, §12.3.
 *
 * One primary action (share the link), and everything else is a compact
 * report on it: the terms a referral is made at, an email invite, who has
 * joined and who is still waiting, and what each one has earned the
 * recruiter so far. §8.9e bounds what this screen is allowed to show — no
 * names, no rates, no players, no payments — which is why a row identifies
 * itself by when it joined rather than by who it is.
 */
function SubAffiliates({ api }: ScreenProps) {
  const [version, setVersion] = useState(0);
  const loaded = useLoad(useCallback(() => api.mySubAffiliates(), [api]), [version]);
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkFailure, setLinkFailure] = useState<unknown>(null);

  if (loaded.state === 'loading') return <Loading />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const view = subAffiliates(loaded.value);
  // §8.9a — the portal's own address plus the path the API composed, the same
  // way `Invitation.tsx` builds the sign-in link it shows after acceptance.
  const absoluteLink = view.signupPath ? `${location.origin}${view.signupPath}` : null;

  return (
    <>
      <div className="page-head"><h1>{t('subaffiliates.title')}</h1></div>

      <Panel title={t('subaffiliates.link.panel')}>
        {!view.canRecruit ? (
          // §8.9a — the reason, and nothing to press: there is no link to
          // offer while any of the three holds.
          <Callout tone="caution">{view.whyNot}</Callout>
        ) : view.referralCode === null ? (
          <div className="form-actions">
            {linkFailure ? <Callout tone="error">{explainFailure(linkFailure).message}</Callout> : null}
            <button
              className="primary"
              disabled={creatingLink}
              onClick={() => {
                setCreatingLink(true);
                setLinkFailure(null);
                api
                  .createSubAffiliateReferralCode()
                  .then(() => setVersion((v) => v + 1))
                  .catch((e: unknown) => setLinkFailure(e))
                  .finally(() => setCreatingLink(false));
              }}
            >
              {creatingLink ? t('subaffiliates.link.creating') : t('subaffiliates.link.create')}
            </button>
          </div>
        ) : (
          <div className="link-url">
            <code>{absoluteLink}</code>
            {absoluteLink ? <CopyButton text={absoluteLink} /> : null}
          </div>
        )}
        {view.canRecruit ? <p className="terms-summary">{view.terms}</p> : null}
        {view.canRecruit ? <p className="panel-note">{view.applyNote}</p> : null}
      </Panel>

      {view.canRecruit ? (
        <Panel title={t('subaffiliates.invite.panel')}>
          <InviteSubAffiliateForm
            api={api}
            invitationsLeftToday={view.invitationsLeftToday}
            onSent={() => setVersion((v) => v + 1)}
          />
        </Panel>
      ) : null}

      <Panel
        title={t('subaffiliates.table.panel')}
        aside={<span className="count count-badge">{view.rows.length}</span>}
      >
        {view.rows.length === 0 ? (
          <Empty>{view.empty}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  {/* No name to head the column with — the row reads "Joined
                      {date}" in its place (§8.9e). */}
                  <th aria-label={t('subaffiliates.col.recruit')} />
                  <th>{t('col.status')}</th>
                  <th className="num">{t('col.clicks')}</th>
                  <th className="num">{t('col.firstDeposits')}</th>
                  <th className="num">{t('subaffiliates.col.overrideEarned')}</th>
                </tr>
              </thead>
              <tbody>
                {view.rows.map((row) => (
                  <Fragment key={row.key}>
                    <tr className={row.note ? 'has-note' : undefined}>
                      <td>{row.joined}</td>
                      <td><Chip tone={row.statusTone}>{row.status}</Chip></td>
                      <td className="num">{formatCount(row.clicks)}</td>
                      <td className="num">{formatCount(row.ftds)}</td>
                      <td className="stack num">
                        {row.overrideEarned.length === 0 ? (
                          <span className="faint">—</span>
                        ) : (
                          row.overrideEarned.map((f) => {
                            const { amount, unit } = splitCurrency(f.amount);
                            return (
                              <div key={f.currency}>
                                {amount}
                                {unit ? <span className="figure-unit"> {unit}</span> : null}
                              </div>
                            );
                          })
                        )}
                      </td>
                    </tr>
                    {/* styles.css's `tr.has-note` / `tr.row-note` pair. */}
                    {row.note ? (
                      <tr className="row-note">
                        <td colSpan={5}><span className="row-note-text">{row.note}</span></td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>

      <Panel title={t('subaffiliates.invitations.panel')}>
        {view.invitations.length === 0 ? (
          <Empty>{view.invitationsEmpty}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{kitText('field.email')}</th>
                  <th>{t('subaffiliates.col.sent')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {view.invitations.map((inv) => (
                  <tr key={`${inv.email}\u0000${inv.sent}`}>
                    <td>{inv.email}</td>
                    <td className="faint">{inv.sent}</td>
                    <td>
                      {inv.joined ? (
                        <Chip tone="locked">{t('subaffiliates.invitations.joined')}</Chip>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
    </>
  );
}

/**
 * Programmes an affiliate could apply to — §6.15, §2.5b, §6.5.
 *
 * The ones they are already on are marked rather than hidden. Hiding them makes
 * the list change shape as somebody joins things, and an affiliate who applied
 * last week and cannot find the programme again assumes the application
 * vanished.
 *
 * §2.5b is the reason the "already on" comparison happens here and not on the
 * server: the directory is the same public list for everybody, and which of
 * them this affiliate is on is theirs to know. A server that personalised the
 * list would be one operator's page knowing about another's relationship.
 */
function Programmes({ api, me }: ScreenProps) {
  const open = useLoad(() => api.programmes(), []);
  const mine = useLoad(() => api.operators(), []);
  const brands = useLoad(() => api.brands(), []);
  const [applying, setApplying] = useState<string | null>(null);
  const [applied, setApplied] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  if (open.state === 'loading') return <div className="main"><Loading rows={3} /></div>;
  if (open.state === 'error') {
    return <Callout tone="error">{explainFailure(open.error).message}</Callout>;
  }

  const joined = joinedProgrammeNames(mine.state === 'ready' ? mine.value.items : []);

  const apply = (code: string) => {
    setBusy(true);
    setFailure(null);
    api
      .signUp({
        code,
        email: me.email,
        display_name: me.display_name,
        country: me.country,
        via: 'SELF_SIGNUP',
      })
      .then((r) => {
        setApplied((a) => ({ ...a, [code]: r.message ?? t('brands.applicationSent') }));
        setApplying(null);
      })
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="page-head"><h1>{t('brands.title')}</h1></div>

      <Panel title={t('brands.youWorkWith')}>
        {brands.state === 'loading' ? (
          <Loading rows={1} />
        ) : brands.state === 'error' ? (
          <Failure error={brands.error} />
        ) : brands.value.items.length === 0 ? (
          <Empty>
            {t('brands.empty')}
          </Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('col.brand')}</th>
                  <th scope="col">{t('col.yourStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {brands.value.items.map((b) => (
                  <tr key={b.brand_id}>
                    <td>{b.brand_name}</td>
                    <td>
                      {/* Item 2 (batch 2) — the account status, not a money
                          figure: `provisional` is for a figure that can
                          still move. Active is settled/healthy (`locked`);
                          Under Review and Ended are `neutral`, the same as
                          the status Chip on Account → Profile below. */}
                      <Chip tone={me.status === 'Active' ? 'locked' : 'neutral'}>
                        {explainStatus(me.status).status}
                      </Chip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
        {me.status === 'Under Review' ? (
          <Callout tone="caution">
            {t('brands.underReview')}
          </Callout>
        ) : null}
        <Callout>
          {t('brands.leaving')}
        </Callout>
      </Panel>

      <Panel title={t('brands.openToApplications')}>
        {open.value.items.length === 0 ? (
          <Empty>
            {t('brands.openEmpty')}
          </Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr><th>{t('col.brands')}</th><th>{t('col.company')}</th><th>{t('col.status')}</th><th /></tr>
              </thead>
              <tbody>
                {open.value.items.map((p) => {
                  const brands = textArray(p.brands);
                  return (
                  <tr key={p.code}>
                    {/* The brands first, because that is the name an affiliate
                        recognises from a banner. The company second, because
                        that is who the relationship is with. */}
                    <td>{brands.length > 0 ? brands.join(', ') : p.operator_trading_name}</td>
                    <td className="faint">{p.operator_trading_name}</td>
                    <td>
                      {/* Item 3 — a directory listing's own state, not a
                          money figure that can still move. */}
                      {joined.has(p.operator_trading_name) ? (
                        <Chip tone="neutral">{t('brands.alreadyApplied')}</Chip>
                      ) : applied[p.code] ? (
                        <Chip tone="neutral">{t('brands.sent')}</Chip>
                      ) : (
                        <Chip tone="neutral">{t('brands.open')}</Chip>
                      )}
                    </td>
                    <td>
                      {joined.has(p.operator_trading_name) || applied[p.code] ? null : (
                        <button className="link-button" onClick={() => setApplying(p.code)}>
                          {t('brands.apply')}
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>

      {/*
        §6.15b — "exactly what the new operator will and will not see". Shown
        before the application is sent, not after, because consent that does not
        state its scope is not consent.

        In place rather than by navigating to /apply/<code>. That link is for
        somebody with no account: following it from here would reload the page,
        and the session lives in memory and nowhere else — so applying would
        have signed the affiliate out, then asked them for a password they
        already have, and then ignored whatever they typed.
      */}
      {applying ? (
        <Modal
          title={t('brands.applyModal', { brands: textArray(open.value.items.find((p) => p.code === applying)?.brands).join(', ') || applying })}
          onClose={() => setApplying(null)}
        >
          {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}

          <p className="terms-summary">
            {t('brands.applyingAs', { name: me.display_name, email: me.email, country: me.country })}
          </p>

          <ul className="scope">
            <li>{t('brands.scope.contact')}</li>
            <li>{t('brands.scope.traffic')}</li>
          </ul>
          {/* Item 3 — what the new operator will not see is a fact about
              scope, not a negative outcome; the kit's `.will-not` red marker
              is for the latter, so this list uses its own neutral one
              instead (aside.css). */}
          <ul className="scope is-neutral">
            <li>{t('apply.scope.notOthers')}</li>
            <li>{t('apply.scope.notHistory')}</li>
          </ul>

          <div className="form-actions">
            <button className="primary" disabled={busy} onClick={() => apply(applying)}>
              {busy ? t('brands.sending') : t('brands.sendApplication')}
            </button>
          </div>
        </Modal>
      ) : null}

      {/* Item 3 (batch 2) — each application's own result, read whole. */}
      {Object.entries(applied).map(([code, message]) => (
        <Callout key={code} fold={false}>{message}</Callout>
      ))}

      {/* One rule per note. They were a single sentence carrying two, and the
          screen read a field the API had stopped returning — so what showed
          was whatever the demo fixture happened to hold. */}
      <Callout>{open.value.relationship_note}</Callout>
      <Callout>{open.value.review_note}</Callout>
    </>
  );
}

function AffiliateBrands({ api }: { api: AffiliateApi }) {
  const loaded = useLoad(() => api.brands(), []);

  if (loaded.state === 'loading') return <Loading rows={1} />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const items = loaded.value.items;

  return (
    <Panel title={t('brands.youWorkWith')}>
      {items.length === 0 ? (
        <Empty>{t('brands.notOnAny')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('col.brand')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.brand_id}>
                  <td>{b.brand_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      <Callout>
        {t('brands.leaving')}
      </Callout>
    </Panel>
  );
}

/**
 * Item 5 — hoisted out of `Account` so the section it is on can be lifted to
 * `App`, which survives the full-tree remount below.
 */
type AccountSection =
  | 'profile' | 'security' | 'mail' | 'api' | 'brands' | 'language' | 'currency' | 'paid'
  | 'trafficSources';

/**
 * §6.15 — signed in with an address still unconfirmed: the link lasts 24
 * hours, and this is where somebody whose link expired asks for another.
 */
function ResendMyConfirmation({ api }: { api: AffiliateApi }) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  return (
    <Callout tone="caution">
      <div className="stack">
        <span>{said ?? t('account.unconfirmed')}</span>
        {failure ? <Failure error={failure} /> : null}
        {said === null ? (
          <div>
            <button
              type="button"
              disabled={busy}
              aria-busy={busy}
              onClick={() => {
                setBusy(true);
                setFailure(null);
                api
                  .resendMyConfirmation()
                  .then((r) => setSaid(r.message))
                  .catch((e: unknown) => setFailure(e))
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? t('verify.resending') : t('account.resendConfirmation')}
            </button>
          </div>
        ) : null}
      </div>
    </Callout>
  );
}

function Account({
  api,
  me,
  onboard,
  onLocaleSaved,
  section,
  onSection,
  savedNote,
}: ScreenProps & {
  /**
   * Item 5 — lifted to `App`. Saving the language or the currency bumps
   * `meVersion`, which re-reads `me` through the *top-level* loading gate
   * (`if (me.state === 'loading') return …`), unmounting this whole screen
   * and remounting a fresh one on Profile. State that lives in `App` (which
   * that gate does not replace) is the only kind that survives the round
   * trip, which is why this is a prop instead of the local `useState` it used
   * to be.
   */
  section: AccountSection;
  onSection: (s: AccountSection) => void;
  /** The confirmation text from the save that just triggered the remount above. */
  savedNote: string | null;
}) {
  // Item 2 (third batch) — a voluntary entry point for the same flow §3.7's
  // forced first-login already uses. The app has not held the password in
  // memory since that login, so this asks for it again rather than reusing
  // ChangePassword's "already known" prop.
  const [changingPassword, setChangingPassword] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [enrollingMfa, setEnrollingMfa] = useState(false);
  const rows: [string, string][] = [
    [t('col.name'), me.display_name],
    [kitText('field.email'), me.email],
    [t('col.country'), me.country],
    // WP4 — the operator keeps this list; it is shown so the affiliate knows
    // what the operator has them down for.
    [
      t('account.geos'),
      (me.geos ?? []).length > 0
        ? (me.geos ?? []).map((code) => countryName(code)).join(', ')
        : t('account.noGeos'),
    ],
    [t('col.joined'), formatDate(me.joined_at)],
    [t('col.attribution'), me.attribution_model],
  ];

  if (editingProfile) {
    return (
      <EditProfile
        api={api}
        me={me}
        onClose={() => setEditingProfile(false)}
        onSaved={() => {
          setEditingProfile(false);
          // Re-reads `me`, so the panel shows what was just saved and any
          // pending request beside it.
          onLocaleSaved(undefined);
        }}
      />
    );
  }

  if (changingPassword) {
    return (
      <ChangePassword
        email={me.email}
        api={onboard}
        title={t('account.changePassword.title')}
        intro={t('account.changePassword.intro')}
        onDone={() => setChangingPassword(false)}
      />
    );
  }

  /*
   * The same full-shell swap the voluntary password change above uses, and the
   * same component §3.7's forced first-login renders — so choosing to set a
   * second factor up and being made to look like one feature, not two.
   *
   * A reload follows rather than a local flag: `me.mfa_enrolled` is what the
   * panel reads, and finishing here is exactly what changes it.
   */
  if (enrollingMfa) {
    return (
      <EnrolMfa
        email={me.email}
        api={onboard}
        title={t('account.enrolMfa.title')}
        intro={t('account.enrolMfa.intro')}
        onCancel={() => setEnrollingMfa(false)}
        onDone={() => { setEnrollingMfa(false); location.reload(); }}
      />
    );
  }

  return (
    <ScreenWithSections
      title={t('account.title')}
      ariaLabel={t('account.sections')}
      section={section}
      onSection={onSection}
      groups={[{
        label: t('account.title'),
        items: [
          { id: 'profile', label: t('account.section.profile') },
          { id: 'security', label: t('account.section.security') },
          { id: 'mail', label: t('account.section.mail') },
          { id: 'api', label: t('account.section.api') },
          { id: 'brands', label: t('account.section.brands') },
          { id: 'language', label: t('account.section.language') },
          { id: 'currency', label: t('account.section.currency') },
          { id: 'paid', label: t('account.section.paid') },
          { id: 'trafficSources', label: t('account.section.trafficSources') },
        ],
      }]}
    >
          {section === 'mail' ? <MailArchive api={api} /> : null}

          {section === 'api' ? <ApiClients api={api} /> : null}

          {section === 'security' ? (
            <Panel title={t('account.section.security')}>
              {me.mfa_enrolled ? (
                <>
                  <Facts items={[{ label: kitText('staff.facts.authenticator'), value: kitText('staff.facts.authenticator.set') }]} />
                  {/*
                    No switch to turn it off. For an affiliate §3.7 does not
                    make a second factor mandatory outright — it makes one
                    mandatory before a contact-address change, because with no
                    payment details held (§31.1b) the contact address is what
                    an account takeover actually targets. A button that
                    silently removed that protection is the first thing
                    somebody with a stolen session would press.
                  */}
                  {/* Item 3 — a plain note, not a provisional-figure legend. */}
                  <p className="panel-note">
                    {t('account.mfa.set')}
                  </p>
                </>
              ) : (
                <>
                  <Callout>
                    {t('account.mfa.why')}
                  </Callout>
                  {/* Item 8's TOTP flag, as this affiliate's operator has set
                      it. It does not gate an affiliate's sign-in — §3.7 keeps
                      that voluntary — so the panel says the operator expects
                      one rather than that it is required. Without this the
                      button was a preference with nothing saying whether
                      anybody wanted it exercised. */}
                  {me.operator_expects_mfa ? (
                    <Callout tone="caution">{t('account.mfa.expected')}</Callout>
                  ) : null}
                  <div className="form-actions">
                    <button type="button" className="primary" onClick={() => setEnrollingMfa(true)}>
                      {t('account.mfa.setUp')}
                    </button>
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="button" className="copy" onClick={() => setChangingPassword(true)}>
                  {t('account.changePassword')}
                </button>
              </div>

              {/* §3.7a — sits under the two controls it gives somebody a
                  reason to use. A sign-in from somewhere they have never been
                  is the whole point of reading it. */}
              <LoginHistory api={api} />
            </Panel>
          ) : null}

          {section === 'profile' ? (
      <Panel title={t('account.profile')} aside={<Chip tone="neutral">{explainStatus(me.status).status}</Chip>}>
        {me.status_explanation ? (
          <Callout>{me.status_explanation}</Callout>
        ) : null}
        {me.email_confirmed === false && !me.impersonated ? <ResendMyConfirmation api={api} /> : null}
        <TableScroll>
          <table>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k}>
                  <th style={{ width: '10rem' }}>{k}</th>
                  <td className="wrap">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        {/* §6.7 — what the operator knows them as, and what they have asked
            to be known as, both true until somebody decides. */}
        {me.pending ? (
          <Callout tone="caution">
            {t('account.pending', {
              value: [me.pending.legal_name, me.pending.country].filter(Boolean).join(' · '),
            })}
          </Callout>
        ) : null}
        <div className="form-actions">
          <button type="button" className="copy" onClick={() => setEditingProfile(true)}>
            {t('account.edit')}
          </button>
          <button type="button" className="copy" onClick={() => setChangingPassword(true)}>
            {t('account.changePassword')}
          </button>
        </div>
      </Panel>
          ) : null}

          {section === 'brands' ? <AffiliateBrands api={api} /> : null}

          {section === 'language' ? (
      <Panel title={t('language.panel')}>
        {/* Item 5 — the confirmation from a save that just remounted this
            screen; `LocalePicker` itself does not survive that remount, so
            it cannot hold this in its own state any more. Item 3 (batch 2) —
            the save's own result, read whole. */}
        {savedNote ? <Callout fold={false}>{savedNote}</Callout> : null}
        <LocalePicker
          api={api}
          chosen={me.locale}
          operatorDefault={me.operator_default_locale}
          onSaved={onLocaleSaved}
        />
      </Panel>
          ) : null}

          {/* §21.7 — the same preference, about the currency rather than the
              language. Its own section rather than a second panel under
              "Language", because it is a different question and somebody
              looking for it would not look there. */}
          {section === 'currency' ? (
      <Panel title={t('currency.panel')}>
        {/* Item 5 — see the same note on the Language panel above. Item 3
            (batch 2) — the save's own result, read whole. */}
        {savedNote ? <Callout fold={false}>{savedNote}</Callout> : null}
        <DisplayCurrencyPicker
          api={api}
          chosen={me.display_currency}
          operatorDefault={me.operator_default_display_currency}
          onSaved={onLocaleSaved}
        />
      </Panel>
          ) : null}

          {section === 'paid' ? (
      <Panel title={t('account.paid')}>
        <PayoutPreference api={api} />
      </Panel>
          ) : null}

          {/* §6.6/§6.6a — the affiliate's own declared channels, and §6.6b's
              verification date once their account manager has recorded one. */}
          {section === 'trafficSources' ? (
      <Panel title={t('account.section.trafficSources')}>
        <TrafficSources api={api} />
      </Panel>
          ) : null}
    </ScreenWithSections>
  );
}

/** A Chip tone, spelled out here because App.tsx stays free of importing present.ts's own alias. */
type PostbackTone = 'neutral' | 'provisional' | 'locked' | 'overdue' | 'attention';

const POSTBACK_EVENT_KEYS: Record<string, AffiliateMessageKey> = {
  REGISTRATION: 'postbacks.event.REGISTRATION',
  FTD: 'postbacks.event.FTD',
  DEPOSIT: 'postbacks.event.DEPOSIT',
  QUALIFICATION: 'postbacks.event.QUALIFICATION',
  WITHDRAWAL: 'postbacks.event.WITHDRAWAL',
  WITHDRAWAL_REVERSED: 'postbacks.event.WITHDRAWAL_REVERSED',
  REVENUE: 'postbacks.event.REVENUE',
};

/** An event code the platform adds later shows as itself rather than vanishing. */
function postbackEventLabel(code: string): string {
  const key = POSTBACK_EVENT_KEYS[code];
  return key ? t(key) : code;
}

const POSTBACK_DELIVERY_STATUS_KEYS: Record<string, AffiliateMessageKey> = {
  DELIVERED: 'postbacks.delivery.status.DELIVERED',
  FAILED: 'postbacks.delivery.status.FAILED',
  EXHAUSTED: 'postbacks.delivery.status.EXHAUSTED',
  WITHHELD: 'postbacks.delivery.status.WITHHELD',
  PENDING: 'postbacks.delivery.status.PENDING',
};

function postbackDeliveryStatusLabel(code: string): string {
  const key = POSTBACK_DELIVERY_STATUS_KEYS[code];
  return key ? t(key) : code;
}

/**
 * The only four values the sender actually writes (`LINE_STATUS_TO_POSTBACK_STATUS`
 * in apps/workers/src/deliver.ts) — a raw commission-line status the payload
 * carries, translated the same way a delivery's own status is; `goal` beside
 * it is an identifier and stays as-is.
 */
const POSTBACK_CONVERSION_STATUS_KEYS: Record<string, AffiliateMessageKey> = {
  pending: 'postbacks.conversionStatus.pending',
  confirmed: 'postbacks.conversionStatus.confirmed',
  hold: 'postbacks.conversionStatus.hold',
  rejected: 'postbacks.conversionStatus.rejected',
};

function postbackConversionStatusLabel(code: string | null): string {
  if (code === null) return '—';
  const key = POSTBACK_CONVERSION_STATUS_KEYS[code];
  return key ? t(key) : code;
}

/**
 * Every stored URL is https (server-enforced, §41.7c) — the scheme is not
 * information, and the list's own URL column is tight enough that eight
 * characters of "https://" repeated on every row is eight characters the
 * host and path do not get.
 */
function postbackEndpoint(url: string): string {
  return url.replace(/^https:\/\//, '');
}

/**
 * By DESIGN.md §4's tone table: delivered is settled (`locked`); failed and
 * a retry run out of attempts are both the negative outcome (`overdue`);
 * withheld is the operator's own condition holding it back — not a failure of
 * anything, but worth a look (`attention`); pending is still moving, the same
 * reasoning `qualificationStatusTone` gives PENDING in present.ts
 * (`provisional`).
 */
function postbackDeliveryStatusTone(code: string): PostbackTone {
  switch (code) {
    case 'DELIVERED':
      return 'locked';
    case 'FAILED':
    case 'EXHAUSTED':
      return 'overdue';
    case 'WITHHELD':
      return 'attention';
    case 'PENDING':
      return 'provisional';
    default:
      return 'neutral';
  }
}

/**
 * A test that reached the URL but got back a non-2xx is not the same as one
 * that never arrived, but neither is the success the affiliate is asking
 * about — so both read as the caution tone, and only reached-and-2xx reads
 * as the note tone.
 */
function postbackTestTone(result: { reached: boolean; status?: number }): 'note' | 'caution' {
  const ok = result.reached && (result.status === undefined || (result.status >= 200 && result.status < 300));
  return ok ? 'note' : 'caution';
}

/**
 * §41.7 — a secret an affiliate does not have to invent by hand. 32 random
 * bytes as hex is 64 characters, comfortably inside the server's 16–256
 * window and nothing anybody would type by coincidence.
 */
function generatePostbackSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * What each macro carries, beside its `{name}` in the autofill dropdown.
 * The ten sub-ID slots share one message with the slot number in it.
 */
const POSTBACK_MACRO_LABEL_KEYS: Partial<Record<PostbackMacro, AffiliateMessageKey>> = {
  event: 'postbacks.macro.event',
  id: 'postbacks.macro.id',
  status: 'postbacks.macro.status',
  goal: 'postbacks.macro.goal',
  revenue: 'postbacks.macro.revenue',
  payout: 'postbacks.macro.payout',
  currency: 'postbacks.macro.currency',
  deposit_amount: 'postbacks.macro.deposit_amount',
  player_ref: 'postbacks.macro.player_ref',
  click_id: 'postbacks.macro.click_id',
  brand_url: 'postbacks.macro.brand_url',
  tid: 'postbacks.macro.tid',
  advertiser_id: 'postbacks.macro.advertiser_id',
  partner_id: 'postbacks.macro.partner_id',
  offer_id: 'postbacks.macro.offer_id',
  country: 'postbacks.macro.country',
  conversion_date: 'postbacks.macro.conversion_date',
  withdrawal_amount: 'postbacks.macro.withdrawal_amount',
  ngr: 'postbacks.macro.ngr',
  period_start: 'postbacks.macro.period_start',
  period_end: 'postbacks.macro.period_end',
};

/** A macro the platform adds later shows as its own name rather than vanishing. */
function postbackMacroLabel(macro: PostbackMacro): string {
  const slot = /^sub_id_(\d+)$/.exec(macro);
  if (slot) return t('postbacks.macro.sub_id', { n: slot[1]! });
  const key = POSTBACK_MACRO_LABEL_KEYS[macro];
  return key ? t(key) : macro;
}

/** '' is the dropdown's "Choose…" — a row nobody has decided yet. */
type PostbackFillSelectValue = PostbackFillChoice | '';

/**
 * A pick is remembered against the parameter as written, not only its
 * position: editing the URL so that a different parameter lands at the same
 * index must not hand it the choice somebody made for the old one.
 */
function postbackFillKey(param: PostbackUrlParam): string {
  return `${param.index}|${param.name}|${param.raw}`;
}

/**
 * A fixed value's chip. One with our macro inside a longer value
 * (`ref=aff_{sub_id_1}`) is never rewritten, but it is not sent as typed
 * either — the sender fills the macro — so it says which.
 */
function FixedPostbackValueChip({ raw }: { raw: string }) {
  const embedded = postbackMacrosInValue(raw);
  if (embedded.length === 0) return <Chip tone="neutral">{t('postbacks.fill.fixed')}</Chip>;
  return (
    <Chip tone="neutral">
      {t('postbacks.fill.embedded', { macros: embedded.map((m) => `{${m}}`).join(', ') })}
    </Chip>
  );
}

/**
 * Registering, or editing one already registered.
 *
 * One form for both, the way `SmartLinkForm` is one form for its own two
 * cases: editing is not a different feature, and a second component would
 * only be a second place for the URL/secret rules to drift from this one.
 * The event type is the one field that is never in the edit half: the
 * operator's conditions and the delivery history both belong to the event a
 * postback was registered for, so changing it would make history about an
 * event this registration no longer is.
 *
 * The URL is pasted exactly as the affiliate's tracker hands it out
 * (`…?subid=REPLACE&status=dep&payout=REPLACE`) and read back as one row per
 * query parameter: a placeholder's row picks the macro that goes there,
 * pre-selected from the parameter's name; a fixed value is shown as fixed and
 * never rewritten. What is saved is the URL with those placeholders replaced
 * by `{macro}` — nothing the server did not already accept.
 *
 * Editing starts every row on what is stored rather than on a fresh
 * suggestion (see `initialPostbackFills`): a registration opened only to
 * rotate its secret sends no URL, and a value left as typed when it was saved
 * does not block saving it again.
 *
 * The secret is optional here: a blank one is generated on save and shown
 * once, in the panel's "Registered as …" note, so nobody has to invent one to
 * get a postback working. On an edit, blank still means keep the current one.
 */
function PostbackForm({
  api,
  existing,
  onClose,
  onSaved,
}: {
  api: AffiliateApi;
  existing: PostbackRegistration | null;
  onClose: () => void;
  onSaved: (result: { id: string; secret?: string }) => void;
}) {
  const [eventType, setEventType] = useState(existing?.event_type ?? 'FTD');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [secret, setSecret] = useState('');
  const [picked, setPicked] = useState<Readonly<Record<string, PostbackFillChoice>>>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<unknown>(null);

  // Derived every render from what is typed: the rows, their suggestions,
  // and whatever the affiliate has picked over a suggestion.
  const parsed = parsePostbackUrl(url);
  // Edit-mode defaults only while the URL is still the stored one: a URL pasted
  // over it is a fresh tracker URL and gets a fresh registration's suggestions.
  const initial = initialPostbackFills(parsed, {
    editing: existing !== null && url.trim() === existing.url,
  });
  const choices: Record<number, PostbackFillChoice | null> = {};
  for (const param of parsed.params) {
    if (!param.fill) continue;
    choices[param.index] = picked[postbackFillKey(param)] ?? initial[param.index] ?? null;
  }
  const finalUrl = applyPostbackFills(url, choices);
  const complete = postbackFillsComplete(parsed, choices);
  const trimmedUrl = url.trim();
  // Counted on what is saved, placeholders rewritten: the server refuses the
  // same length, and saying so here beats a refusal after pressing Save.
  const tooLong = postbackUrlTooLong(finalUrl);

  // Blank is fine both ways now: a new registration generates one on save,
  // an edit keeps the current one (the server never returns it to prefill).
  const secretValid = secret === '' || (secret.length >= 16 && secret.length <= 256);
  const canSubmit = trimmedUrl !== '' && complete && secretValid && !tooLong;
  // A secret that keeps Save disabled keeps Advanced open: folded away, the
  // one field saying why would be out of sight.
  const showAdvanced = advancedOpen || !secretValid;

  const macroOptions = POSTBACK_MACRO_KEYS.map((k) => ({
    value: k as PostbackFillSelectValue,
    label: `{${k}} — ${postbackMacroLabel(k)}`,
  }));

  const urlField = (
    <Field label={t('postbacks.field.url')} hint={t('postbacks.fill.intro')}>
      <TextInput
        value={url}
        onChange={setUrl}
        placeholder="https://…?subid=REPLACE&payout=REPLACE"
      />
    </Field>
  );

  return (
    <Modal
      title={
        existing
          ? t('postbacks.edit.title', { event: postbackEventLabel(existing.event_type) })
          : t('postbacks.add')
      }
      onClose={onClose}
      wide
      keepOnBackdropClick
    >
      <div className="form">
        {problem ? <ErrorNotice error={problem} /> : null}

        {existing ? (
          urlField
        ) : (
          <div className="form-row">
            <Field label={t('col.event')} hint={t('postbacks.field.event.hint')}>
              <Select
                value={eventType}
                onChange={setEventType}
                options={[
                  { value: 'REGISTRATION', label: t('postbacks.event.REGISTRATION') },
                  { value: 'FTD', label: t('postbacks.event.FTD') },
                  { value: 'DEPOSIT', label: t('postbacks.event.DEPOSIT') },
                  { value: 'QUALIFICATION', label: t('postbacks.event.QUALIFICATION') },
                  { value: 'WITHDRAWAL', label: t('postbacks.event.WITHDRAWAL') },
                  { value: 'WITHDRAWAL_REVERSED', label: t('postbacks.event.WITHDRAWAL_REVERSED') },
                  { value: 'REVENUE', label: t('postbacks.event.REVENUE') },
                ]}
              />
            </Field>
            {urlField}
          </div>
        )}

        {parsed.params.length > 0 ? (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('postbacks.fill.col.param')}</th>
                  <th>{t('postbacks.fill.col.typed')}</th>
                  <th>{t('postbacks.fill.col.carries')}</th>
                </tr>
              </thead>
              <tbody>
                {parsed.params.map((param) => {
                  const choice = choices[param.index] ?? null;
                  return (
                    <tr key={param.index}>
                      <td><code>{param.name}</code></td>
                      <td className="mono small faint" style={{ overflowWrap: 'anywhere' }}>
                        {param.raw === '' ? '—' : param.raw}
                      </td>
                      <td style={{ minWidth: '16rem' }}>
                        {param.fill ? (
                          <>
                            {/* Select takes no aria-label, so a wrapping label
                                names it after the parameter it fills. */}
                            <label>
                              <span className="sr-only">{`${t('postbacks.fill.col.carries')}: ${param.name}`}</span>
                              <Select<PostbackFillSelectValue>
                                value={choice ?? ''}
                                onChange={(v) => {
                                  if (v === '') return;
                                  setPicked((cur) => ({ ...cur, [postbackFillKey(param)]: v }));
                                }}
                                searchable={false}
                                options={[
                                  { value: '', label: t('postbacks.fill.choose'), disabled: true },
                                  { value: KEEP_AS_TYPED, label: t('postbacks.fill.keep') },
                                  ...macroOptions,
                                ]}
                              />
                            </label>
                            {choice === null ? <p className="link-note">{t('postbacks.fill.unrecognised')}</p> : null}
                            {isPostbackCidParam(param.name) ? <p className="link-note">{t('postbacks.fill.cid')}</p> : null}
                          </>
                        ) : (
                          <FixedPostbackValueChip raw={param.raw} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        ) : null}

        {complete ? null : <Callout tone="caution">{t('postbacks.fill.incomplete')}</Callout>}
        {tooLong ? (
          <Callout tone="caution">
            {t('postbacks.fill.tooLong', { length: finalUrl.trim().length, max: POSTBACK_URL_MAX_LENGTH })}
          </Callout>
        ) : null}
        {finalUrl !== trimmedUrl ? (
          <Field label={t('postbacks.fill.preview')} group>
            <code style={{ overflowWrap: 'anywhere' }}>{finalUrl}</code>
          </Field>
        ) : null}

        <details
          className="terms-details"
          open={showAdvanced}
          onToggle={(e) => {
            const el = e.currentTarget;
            // React sets `open` only when the prop changes, so a close while
            // the secret is invalid is undone here rather than by a render.
            if (!el.open && !secretValid) {
              el.open = true;
              return;
            }
            setAdvancedOpen(el.open);
          }}
        >
          <summary>{t('postbacks.advanced')}</summary>
          <Field
            label={t('postbacks.field.secret')}
            hint={existing ? t('postbacks.field.secret.keepHint') : t('postbacks.field.secret.hint')}
            action={
              <button type="button" className="link-button" onClick={() => setSecret(generatePostbackSecret())}>
                {t('postbacks.field.secret.generate')}
              </button>
            }
          >
            <TextInput value={secret} onChange={setSecret} invalid={!secretValid} />
          </Field>
          {secret ? (
            <Callout tone="caution">
              {existing ? t('postbacks.field.secret.editNote') : t('postbacks.field.secret.note')}{' '}
              <CopyButton text={secret} />
            </Callout>
          ) : null}
        </details>

        <div className="form-actions">
          <button className="copy" onClick={onClose}>{kitText('action.cancel')}</button>
          <button
            className="primary"
            disabled={busy || !canSubmit}
            onClick={() => {
              if (existing) {
                const patch: { url?: string; secret?: string } = {};
                if (finalUrl !== existing.url) patch.url = finalUrl;
                if (secret) patch.secret = secret;
                if (Object.keys(patch).length === 0) {
                  // Nothing changed — no request. Sending the unchanged URL
                  // anyway would re-run §41.7c's DNS check and the duplicate
                  // check for no reason, and a URL whose DNS blips right
                  // then would block rotating a secret that has nothing to
                  // do with it.
                  onClose();
                  return;
                }
                setBusy(true);
                setProblem(null);
                api
                  .updatePostback(existing.id, patch)
                  .then((saved) => onSaved({ id: saved.id }))
                  .catch((e: unknown) => setProblem(e))
                  .finally(() => setBusy(false));
                return;
              }

              // Generated here rather than on the server so the one copy the
              // affiliate ever sees comes back through the same callout
              // whether they typed it or not; the contract stays 16–256.
              const sentSecret = secret || generatePostbackSecret();
              setBusy(true);
              setProblem(null);
              api
                .registerPostback({ event_type: eventType, url: finalUrl, secret: sentSecret })
                .then((saved) => onSaved({ id: saved.id, secret: sentSecret }))
                .catch((e: unknown) => setProblem(e))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? kitText('action.saving') : existing ? t('postbacks.edit.save') : t('postbacks.register')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * §41.7b — one registration's own delivery history. A section, not its own
 * Modal: it lives inside `PostbackDetailModal`, below the actions, which is
 * what replaced the row's separate "Deliveries" button — one popup per
 * registration instead of two.
 */
function PostbackDeliveriesSection({
  api,
  registrationId,
}: {
  api: AffiliateApi;
  registrationId: string;
}) {
  const deliveries = useLoad(() => api.postbackDeliveries(registrationId), [registrationId]);

  return (
    <>
      {deliveries.state === 'loading' ? <Loading rows={2} /> : null}
      {deliveries.state === 'error' ? <Failure error={deliveries.error} /> : null}
      {deliveries.state === 'ready' ? (
        deliveries.value.items.length === 0 ? (
          <Empty>{t('postbacks.noDeliveries')}</Empty>
        ) : (
          <Sorted rows={deliveries.value.items} by={{ status: (d) => sortableCell(d.status), conversion_status: (d) => sortableCell(d.conversion_status), goal: (d) => sortableCell(d.goal), click_id: (d) => sortableCell(d.click_id), attempts: (d) => sortableCell(d.attempts), response_status: (d) => sortableCell(d.response_status), last_attempt_at: (d) => sortableCell(d.last_attempt_at) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="status">{t('col.status')}</SortTh>
                  <SortTh sort={sort} col="conversion_status">{t('postbacks.col.conversionStatus')}</SortTh>
                  <SortTh sort={sort} col="goal">{t('postbacks.col.goal')}</SortTh>
                  <SortTh sort={sort} col="click_id">{t('postbacks.col.clickId')}</SortTh>
                  <SortTh sort={sort} col="attempts" align="num">{t('col.attempts')}</SortTh>
                  <SortTh sort={sort} col="response_status" align="num">{t('postbacks.col.httpCode')}</SortTh>
                  <SortTh sort={sort} col="last_attempt_at">{t('postbacks.col.lastAttempt')}</SortTh>
                  <th>{t('postbacks.col.error')}</th>
                </tr>
              </thead>
              <tbody>
                {/*
                  No "Delivered" column: for a delivered row it repeats Last
                  attempt (the send and the delivery are the same event), and
                  the wide modal has no room to spare — with it, this table
                  itself scrolled sideways. `Status` already says "Delivered".
                */}
                {ordered.map((d) => (
                  <tr key={d.id}>
                    <td><Chip tone={postbackDeliveryStatusTone(d.status)}>{postbackDeliveryStatusLabel(d.status)}</Chip></td>
                    <td className="faint">{postbackConversionStatusLabel(d.conversion_status)}</td>
                    <td className="faint">{d.goal ?? '—'}</td>
                    <td className="wrap"><code>{d.click_id ?? '—'}</code></td>
                    <td className="num">{formatCount(d.attempts)}</td>
                    <td className="num">{d.response_status ?? '—'}</td>
                    <td className="num faint">{d.last_attempt_at ? formatDateTime(d.last_attempt_at) : '—'}</td>
                    <td className="wrap faint">{d.last_error ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
        )
      ) : null}
      {deliveries.state === 'ready' ? <Callout>{deliveries.value.note}</Callout> : null}
    </>
  );
}

/**
 * One postback, opened from its row: what it is, its state and counts, every
 * action that touches it (test, edit, pause/resume, remove), and its
 * delivery history below — one popup instead of the row's former five
 * buttons and separate deliveries dialog.
 *
 * `registration` is the row `Postbacks` found for this id in `items` — the
 * last successfully loaded list, kept around across a reload rather than
 * read straight off `useLoad`'s own state — so a pause/resume or an edit,
 * which both call `onChanged` to reload it, never unmounts this modal for
 * the moment between one 'ready' state and the next; the new values just
 * land in the same open popup, from a fresh prop rather than stale local
 * state.
 */
function PostbackDetailModal({
  api,
  registration,
  onClose,
  onChanged,
  onRemoved,
}: {
  api: AffiliateApi;
  registration: PostbackRegistration;
  onClose: () => void;
  onChanged: () => void;
  /** So the panel can drop a "Registered as {id}" callout about this one. */
  onRemoved: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<unknown>(null);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<PostbackTestResult | null>(null);
  const [removing, setRemoving] = useState(false);

  // Switches this same popup to the edit form rather than opening a second
  // one on top of it; the event type is not editable in either.
  if (editing) {
    return (
      <PostbackForm
        api={api}
        existing={registration}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); onChanged(); }}
      />
    );
  }

  return (
    <Modal title={postbackEventLabel(registration.event_type)} onClose={onClose} wide>
      <div className="stack">
        {problem ? <ErrorNotice error={problem} /> : null}

        <Facts
          items={[
            { label: t('postbacks.col.url'), value: <code className="wrap">{registration.url}</code> },
            {
              label: t('col.state'),
              // Paused is `neutral`: DESIGN.md's tone table keeps `overdue`
              // and `provisional` off anything merely switched off.
              value: (
                <Chip tone={registration.enabled ? 'locked' : 'neutral'}>
                  {registration.enabled ? t('postbacks.state.active') : t('postbacks.state.paused')}
                </Chip>
              ),
            },
            { label: t('col.delivered'), value: formatCount(registration.delivered) },
            { label: t('postbacks.col.pending'), value: formatCount(registration.pending) },
            { label: t('postbacks.col.failed'), value: formatCount(registration.failed + registration.exhausted) },
            {
              label: t('postbacks.col.withheld'),
              value: registration.withheld > 0 ? formatCount(registration.withheld) : '—',
            },
            {
              label: t('postbacks.col.lastAttempt'),
              value: registration.last_attempt_at ? formatDateTime(registration.last_attempt_at) : '—',
            },
          ]}
        />

        {/* Paused holds every event rather than dropping it — true of all
            three event types now, not only REGISTRATION/FTD — so "nothing
            is lost" is the one thing worth saying here, not "off". */}
        {registration.enabled ? null : <Callout>{t('postbacks.paused.note')}</Callout>}

        <div className="form-actions">
          <button
            className="copy"
            disabled={testBusy}
            onClick={() => {
              setTestBusy(true);
              setProblem(null);
              setTestResult(null);
              api
                .testPostback(registration.id)
                .then((r) => setTestResult(r))
                .catch((e: unknown) => setProblem(e))
                .finally(() => setTestBusy(false));
            }}
          >
            {testBusy ? t('postbacks.action.sending') : t('postbacks.action.sendTest')}
          </button>
          <button className="copy" onClick={() => setEditing(true)}>
            {t('postbacks.action.edit')}
          </button>
          <button
            className="copy"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setProblem(null);
              api
                .updatePostback(registration.id, { enabled: !registration.enabled })
                .then(() => onChanged())
                .catch((e: unknown) => setProblem(e))
                .finally(() => setBusy(false));
            }}
          >
            {registration.enabled ? t('postbacks.action.pause') : t('postbacks.action.resume')}
          </button>
          <button className="link-button danger" onClick={() => setRemoving(true)}>
            {t('postbacks.action.remove')}
          </button>
        </div>
        {testResult ? <Callout tone={postbackTestTone(testResult)}>{testResult.detail}</Callout> : null}

        <h3 className="subhead">{t('postbacks.deliveries.heading')}</h3>
        <PostbackDeliveriesSection api={api} registrationId={registration.id} />
      </div>

      {removing ? (
        <Confirm
          title={t('postbacks.remove.title')}
          tone="caution"
          confirmLabel={t('postbacks.action.remove')}
          body={t('postbacks.remove.body', { url: registration.url })}
          onCancel={() => setRemoving(false)}
          onConfirm={() =>
            api.removePostback(registration.id).then(() => {
              setRemoving(false);
              onClose();
              onChanged();
              onRemoved(registration.id);
            })
          }
        />
      ) : null}
    </Modal>
  );
}

/**
 * Account → Postbacks.
 *
 * Was register-only: the id of the one just registered lived in React state,
 * so leaving the section lost it and an affiliate could never again see what
 * they had registered or how it was going (§41.7b requires delivery status
 * stay visible). This is the list §41.7 asks for, with the edit, pause/resume,
 * remove and test that go with maintaining one over time rather than only
 * ever adding the first.
 *
 * The list itself carries no action buttons — even six columns (Event,
 * Endpoint, State and three counts) plus a row of five buttons could not fit
 * at 1440px with the right rail showing; State and the counts are the reason
 * to look at a row, and they were what a scroll hid behind the buttons.
 * Every action lives one click away instead, behind the row itself
 * (`row-clickable`, the same idiom Disputes uses).
 */
function Postbacks({ api }: ScreenProps) {
  const [version, setVersion] = useState(0);
  const loaded = useLoad(() => api.postbacks(), [version]);
  const [adding, setAdding] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  // Item 3 (batch 2) — the registration's own result, read whole; kept at the
  // panel level (not inside the form) because the form's modal closes on
  // success and this must still be there afterward. Cleared when Add opens
  // again (a fresh attempt should not sit under an old confirmation) and
  // when the postback it names is removed (a confirmation about a
  // registration that no longer exists is a stale one, not a wrong one, but
  // it reads exactly like a mistake if the affiliate never notices it left).
  // Carries the secret too — the only place it is ever shown after saving,
  // since a blank one is generated by the form and the server never returns it.
  const [registered, setRegistered] = useState<{ id: string; secret?: string } | null>(null);

  // Bug found on the way: `viewing` used to be derived straight from
  // `loaded`, which is `'loading'` for the moment of every reload — so a
  // pause/resume/edit (all of which call `onChanged` below) unmounted the
  // open detail modal, losing its test result, and flashed the table back to
  // its loading skeleton. This keeps the last successfully loaded list
  // across that moment; `useLoad` doing this everywhere is a kit-wide change
  // still deferred (DESIGN.md §8), and this is the same fix scoped to one
  // screen, the way Disputes already scopes it for its own filter.
  const [items, setItems] = useState<readonly PostbackRegistration[] | null>(null);
  useEffect(() => {
    if (loaded.state === 'ready') setItems(loaded.value.items);
  }, [loaded]);

  const viewing = viewingId ? (items ?? []).find((r) => r.id === viewingId) ?? null : null;

  return (
    <>
    <div className="page-head"><h1>{t('nav.postbacks')}</h1></div>
    <Panel
      title={t('postbacks.panel')}
      aside={items ? <span className="count count-badge">{items.length}</span> : null}
    >
      {/* §41.7c / §41.7b */}
      <Callout>
        {/* The macro names are identifiers (§2.8b) and are what the affiliate
            types into their own URL, so only the sentences around them move. */}
        {t('postbacks.macros')}{' '}
        {'{status} {goal} {revenue} {payout} {currency} {deposit_amount} {click_id} {tid} {id} '}
        {'{advertiser_id} {partner_id} {offer_id} {country} {conversion_date} '}
        {'{withdrawal_amount} {ngr} {period_start} {period_end} {brand_url} '}
        {'{sub_id_1}..{sub_id_10}'}.{' '}
        {t('postbacks.signed', { header: 'x-signature' })}
      </Callout>

      <div className="form-actions">
        <button
          className="primary"
          onClick={() => {
            setRegistered(null);
            setAdding(true);
          }}
        >
          {t('postbacks.add')}
        </button>
      </div>

      {registered ? (
        <Callout fold={false}>
          {t('postbacks.registered', { id: registered.id })}
          {registered.secret ? (
            <>
              {' '}{t('postbacks.registered.secret')}{' '}
              <code style={{ overflowWrap: 'anywhere' }}>{registered.secret}</code>{' '}
              <CopyButton text={registered.secret} />
            </>
          ) : null}
        </Callout>
      ) : null}

      {items === null && loaded.state === 'loading' ? <Loading /> : null}
      {items === null && loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {items ? (
        items.length === 0 ? (
          <Empty>{t('postbacks.empty')}</Empty>
        ) : (
          <Sorted rows={items} by={{ event_type: (row) => sortableCell(row.event_type), url: (row) => sortableCell(row.url), enabled: (row) => sortableCell(row.enabled), delivered: (row) => sortableCell(row.delivered), failed: (row) => sortableCell(row.failed), last_attempt_at: (row) => sortableCell(row.last_attempt_at) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="event_type">{t('col.event')}</SortTh>
                  <SortTh sort={sort} col="url">{t('postbacks.col.url')}</SortTh>
                  <SortTh sort={sort} col="enabled">{t('col.state')}</SortTh>
                  <SortTh sort={sort} col="delivered" align="num">{t('col.delivered')}</SortTh>
                  <SortTh sort={sort} col="failed" align="num">{t('postbacks.col.failed')}</SortTh>
                  <SortTh sort={sort} col="last_attempt_at">{t('postbacks.col.lastAttempt')}</SortTh>
                </tr>
              </thead>
              <tbody>
                {ordered.map((row) => (
                  <tr
                    key={row.id}
                    className="row-clickable"
                    onClick={() => setViewingId(row.id)}
                    {...rowOpen(() => setViewingId(row.id))}
                  >
                    <td>{postbackEventLabel(row.event_type)}</td>
                    {/*
                      Scheme dropped (every stored URL is https) and
                      truncated at the end, not the front — host first, and
                      the row's own detail modal or a hover on this cell has
                      the rest. Pending and Withheld moved into that modal to
                      make room for this: at 1440px with the right rail this
                      column and State plus two counts is what fits.
                    */}
                    <td
                      className="mono small faint"
                      title={row.url}
                      style={{ maxWidth: '13rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {postbackEndpoint(row.url)}
                    </td>
                    <td>
                      {/* Paused is `neutral`, not a warning or a failure —
                          DESIGN.md's tone table. */}
                      <Chip tone={row.enabled ? 'locked' : 'neutral'}>
                        {row.enabled ? t('postbacks.state.active') : t('postbacks.state.paused')}
                      </Chip>
                    </td>
                    <td className="num">{formatCount(row.delivered)}</td>
                    <td className="num">{formatCount(row.failed + row.exhausted)}</td>
                    {/*
                      Date only, not `formatDateTime` — the list is a scan,
                      and the full timestamp (with zone) is one click away in
                      the detail modal's own Facts.
                    */}
                    <td className="num faint" title={row.last_attempt_at ? formatDateTime(row.last_attempt_at) : undefined}>
                      {row.last_attempt_at ? formatDate(row.last_attempt_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
        )
      ) : null}
      {/*
        Not the server's own `note`: it is written in English, in the third
        person ("visible to the affiliate"), for every reader whatever
        language they chose — the deliveries note below (which explains a
        withheld row, and does change request to request) stays; this one is
        always the same sentence, so it is ours to translate instead.
      */}
      <Callout>{t('postbacks.list.note')}</Callout>

      {adding ? (
        <PostbackForm
          api={api}
          existing={null}
          onClose={() => setAdding(false)}
          onSaved={(result) => {
            setAdding(false);
            setVersion((v) => v + 1);
            setRegistered(result);
          }}
        />
      ) : null}

      {viewing ? (
        <PostbackDetailModal
          api={api}
          registration={viewing}
          onClose={() => setViewingId(null)}
          onChanged={() => setVersion((v) => v + 1)}
          onRemoved={(id) => setRegistered((cur) => (cur?.id === id ? null : cur))}
        />
      ) : null}
    </Panel>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Shell
 * ------------------------------------------------------------------ */

/**
 * The open tab lives in the address bar.
 *
 * Refreshing a portal and landing back on the dashboard is a small thing that
 * happens twenty times a day: an affiliate checking a statement reloads, loses
 * their place, and navigates back. It also makes a screen linkable, which is
 * what support asks for the moment a query is about one.
 */
function useTabInHash(): [Tab, (t: Tab) => void] {
  const read = (): Tab => {
    const raw = location.hash.replace(/^#/, '');
    return TABS.some((t) => t.id === raw) ? (raw as Tab) : 'dashboard';
  };
  const [tab, setTab] = useState<Tab>(read);

  useEffect(() => {
    const onHash = () => setTab(read());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return [tab, (t: Tab) => { location.hash = t; setTab(t); }];
}

/**
 * §2.5c — brand scope for every report, and the one control in the rail.
 *
 * It names a brand rather than the company behind it, because a brand is what
 * an affiliate recognises: they send traffic to Aurora Casino, and whether
 * Aurora and Vela happen to be run by the same operator is our organisational
 * fact, not theirs. The operator switch it used to sit beneath moved to
 * Brands, which is the screen about who they work with.
 *
 * Rendered for a single brand too. The rail is where somebody looks to find
 * out what the figures on screen are about, and hiding the control until there
 * are two of them answers that question only for affiliates who do not need to
 * ask it.
 *
 * `version` is bumped when a brand is added — accepting an invitation — so the
 * list here is not the one loaded when the session started.
 */
/** A brand's logo at rail size; a failed load falls away rather than showing a broken image. */
function BrandLogoTile({ src, name }: { src: string | undefined; name: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (!src || failed) return null;
  return (
    <span className="brand-logo-tile">
      <img src={src} alt={t('brand.logoAlt', { brand: name })} onError={() => setFailed(true)} />
    </span>
  );
}

function BrandSwitcher({
  brands,
  brandId,
  onBrandId,
  apiBase,
}: {
  // Item 1 (batch 2) — loaded once by `App` and passed down, rather than
  // fetched again here: the two independent `api.brands()` calls (this one
  // and the rail's brand-name lookup) doubled every load and every
  // `brandsVersion` bump.
  brands: Load<Awaited<ReturnType<AffiliateApi['brands']>>>;
  brandId: string;
  onBrandId: (v: string) => void;
  /** Where a logo path is loaded from. */
  apiBase: string;
}) {
  const only = brands.state === 'ready' && brands.value.items.length === 1 ? brands.value.items[0] : null;

  // One brand is not a choice. An affiliate on a single brand was offered
  // "All brands" and that brand — two names for the same figures — and the
  // default, "All brands", read as if there were others. So the one brand is
  // selected for them and shown as a fact, not a control.
  useEffect(() => {
    if (only && brandId !== only.brand_id) onBrandId(only.brand_id);
  }, [only, brandId, onBrandId]);

  if (brands.state !== 'ready') return null;

  // Not on one yet — an invitation still unanswered, or an application still
  // being assessed. A select with "All brands" and nothing else is a control
  // that cannot be used; the sentence says the same thing and is true.
  if (brands.value.items.length === 0) {
    return (
      <div className="switcher">
        <span className="figure-label">{t('switcher.showing')}</span>
        <span className="switcher-note">{t('brands.notOnAny')}</span>
      </div>
    );
  }

  if (only) {
    return (
      <div className="switcher">
        <span className="figure-label">{t('switcher.showing')}</span>
        <span className="switcher-brand">
          <BrandLogoTile src={logoSrc(apiBase, only.logo_url)} name={only.brand_name} />
          <span className="switcher-only">{only.brand_name}</span>
        </span>
        <span className="switcher-note">{t('switcher.onlyBrand')}</span>
      </div>
    );
  }

  const chosen = brands.value.items.find((b) => b.brand_id === brandId);
  const logo = logoSrc(apiBase, railBrandLogo(brands.value.items, brandId));

  return (
    <div className="switcher">
      {/*
        "Showing", not "Brand".

        The control decides which brand's figures every other screen is
        about — the same reach the programme switcher had — and an affiliate
        reading "Commission £4,200" has no way to know whose it is without a
        label that names the effect rather than the field.
      */}
      <span className="figure-label">{t('switcher.showing')}</span>
      <span className="switcher-brand">
        {/* Beside the select, not inside its options: a native option list is
            text only, and the logo of the brand in force is what to see. */}
        {chosen && logo ? <BrandLogoTile src={logo} name={chosen.brand_name} /> : null}
        <Select
          value={brandId}
          onChange={onBrandId}
          options={[
            { value: '', label: t('invitations.allBrands') },
            ...brands.value.items.map((b) => ({
              value: b.brand_id,
              label: b.brand_name,
            })),
          ]}
        />
      </span>
      <span className="switcher-note">
        {brandId === ''
          ? t('switcher.allBrands')
          : t('switcher.oneBrand')}
      </span>
    </div>
  );
}

/**
 * §2.5c — the programme switcher.
 *
 * "The multi-operator view, with a switcher, exists only on the platform's own
 * portal." So it renders only when there is more than one: an affiliate on a
 * single programme is shown their operator, not a control implying there are
 * others.
 *
 * §2.5d is why the operator's own name sits above it rather than the
 * platform's. "An affiliate on Operator A's programme who receives
 * platform-branded mail about their commission has been shown that A is not the
 * whole relationship."
 *
 * It lives on Brands rather than in the rail. Two dropdowns stacked in the
 * chrome, one naming a company and one naming a brand, is a question about our
 * org chart asked on every screen — and the rail has room for the one an
 * affiliate actually changes. Brands is where they are already looking at who
 * they work with, which is the only moment the company matters.
 */
function ProgrammeSwitcher({ api, onSwitch }: { api: AffiliateApi; onSwitch: () => void }) {
  const loaded = useLoad(() => api.operators(), []);
  const [busy, setBusy] = useState(false);

  // 0332 — only what can be opened: a rejected or ended programme stays on
  // the list for the Brands screen, but selecting it would lock the console.
  const choices = loaded.state === 'ready' ? selectableProgrammes(loaded.value.items) : [];
  if (choices.length < 2) return null;

  const active = choices.find((o) => o.is_active);

  return (
    <div className="switcher">
      {/*
        "Company", with the brand selector's "Showing" under it.

        The control decides which company's figures every other screen is
        about — 38 of the API's routes are filtered by this selection — and an
        affiliate on two programmes reading "Commission £4,200" has no way to
        know whose money that is without it. "Programme" named the thing being
        selected and left its effect to be inferred, so this said "Showing"
        instead, which names the effect.

        That was right while this was the only such control on the page. In
        the rail it sits directly above the brand selector, which says
        "Showing" for the same good reason — and two stacked controls both
        labelled "Showing" say less than either did alone. So this one names
        the entity and the one below it keeps the effect: Company, then
        showing all of their brands or one.
      */}
      <span className="figure-label">{t('switcher.company')}</span>
      <Select
        value={active?.operator_id ?? ''}
        onChange={(v) => {
          setBusy(true);
          void api.selectOperator(v).then(onSwitch).finally(() => setBusy(false));
        }}
        options={choices.map((o) => ({
          value: o.operator_id,
          // §6.3g — the portal word. A switcher is glanced at, and the ledger
          // status glanced at is a verdict nobody has reached.
          label: `${o.display_name} · ${o.portal_status}`,
        }))}
      />
      {busy ? <span className="count">{t('switcher.switching')}</span> : null}
      {/*
        The note used to sit here permanently: four lines of explanation in the
        rail, on every screen, for a fact somebody needs once. It moved to the
        Brands screen, which is where an affiliate is deciding between
        companies and is the only moment the distinction matters.

        The switcher went with it, and should not have. It changes what every
        figure on every screen refers to, and a control with that reach belongs
        in the chrome rather than on one page — which is what the sentence here
        already said while the mount said the opposite.

        What that cost: an affiliate with profiles under two operators is
        signed in to whichever one `login.ts` picked, and on every screen but
        Brands there was nothing to say so. Terms proposed by the other
        operator sat unanswered in a portal that showed no sign of them, and
        the affiliate had no way to learn there was a second programme at all
        without opening the one page that mentioned it.
      */}
    </div>
  );
}

export function App({
  api,
  now,
  permissions,
  onboard,
  onSignedOut,
}: {
  api: AffiliateApi;
  now: () => Date;
  /** §3.2 — an affiliate holds `own.read` and nothing else. */
  permissions: readonly string[];
  /** Item 2 (third batch) — Account's own voluntary "Change password". */
  onboard: OnboardApi;
  /**
   * What signing out returns to.
   *
   * Was `location.reload()`, which happens to work here because the session
   * lives in memory — and does nothing visible anywhere it does not.
   */
  onSignedOut?: () => void;
}) {
  const [tab, setTab] = useTabInHash();
  const [days, setDays] = useState('30');
  /*
   * Kept beside `days`, not inside `Filters`: switching to another preset and
   * back would otherwise discard two dates somebody had just typed.
   */
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [brandId, setBrandId] = useState('');
  // Bumped when accepting an invitation adds a brand, so the rail's selector
  // re-reads the list rather than staying on the one it loaded at sign-in.
  const [brandsVersion, setBrandsVersion] = useState(0);
  const [dealProposalsVersion, setDealProposalsVersion] = useState(0);
  const range = useMemo(
    () => rangeOf(now(), days, brandId, customRange),
    [days, brandId, customRange],
  );
  const at = now();

  // Bumped when the language is saved, so `me.locale` is re-read and the
  // next render is in the chosen language — the same version-counter refetch
  // the operator console uses. Before this the portal stayed in the old
  // language until a reload, which reads as a save that did nothing.
  const [meVersion, setMeVersion] = useState(0);
  const me = useLoad(useCallback(() => api.me(), [api]), [meVersion]);
  const currencyScales = useLoad(useCallback(() => api.currencies(), [api]), []);
  // Item 5 — Account's own section and its last save confirmation, lifted up
  // here because saving the language or currency bumps `meVersion` above,
  // which remounts every screen below through the loading gate a few lines
  // down; state that lives in `App` is what survives that round trip.
  const [accountSection, setAccountSection] = useState<AccountSection>('profile');
  const [accountSavedNote, setAccountSavedNote] = useState<string | null>(null);
  // Item 4 (batch 2) — living in `App` means the note also survives leaving
  // the Account tab and coming back, which the section-change clear below
  // does not cover: without this, a stale "Saved." from a previous visit
  // shows next to a section nobody has just saved.
  useEffect(() => {
    setAccountSavedNote(null);
  }, [tab]);
  // Item 9 — the rail's brand filter has no name to show on the page itself,
  // so the list is loaded once here and passed down: to `BrandSwitcher` for
  // the rail control, and read again below for the page's own filter chip.
  // (Item 1, batch 2 — this used to be a second `api.brands()` alongside
  // `BrandSwitcher`'s own, doubling the request on every load and every
  // `brandsVersion` bump.)
  const brandsList = useLoad(useCallback(() => api.brands(), [api]), [brandsVersion]);
  // §8.9a — whether the Sub-affiliates tab shows at all. Hidden until known,
  // and a failed load hides it rather than breaking the console: most
  // operators leave the programme off, so "not shown" is the safe default
  // while this is still loading or could not be reached. The programme being
  // on is the ordinary reason; `subAffiliatesTabVisible` also keeps it once
  // this affiliate already has recruits, so switching the programme off does
  // not make their existing recruits — and what those recruits have earned
  // them — disappear from the portal.
  const subAffiliatesGate = useLoad(useCallback(() => api.mySubAffiliates(), [api]), []);
  const subAffiliatesEnabled =
    subAffiliatesGate.state === 'ready' && subAffiliatesTabVisible(subAffiliatesGate.value);
  const brandName =
    brandsList.state === 'ready'
      ? brandsList.value.items.find((b) => b.brand_id === brandId)?.brand_name
      : undefined;
  // Item 1 (batch 2) — "Show all" only means something when there is
  // something else to show: a single-brand affiliate's `BrandSwitcher` effect
  // (below) re-selects their one brand the instant it is cleared, so the chip
  // and its "Show all" button would appear on every screen and do nothing.
  const railLogo = brandsList.state === 'ready'
    ? logoSrc(api.apiBaseUrl, railBrandLogo(brandsList.value.items, brandId))
    : undefined;
  const hasMultipleBrands = brandsList.state === 'ready' && brandsList.value.items.length > 1;
  // The intro tour: starts by itself on a first sign-in, then only from the rail.
  const [tourStore] = useState(() => ({
    load: async () => {
      const r = await api.tour();
      return { seenVersion: r.seen_version, onBehalf: r.on_behalf };
    },
    save: (version: number) => api.markTourSeen(version),
  }));
  const tour = useTour({
    version: AFFILIATE_TOUR_VERSION,
    store: tourStore,
    // The same tabs the rail draws below.
    steps: affiliateTourSteps(
      railItems(subAffiliatesEnabled).map((item) => item.id),
      setTab,
      {
        rail: railItems(subAffiliatesEnabled),
        main: tourMain,
        viewer: {
          selectedBrand: brandName ?? null,
          brands: brandsList.state === 'ready' ? brandsList.value.items.map((b) => b.brand_name) : [],
          brandColours: brandsList.state === 'ready'
            ? Object.fromEntries(brandsList.value.items.map((b) => [b.brand_name, b.theme_accent]))
            : {},
          affiliateName: me.state === 'ready' ? me.value.display_name : null,
        },
      },
    ),
  });

  if (me.state === 'loading') return <div className="main"><Loading /></div>;
  if (me.state === 'error') {
    // A session that has ended is not an error page; it is a sign-in prompt.
    return (
      <div className="main">
        {me.error instanceof SessionExpiredError
          ? <Callout tone="caution">{me.error.message}</Callout>
          : <Failure error={me.error} />}
      </div>
    );
  }

  /*
   * §2.8a / §2.8d — the language, decided once, here.
   *
   * Written during render rather than in an effect: every screen below builds
   * its view model synchronously on this same pass, and a locale that arrives
   * one paint later means the first render of every figure is in the wrong
   * language. The write is idempotent — the same value on every re-render —
   * which is what makes doing it here safe rather than merely convenient.
   */
  setDisplayLocale(me.value.locale, me.value.operator_default_locale);

  /*
   * §21.7 — and the currency their reports are read in, from the same two
   * fields and for the same reason. Kept as the pair rather than the resolved
   * answer, so a screen can say "your operator's default" where that is what
   * is happening and say nothing where the reader chose for themselves.
   */
  setDisplayCurrency(me.value.display_currency, me.value.operator_default_display_currency);

  /*
   * §1.3d / §21.7 — and how many minor-unit digits each currency is written
   * with, which had never left the server.
   *
   * `ref_currencies.minor_unit_scale` is real and editable, `packages/money`
   * honours it, and `formatMoney` defaulted every figure to two digits — so a
   * zero-decimal currency rendered a hundred times too large. Written during
   * render beside the locale, idempotently, for the same reason: every screen
   * below builds its view model on this pass.
   */
  if (currencyScales.state === 'ready') {
    setCurrencyScales(currencyScales.value.items);
  }

  const props: ScreenProps = {
    api,
    range,
    now: at,
    me: me.value,
    onboard,
    // Item 5 — captures the save's own confirmation before the remount below
    // would otherwise take it with it.
    onLocaleSaved: (note) => {
      setAccountSavedNote(note ?? null);
      setMeVersion((n) => n + 1);
    },
    dealProposalsVersion,
    onDealProposalsChanged: () => setDealProposalsVersion((n) => n + 1),
    filters: (
      <Filters
        days={days}
        onDays={setDays}
        custom={customRange}
        onCustom={setCustomRange}
        {...(brandId && brandName && hasMultipleBrands
          ? { brand: brandName, onClearBrand: () => setBrandId('') }
          : {})}
      />
    ),
  };
  const dot =
    me.value.status === 'Active' ? 'active' : me.value.status === 'Under Review' ? 'review' : 'ended';

  return (
    <>
      {/* §3.6a — MUST be visibly banner-flagged. Said once, at the top,
          rather than per-screen — the same reasoning §39.4's provisional
          note already follows elsewhere in this portal. */}
      {me.value.impersonated ? (
        <div className="impersonation-banner" role="status">
          Support is viewing this account to reproduce an issue. Read-only —
          nothing can be changed from this session.
        </div>
      ) : null}
    <div className="shell">
      <Rail
        head={<PortalMark
          portal="affiliate"
          product="KD Affiliate"
          {...(railLogo ? { logoUrl: railLogo } : {})}
        />}
        foot={<>
          <TourButton tour={tour} />
          <RailIdentity
            name={me.value.display_name}
            // §6.3g — the portal state, never the ledger status behind it.
            detail={me.value.status}
            tone={dot}
            label={t('nav.account')}
            current={tab === 'account'}
            onOpen={() => setTab('account')}
          />
          <div className="rail-foot-row">
            {/* §3.7 — revoked at the server, not merely forgotten here. */}
            <SignOut
              revoke={() => api.logout()}
              onDone={onSignedOut ?? (() => location.reload())}
            />
            <ThemeToggle />
          </div>
        </>}
      >

        {/* The one figure an affiliate opens the console to see, on every
            page rather than only on the one that computes it. Under the mark,
            not beside it: the rail head is a row, and beside the product name
            it squeezed the name to an ellipsis. */}
        <RunningCommission api={api} range={range} />

        {/*
          §2.5c — which company every figure on every screen is about.
          Above the brand selector because a programme contains brands: choose
          the company, then which of their brands. Renders only for an
          affiliate who has more than one.
        */}
        <ProgrammeSwitcher api={api} onSwitch={() => location.reload()} />

        <BrandSwitcher
          brands={brandsList}
          brandId={brandId}
          onBrandId={setBrandId}
          apiBase={api.apiBaseUrl}
        />

        <NavList
          items={railItems(subAffiliatesEnabled)}
          current={tab}
          onSelect={setTab}
        />
      </Rail>
      <TourDialog tour={tour} />

      <main className="main">
        {tab === 'dashboard' ? <Dashboard {...props} /> : null}
        {tab === 'links' ? <Links {...props} /> : null}
        {tab === 'campaigns' ? <Campaigns {...props} /> : null}
        {tab === 'reports' ? <Reports {...props} /> : null}
        {tab === 'sources' ? <Sources {...props} /> : null}
        {tab === 'postbacks' ? <Postbacks {...props} /> : null}
        {tab === 'terms' ? <Terms {...props} /> : null}
        {tab === 'spend' ? <SpendDeclarations api={props.api} /> : null}
        {tab === 'statement' ? <Statement {...props} /> : null}
        {tab === 'statements' ? <Statements {...props} /> : null}
        {tab === 'history' ? <History {...props} /> : null}
        {tab === 'disputes' ? <Disputes {...props} /> : null}
        {tab === 'support' ? <Support api={props.api} /> : null}
        {tab === 'payments' ? <Payments {...props} /> : null}
        {tab === 'players' ? <Players {...props} /> : null}
        {tab === 'players' ? <DailyActivity api={props.api} range={props.range} now={props.now} /> : null}
        {tab === 'subaffiliates' ? <SubAffiliates {...props} /> : null}
        {tab === 'programmes' ? <Programmes {...props} /> : null}
        {tab === 'account' ? (
          <Account
            {...props}
            section={accountSection}
            // Item 5 — a manual section change (not a save) starts the next
            // one with a clean slate, so an old confirmation does not sit
            // under a section it no longer belongs to.
            onSection={(s) => { setAccountSection(s); setAccountSavedNote(null); }}
            savedNote={accountSavedNote}
          />
        ) : null}
      </main>

      {/* The other rail. Same chrome as the left one, on the other side, and
          absent entirely when nothing is waiting for an answer. */}
      <PortalAside
        api={api}
        onBrandsChanged={() => setBrandsVersion((n) => n + 1)}
        dealProposalsVersion={dealProposalsVersion}
        onDealProposalsChanged={() => setDealProposalsVersion((n) => n + 1)}
        currentTab={tab}
      />
    </div>
    </>
  );
}
